import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./App.css";
import axios from "axios";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Notification chime using Web Audio API
const playNotificationChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    
    // First tone (higher, bright)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.setValueAtTime(1047, now + 0.08);
    gain1.gain.setValueAtTime(0.3, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);
    
    // Second tone (resolve, pleasant)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1319, now + 0.1);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(0.25, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.5);
    
    // Cleanup
    setTimeout(() => ctx.close(), 600);
  } catch (e) {
    console.log('Audio not available');
  }
};

// Zone Close Stats Modal Component
const ZoneCloseStatsModal = ({ stats, setShowZoneCloseStats, currencyPair }) => {
  if (!stats) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2 text-white">📊 Zone Close Stats</h2>
          <p className="text-gray-300 mb-2 text-sm">
            Last 200 • 15-minute closes • {currencyPair}
          </p>
          <div className="text-xs text-gray-400 mb-6">
            Current: {stats.currentPrice} • Zone: {stats.currentZone}
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {/* Uppermost Zone */}
            <div className="bg-blue-800 bg-opacity-40 border border-blue-400 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-blue-300 font-semibold text-sm">Uppermost Zone</h3>
                <div className="text-blue-200 font-bold text-lg">{stats.uppermostArrival.percentage}%</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                <div className="bg-blue-500 h-2 rounded-full" style={{width: `${stats.uppermostArrival.percentage}%`}}></div>
              </div>
              <div className="text-xs text-gray-300">{stats.uppermostArrival.count} closes</div>
            </div>

            {/* Bull Zone */}
            <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-green-400 font-semibold text-sm">Bull Zone</h3>
                <div className="text-green-300 font-bold text-lg">{stats.bull.percentage}%</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                <div className="bg-green-500 h-2 rounded-full" style={{width: `${stats.bull.percentage}%`}}></div>
              </div>
              <div className="text-xs text-gray-300">{stats.bull.count} closes</div>
            </div>

            {/* Equilibrium Zone */}
            <div className="bg-gray-700 border border-gray-500 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-white font-semibold text-sm">Equilibrium Zone</h3>
                <div className="text-gray-200 font-bold text-lg">{stats.equilibrium.percentage}%</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                <div className="bg-white h-2 rounded-full" style={{width: `${stats.equilibrium.percentage}%`}}></div>
              </div>
              <div className="text-xs text-gray-300">{stats.equilibrium.count} closes</div>
            </div>

            {/* Bear Zone */}
            <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-red-400 font-semibold text-sm">Bear Zone</h3>
                <div className="text-red-300 font-bold text-lg">{stats.bear.percentage}%</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                <div className="bg-red-500 h-2 rounded-full" style={{width: `${stats.bear.percentage}%`}}></div>
              </div>
              <div className="text-xs text-gray-300">{stats.bear.count} closes</div>
            </div>

            {/* Lowermost Zone */}
            <div className="bg-purple-800 bg-opacity-40 border border-purple-400 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-purple-300 font-semibold text-sm">Lowermost Zone</h3>
                <div className="text-purple-200 font-bold text-lg">{stats.lowermostArrival.percentage}%</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                <div className="bg-purple-500 h-2 rounded-full" style={{width: `${stats.lowermostArrival.percentage}%`}}></div>
              </div>
              <div className="text-xs text-gray-300">{stats.lowermostArrival.count} closes</div>
            </div>
          </div>

          {/* What is an Octet Section */}
          <div className="mt-6 mb-4">
            <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
              <h4 className="text-gray-200 font-semibold mb-3">What is an Octet?</h4>
              <div className="text-xs text-gray-300 leading-relaxed">
                <p>
                  Each Octet contains 3-length, 5-length and an 8-length Directional Patterns. If the time interval selection is 1 Day then that Octet covers a period of 8 days. If the time interval selection is 1 Hour then an Octet covers a period of 8 hours and if the time interval selected is 4 Hours then an Octet covers a period of 32 hours.
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setShowZoneCloseStats(false)}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// HEGM Horizons Modal Component
const HegmHorizonsModal = ({ 
  showHegmHorizons, 
  setShowHegmHorizons, 
  hegmHorizonsData, 
  hegmHorizonsLoading,
  fetchHegmHorizons 
}) => {
  const [selectedPair, setSelectedPair] = useState(hegmHorizonsData?.currency_pair || 'EURUSD');
  const PAIRS = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'];

  const handlePairChange = (pair) => {
    setSelectedPair(pair);
    fetchHegmHorizons(pair);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" data-testid="hegm-horizons-modal">
      <div className="bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2 text-white">HEGM Horizons</h2>
          <p className="text-gray-400 text-sm">Rolling 4-Hour Forex Forecasts</p>
          <p className="text-white text-xs mt-1">Hurst + Elliott + Gann + Mandelbrot</p>
          <p className="text-gray-400 text-xs mt-1">Updated at the top of every hour</p>
        </div>
        
        {/* Currency Pair Selector */}
        <div className="flex justify-center gap-2 mb-6 flex-wrap">
          {PAIRS.map((pair) => (
            <button
              key={pair}
              onClick={() => handlePairChange(pair)}
              className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                (hegmHorizonsData?.currency_pair === pair) 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {pair}
            </button>
          ))}
        </div>

        {hegmHorizonsLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
          </div>
        ) : hegmHorizonsData ? (
          <div className="space-y-6">
            {/* HEGM Metrics Grid - Each box shows its own model's bias colour */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Hurst */}
              {(() => {
                const sig = hegmHorizonsData.metrics?.hurst_signal;
                const bgClass = sig === 1 ? 'bg-gradient-to-br from-green-900/60 to-green-800/40 border-green-500/50' :
                                sig === -1 ? 'bg-gradient-to-br from-red-900/60 to-red-800/40 border-red-500/50' :
                                'bg-gradient-to-br from-yellow-900/60 to-yellow-800/40 border-yellow-500/50';
                const textClass = sig === 1 ? 'text-green-400' : sig === -1 ? 'text-red-400' : 'text-yellow-400';
                return (
                  <div className={`rounded-lg p-3 text-center border ${bgClass}`}>
                    <p className={`text-xs mb-1 font-semibold ${textClass}`}>H - Hurst</p>
                    <p className="text-white text-lg font-bold font-mono">
                      {hegmHorizonsData.metrics?.hurst_target != null 
                        ? Number(hegmHorizonsData.metrics.hurst_target).toFixed(hegmHorizonsData.metrics?.latest_close < 10 ? 5 : 3)
                        : 'N/A'}
                    </p>
                    <p className="text-gray-300 text-xs mt-1">
                      {hegmHorizonsData.metrics?.hurst_detail || 'Cycle Target'}
                    </p>
                  </div>
                );
              })()}
              
              {/* Elliott */}
              {(() => {
                const sig = hegmHorizonsData.elliott?.signal;
                const bgClass = sig === 1 ? 'bg-gradient-to-br from-green-900/60 to-green-800/40 border-green-500/50' :
                                sig === -1 ? 'bg-gradient-to-br from-red-900/60 to-red-800/40 border-red-500/50' :
                                'bg-gradient-to-br from-yellow-900/60 to-yellow-800/40 border-yellow-500/50';
                const textClass = sig === 1 ? 'text-green-400' : sig === -1 ? 'text-red-400' : 'text-yellow-400';
                return (
                  <div className={`rounded-lg p-3 text-center border ${bgClass}`}>
                    <p className={`text-xs mb-1 font-semibold ${textClass}`}>E - Elliott</p>
                    <p className="text-white text-lg font-bold font-mono">
                      {hegmHorizonsData.elliott?.fib_target != null 
                        ? Number(hegmHorizonsData.elliott.fib_target).toFixed(hegmHorizonsData.metrics?.latest_close < 10 ? 5 : 3)
                        : 'N/A'}
                    </p>
                    <p className="text-gray-300 text-xs mt-1">{hegmHorizonsData.elliott?.detail || '1.618 Extension'}</p>
                  </div>
                );
              })()}
              
              {/* Gann */}
              {(() => {
                const gann = hegmHorizonsData.gann || {};
                const sig = gann.is_bullish === true ? 1 : gann.is_bullish === false ? -1 : 0;
                const bgClass = sig === 1 ? 'bg-gradient-to-br from-green-900/60 to-green-800/40 border-green-500/50' :
                                sig === -1 ? 'bg-gradient-to-br from-red-900/60 to-red-800/40 border-red-500/50' :
                                'bg-gradient-to-br from-yellow-900/60 to-yellow-800/40 border-yellow-500/50';
                const textClass = sig === 1 ? 'text-green-400' : sig === -1 ? 'text-red-400' : 'text-yellow-400';
                const decimals = hegmHorizonsData.metrics?.latest_close < 10 ? 5 : 3;
                const gannDisplay = gann.base_target != null ? Number(gann.base_target).toFixed(decimals) : 'N/A';
                return (
                  <div className={`rounded-lg p-3 text-center border ${bgClass}`}>
                    <p className={`text-xs mb-1 font-semibold ${textClass}`}>G - Gann</p>
                    <p className="text-white text-lg font-bold font-mono">{gannDisplay}</p>
                    <p className="text-gray-300 text-xs mt-1">{gann.detail || 'Fan + Sq9'}</p>
                  </div>
                );
              })()}
              
              {/* Mandelbrot */}
              {(() => {
                const sig = hegmHorizonsData.mandelbrot?.signal;
                const bgClass = sig === 1 ? 'bg-gradient-to-br from-green-900/60 to-green-800/40 border-green-500/50' :
                                sig === -1 ? 'bg-gradient-to-br from-red-900/60 to-red-800/40 border-red-500/50' :
                                'bg-gradient-to-br from-yellow-900/60 to-yellow-800/40 border-yellow-500/50';
                const textClass = sig === 1 ? 'text-green-400' : sig === -1 ? 'text-red-400' : 'text-yellow-400';
                return (
                  <div className={`rounded-lg p-3 text-center border ${bgClass}`}>
                    <p className={`text-xs mb-1 font-semibold ${textClass}`}>M - Mandelbrot</p>
                    <p className="text-white text-lg font-bold font-mono">
                      {hegmHorizonsData.mandelbrot?.target != null 
                        ? Number(hegmHorizonsData.mandelbrot.target).toFixed(hegmHorizonsData.metrics?.latest_close < 10 ? 5 : 3)
                        : 'N/A'}
                    </p>
                    <p className="text-gray-300 text-xs mt-1">{hegmHorizonsData.mandelbrot?.detail || 'Fractals + MF-DFA'}</p>
                  </div>
                );
              })()}
            </div>

            {/* Data Points */}
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-gray-400 text-xs mb-1">Data Points (1h bars)</p>
              <p className="text-white text-xl font-bold">
                {hegmHorizonsData.data_points}
              </p>
            </div>

            {/* 4-Hour Blended Forecast Section with Bias-colored background */}
            <div className={`rounded-lg p-4 border ${
              hegmHorizonsData.bias?.label === 'Bullish' ? 'bg-gradient-to-r from-green-900/50 to-green-800/40 border-green-500/50' :
              hegmHorizonsData.bias?.label === 'Bearish' ? 'bg-gradient-to-r from-red-900/50 to-red-800/40 border-red-500/50' :
              'bg-gradient-to-r from-yellow-900/50 to-yellow-800/40 border-yellow-500/50'
            }`}>
              <h3 className={`font-bold mb-2 text-center ${
                hegmHorizonsData.bias?.label === 'Bullish' ? 'text-green-300' :
                hegmHorizonsData.bias?.label === 'Bearish' ? 'text-red-300' : 'text-yellow-300'
              }`}>4-Hour HEGM Blended Forecast</h3>
              
              {/* Bias + Forecast - layout mirrors direction */}
              {hegmHorizonsData.bias && (
                <div className="text-center mb-4">
                  {hegmHorizonsData.bias.label === 'Bearish' ? (
                    <>
                      <span className="text-2xl font-bold text-red-400">
                        {hegmHorizonsData.bias.emoji} Bearish
                      </span>
                      <p className="text-gray-300 text-sm my-2">
                        Projected to move down towards the price horizon below over the next 4 hours:
                      </p>
                      <p className="text-white text-2xl font-bold font-mono">
                        {hegmHorizonsData.forecast?.projected_close || 'N/A'}
                      </p>
                    </>
                  ) : hegmHorizonsData.bias.label === 'Bullish' ? (
                    <>
                      <p className="text-white text-2xl font-bold font-mono">
                        {hegmHorizonsData.forecast?.projected_close || 'N/A'}
                      </p>
                      <p className="text-gray-300 text-sm my-2">
                        Projected to move up towards the price horizon above over the next 4 hours:
                      </p>
                      <span className="text-2xl font-bold text-green-400">
                        {hegmHorizonsData.bias.emoji} Bullish
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-yellow-400">
                        {hegmHorizonsData.bias.emoji} Neutral
                      </span>
                      <p className="text-gray-300 text-sm my-2">
                        Projected to move sideways near the price horizon over the next 4 hours:
                      </p>
                      <p className="text-white text-2xl font-bold font-mono">
                        {hegmHorizonsData.forecast?.projected_close || 'N/A'}
                      </p>
                    </>
                  )}
                </div>
              )}
              
              <div className="text-center">
                <div>
                  <p className={`text-xs mt-1 ${
                    hegmHorizonsData.bias?.label === 'Bullish' ? 'text-green-400' :
                    hegmHorizonsData.bias?.label === 'Bearish' ? 'text-red-400' : 'text-yellow-400'
                  }`}>Current: {hegmHorizonsData.metrics?.latest_close}</p>
                </div>
              </div>
              <p className="text-gray-400 text-xs text-center mt-3">
                Blended from: Hurst 3-Cycle + Elliott 5-Wave + Gann Sq9 + Mandelbrot Fractals
              </p>
            </div>

            {/* Gann Angles Details */}
            {hegmHorizonsData.gann?.angles && hegmHorizonsData.gann.angles.length > 0 && (
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2 text-sm">Gann Angles (4-Hour Levels)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {hegmHorizonsData.gann.angles.map((angle, idx) => (
                    <div key={idx} className="bg-gray-600 rounded p-2 text-center">
                      <span className="text-yellow-300 text-xs font-semibold">{angle.label || 'N/A'}</span>
                      <p className="text-white text-sm font-mono">{angle.target_price != null ? Number(angle.target_price).toFixed(5) : 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* HEGM Component Guide */}
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2 text-sm">HEGM Components Guide</h4>
              <div className="text-xs space-y-1">
                <p className="text-blue-300"><span className="font-bold">H</span>urst: 3-cycle voting (40d/20d/10d) — phase position determines trend direction</p>
                <p className="text-green-300"><span className="font-bold">E</span>lliott: 5-wave pattern detection with Fibonacci ratio validation</p>
                <p className="text-yellow-300"><span className="font-bold">G</span>ann: Square of 9 price levels + 1x1 angle directional confirmation</p>
                <p className="text-purple-300"><span className="font-bold">M</span>andelbrot: MF-DFA Multifractal Detrended Fluctuation Analysis. Mandelbrot introduced the concept of fractals and multifractals to describe patterns that repeat themselves at smaller and smaller scales in nature, financial markets and other complex systems.</p>
              </div>
            </div>

            {/* Recent Data Table */}
            {hegmHorizonsData.recent_data && hegmHorizonsData.recent_data.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-2 text-sm">Recent Daily Data</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-600">
                        <th className="py-2 text-left">Date</th>
                        <th className="py-2 text-right">Open</th>
                        <th className="py-2 text-right">High</th>
                        <th className="py-2 text-right">Low</th>
                        <th className="py-2 text-right">Close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hegmHorizonsData.recent_data.slice(-5).map((row, idx) => (
                        <tr key={idx} className="text-gray-300 border-b border-gray-700">
                          <td className="py-1">{row.Date}</td>
                          <td className="py-1 text-right font-mono">{row.Open?.toFixed(5)}</td>
                          <td className="py-1 text-right font-mono">{row.High?.toFixed(5)}</td>
                          <td className="py-1 text-right font-mono">{row.Low?.toFixed(5)}</td>
                          <td className="py-1 text-right font-mono">{row.Close?.toFixed(5)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Version Info */}
            <p className="text-gray-500 text-xs text-center">
              {hegmHorizonsData.version} • Updated: {new Date(hegmHorizonsData.last_updated).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p>Select a currency pair to view HEGM Horizons forecast</p>
          </div>
        )}

        {/* Close Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowHegmHorizons(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Institutional Bias Locations Modal Component with Admin Controls
const InstitutionalBiasModal = ({ 
  showInstitutionalBias, 
  setShowInstitutionalBias, 
  institutionalBiasData, 
  institutionalBiasLoading,
  fetchInstitutionalBias,
  adminMode 
}) => {
  const [showAddSession, setShowAddSession] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    currency_pair: 'EURUSD',
    session_type: 'London',
    session_date: '',
    high: '',
    low: ''
  });
  const [viewingSessions, setViewingSessions] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);

  const PAIRS = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'];

  const handleAddSession = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/institutional-bias/record-session?session_id=admin_test_123`, {
        currency_pair: sessionForm.currency_pair,
        session_type: sessionForm.session_type,
        session_date: sessionForm.session_date,
        high: parseFloat(sessionForm.high),
        low: parseFloat(sessionForm.low)
      });
      toast.success(`Added data for ${sessionForm.currency_pair}`);
      setSessionForm({ ...sessionForm, high: '', low: '', session_date: '' });
      fetchInstitutionalBias();
    } catch (err) {
      console.error('Failed to add data:', err);
      toast.error('Failed to add data');
    }
  };

  const fetchSessions = async (pair) => {
    try {
      const response = await axios.get(`${API}/admin/institutional-bias/sessions/${pair}?session_id=admin_test_123`);
      setSessionsData(response.data.sessions || []);
      setViewingSessions(pair);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" data-testid="institutional-bias-modal">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2 text-white">Institutional Bias Locations</h2>
        </div>
        
        {institutionalBiasLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        ) : institutionalBiasData ? (
          <div>
            {/* Bias Grid Table */}
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm" data-testid="institutional-bias-table">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-2 px-3 text-gray-400">Currency</th>
                    <th className="text-center py-2 px-3 text-green-400">Bullish</th>
                    <th className="text-center py-2 px-3 text-gray-400">Neutral</th>
                    <th className="text-center py-2 px-3 text-red-400">Bearish</th>
                    {adminMode && <th className="text-center py-2 px-3 text-gray-400">Admin</th>}
                  </tr>
                </thead>
                <tbody>
                  {institutionalBiasData.pairs && Object.entries(institutionalBiasData.pairs).map(([pair, data]) => (
                    <tr key={pair} className="border-b border-gray-700 hover:bg-gray-700">
                      <td className="py-3 px-3 font-medium text-white">{pair}</td>
                      <td className="py-3 px-3 text-center">
                        <span className={`font-bold ${data.bullish > 5 ? 'text-green-400' : data.bullish > 0 ? 'text-green-300' : 'text-gray-500'}`}>
                          {data.bullish || ''}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="font-bold text-gray-300">{data.neutral || ''}</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`font-bold ${data.bearish > 5 ? 'text-red-400' : data.bearish > 0 ? 'text-red-300' : 'text-gray-500'}`}>
                          {data.bearish || ''}
                        </span>
                      </td>
                      {adminMode && (
                        <td className="py-3 px-3 text-center">
                          <button
                            onClick={() => fetchSessions(pair)}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            View
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Admin Controls - Only visible in admin mode */}
            {adminMode && (
              <div className="mb-4">
                <button
                  onClick={() => setShowAddSession(!showAddSession)}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-sm font-medium"
                  data-testid="add-session-btn"
                >
                  {showAddSession ? '✕ Cancel' : '➕ Add Data'}
                </button>
                
                {showAddSession && (
                  <form onSubmit={handleAddSession} className="mt-4 bg-gray-700 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Currency Pair</label>
                        <select
                          value={sessionForm.currency_pair}
                          onChange={(e) => setSessionForm({...sessionForm, currency_pair: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                        >
                          {PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <select
                          value={sessionForm.session_type}
                          onChange={(e) => setSessionForm({...sessionForm, session_type: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                        >
                          <option value="London">London</option>
                          <option value="Tokyo">Tokyo</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Date</label>
                        <input
                          type="date"
                          value={sessionForm.session_date}
                          onChange={(e) => setSessionForm({...sessionForm, session_date: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                          required
                        />
                      </div>
                      <div className="col-span-2 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">High</label>
                          <input
                            type="number"
                            step="0.00001"
                            value={sessionForm.high}
                            onChange={(e) => setSessionForm({...sessionForm, high: e.target.value})}
                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                            placeholder="1.08500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Low</label>
                          <input
                            type="number"
                            step="0.00001"
                            value={sessionForm.low}
                            onChange={(e) => setSessionForm({...sessionForm, low: e.target.value})}
                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                            placeholder="1.08000"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium text-sm"
                    >
                      Save
                    </button>
                  </form>
                )}
                
                {/* View data for selected pair - Admin only */}
                {viewingSessions && (
                  <div className="mt-4 bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-white font-medium">{viewingSessions} Data</h4>
                      <button onClick={() => setViewingSessions(null)} className="text-gray-400 hover:text-white text-sm">✕</button>
                    </div>
                    {sessionsData.length === 0 ? (
                      <p className="text-gray-400 text-sm">No data recorded yet</p>
                    ) : (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {sessionsData.map((s, i) => (
                          <div key={i} className="flex justify-between text-xs bg-gray-800 p-2 rounded">
                            <span className="text-gray-300">{s.session_type} - {s.session_date}</span>
                            <span className="text-white">H: {s.high?.toFixed(5)} | L: {s.low?.toFixed(5)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Update Frequency */}
            <p className="text-xs text-gray-500 text-center">
              Updates every 1 minute
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              Last updated: {institutionalBiasData.timestamp ? new Date(institutionalBiasData.timestamp).toLocaleTimeString() : 'N/A'}
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            No data available. Please try again.
          </div>
        )}
        
        {/* Close Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowInstitutionalBias(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
            data-testid="close-institutional-bias-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Add History Modal Component for 2-Day Cycle Forecasts
const AddHistoryModal = ({ currencyPair, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    direction: 'UP',
    triggerDate: '',
    triggerTime: '',
    triggerPrice: '',
    maxPipsReached: '',
    endPips: '',
    triggerLevel: 1
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.triggerDate || !formData.triggerTime || !formData.triggerPrice || !formData.maxPipsReached || !formData.endPips) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const triggerDateTime = `${formData.triggerDate}T${formData.triggerTime}:00+00:00`;
      
      await axios.post(`${API}/admin/2day-cycle-add-history/${currencyPair}?session_id=admin_test_123`, {
        direction: formData.direction,
        trigger_time: triggerDateTime,
        trigger_price: parseFloat(formData.triggerPrice),
        max_pips_reached: parseFloat(formData.maxPipsReached),
        end_pips: parseFloat(formData.endPips),
        trigger_level: parseInt(formData.triggerLevel)
      });

      toast.success('Historical forecast added!');
      
      // Clear form for next entry
      setFormData({
        ...formData,
        triggerDate: '',
        triggerTime: '',
        triggerPrice: '',
        maxPipsReached: '',
        endPips: ''
      });
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to add history:', err);
      toast.error('Failed to add historical forecast');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecalculateStats = async () => {
    try {
      await axios.post(`${API}/admin/2day-cycle-recalculate-stats/${currencyPair}?session_id=admin_test_123`);
      toast.success('Stats recalculated from history!');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error('Failed to recalculate stats');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white mb-4 text-center">
          📝 Add Historical Forecast
        </h2>
        <p className="text-gray-400 text-sm text-center mb-4">{currencyPair}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Direction */}
          <div>
            <label className="block text-gray-400 text-sm mb-1">Direction *</label>
            <select
              value={formData.direction}
              onChange={(e) => setFormData({...formData, direction: e.target.value})}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
            >
              <option value="UP">UP</option>
              <option value="DOWN">DOWN</option>
            </select>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Trigger Date *</label>
              <input
                type="date"
                value={formData.triggerDate}
                onChange={(e) => setFormData({...formData, triggerDate: e.target.value})}
                className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Time (UTC) *</label>
              <input
                type="time"
                value={formData.triggerTime}
                onChange={(e) => setFormData({...formData, triggerTime: e.target.value})}
                className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
              />
            </div>
          </div>

          {/* Trigger Price */}
          <div>
            <label className="block text-gray-400 text-sm mb-1">Trigger Price *</label>
            <input
              type="number"
              step="0.00001"
              value={formData.triggerPrice}
              onChange={(e) => setFormData({...formData, triggerPrice: e.target.value})}
              placeholder={currencyPair === 'USDJPY' ? '153.500' : '1.08500'}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
            />
          </div>

          {/* Max Pips and End Pips */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Max Pips {formData.direction} *</label>
              <input
                type="number"
                step="0.1"
                value={formData.maxPipsReached}
                onChange={(e) => setFormData({...formData, maxPipsReached: e.target.value})}
                placeholder="45.5"
                className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Pips {formData.direction} at End *</label>
              <input
                type="number"
                step="0.1"
                value={formData.endPips}
                onChange={(e) => setFormData({...formData, endPips: e.target.value})}
                placeholder="12.3"
                className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
              />
            </div>
          </div>

          {/* Trigger Level */}
          <div>
            <label className="block text-gray-400 text-sm mb-1">Trigger Level</label>
            <select
              value={formData.triggerLevel}
              onChange={(e) => setFormData({...formData, triggerLevel: e.target.value})}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
            >
              <option value={1}>1st Trigger</option>
              <option value={2}>2nd Trigger (after 1st incorrect)</option>
              <option value={3}>3rd Trigger (after 2nd incorrect)</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Adding...' : 'Add Forecast'}
            </button>
            <button
              type="button"
              onClick={handleRecalculateStats}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              title="Recalculate stats from all history"
            >
              📊 Recalc
            </button>
          </div>
        </form>

        <button
          onClick={onClose}
          className="w-full mt-4 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Set Daily Open Modal Component for Compass Cycles Forecasts
const SetDailyOpenModal = ({ currencyPair, currentOpen, capturedAt, onClose, onSuccess }) => {
  const [newOpen, setNewOpen] = useState(currentOpen?.toString() || '');
  const [submitting, setSubmitting] = useState(false);

  // Check if capture time is around 22:00 UTC
  const isCorrectCaptureTime = () => {
    if (!capturedAt) return false;
    const captureDate = new Date(capturedAt);
    const hours = captureDate.getUTCHours();
    const minutes = captureDate.getUTCMinutes();
    return hours === 22 && minutes <= 5;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newOpen || isNaN(parseFloat(newOpen))) {
      toast.error('Please enter a valid price');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/2day-cycle-set-open/${currencyPair}?session_id=admin_test_123`, {
        open_price: parseFloat(newOpen)
      });

      toast.success(`${currencyPair} daily open updated to ${newOpen}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to set daily open:', err);
      toast.error('Failed to update daily open');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCaptureTime = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      timeZone: 'UTC' 
    }) + ' UTC';
  };

  const decimalPlaces = currencyPair === 'EURUSD' ? 5 : 3;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
        <h2 className="text-xl font-bold text-white mb-4 text-center">
          📍 Set Daily Open
        </h2>
        <p className="text-gray-400 text-sm text-center mb-4">{currencyPair} Crandell Cycle</p>

        {/* Current Status */}
        <div className={`rounded-lg p-4 mb-4 ${isCorrectCaptureTime() ? 'bg-green-900/30 border border-green-600' : 'bg-yellow-900/30 border border-yellow-600'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Current Open:</span>
            <span className="text-white font-mono text-lg">{currentOpen?.toFixed(decimalPlaces)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Captured At:</span>
            <span className={`text-sm font-medium ${isCorrectCaptureTime() ? 'text-green-400' : 'text-yellow-400'}`}>
              {formatCaptureTime(capturedAt)}
              {isCorrectCaptureTime() ? ' ✓' : ' ⚠️'}
            </span>
          </div>
          {!isCorrectCaptureTime() && (
            <p className="text-yellow-400 text-xs mt-2">
              ⚠️ Open was not captured at 22:00 UTC. You may need to correct it.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">New Daily Open Price *</label>
            <input
              type="number"
              step={currencyPair === 'EURUSD' ? '0.00001' : '0.001'}
              value={newOpen}
              onChange={(e) => setNewOpen(e.target.value)}
              placeholder={currencyPair === 'EURUSD' ? '1.18650' : '152.620'}
              className="w-full bg-gray-700 text-white rounded px-3 py-2 border border-gray-600 font-mono"
              required
            />
            <p className="text-gray-500 text-xs mt-1">
              Enter the actual price at 22:00 UTC session open
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Saving...' : 'Update Open'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 5 Day Forecast Modal Component
const FiveDayForecastModal = ({ forecast, setShow5DayForecast, currencyPair, adminMode, API, fifteenMinClosePrice, zones, determineZoneForPrice, getDisplayZoneName, formatPrice }) => {
  const [prevForecast, setPrevForecast] = useState(null);
  const [showPrevForecast, setShowPrevForecast] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);

  // Fetch previous forecast on mount
  useEffect(() => {
    const fetchPrev = async () => {
      try {
        const res = await axios.get(`${API}/forex/previous-forecast/${currencyPair}`);
        if (res.data.previous_forecast) {
          const pf = res.data.previous_forecast;
          // Check status dynamically if still active
          if (pf.status === 'active' && pf.zones && fifteenMinClosePrice) {
            const currentPrice = fifteenMinClosePrice;
            const prevZones = pf.zones;
            const targetIsUppermost = pf.forecast_type === 'UP';
            
            // Check if arrived in target zone
            if (targetIsUppermost && currentPrice >= prevZones.uppermost_bottom) {
              pf.status = 'arrived';
              pf.final_price = currentPrice;
              pf.final_zone = 'Uppermost';
              // Update backend
              axios.post(`${API}/admin/update-previous-forecast-status/${currencyPair}`, {
                status: 'arrived', final_price: currentPrice, final_zone: 'Uppermost'
              }).catch(() => {});
            } else if (!targetIsUppermost && currentPrice <= prevZones.lowermost_top) {
              pf.status = 'arrived';
              pf.final_price = currentPrice;
              pf.final_zone = 'Lowermost';
              axios.post(`${API}/admin/update-previous-forecast-status/${currencyPair}`, {
                status: 'arrived', final_price: currentPrice, final_zone: 'Lowermost'
              }).catch(() => {});
            } else if (pf.days_remaining <= 0) {
              // Determine current zone in previous week's zones
              let prevZoneName = 'unknown';
              if (currentPrice >= prevZones.uppermost_bottom) prevZoneName = 'Uppermost';
              else if (currentPrice >= prevZones.bull_bottom) prevZoneName = 'Bull';
              else if (currentPrice >= prevZones.equilibrium_bottom) prevZoneName = 'Equilibrium';
              else if (currentPrice >= prevZones.bear_bottom) prevZoneName = 'Bear';
              else prevZoneName = 'Lowermost';
              
              pf.status = 'ended';
              pf.final_price = currentPrice;
              pf.final_zone = prevZoneName;
              axios.post(`${API}/admin/update-previous-forecast-status/${currencyPair}`, {
                status: 'ended', final_price: currentPrice, final_zone: prevZoneName
              }).catch(() => {});
            }
          }
          setPrevForecast(pf);
        }
      } catch (err) {
        console.log('No previous forecast data');
      }
    };
    fetchPrev();
  }, [API, currencyPair, fifteenMinClosePrice]);

  // Format price for display
  const fmtPrice = (price) => {
    if (!price) return '—';
    const p = parseFloat(price);
    return currencyPair.includes('JPY') ? p.toFixed(3) : p.toFixed(5);
  };

  // Get previous zone name for current price
  const getPrevZoneName = (price, prevZones) => {
    if (!price || !prevZones) return 'unknown';
    const p = parseFloat(price);
    if (p >= prevZones.uppermost_bottom) return 'Uppermost';
    if (p >= prevZones.bull_bottom) return 'Bull';
    if (p >= prevZones.equilibrium_bottom) return 'Equilibrium';
    if (p >= prevZones.bear_bottom) return 'Bear';
    return 'Lowermost';
  };

  if (!forecast) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg p-6 max-w-lg w-full ${
        forecast.forecastType === 'UP' ? 'bg-green-900 border-2 border-green-600' :
        forecast.forecastType === 'DOWN' ? 'bg-red-900 border-2 border-red-600' : 
        'bg-gray-800 border-2 border-gray-600'
      }`}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2 text-white">5 Day Forecast</h2>
          <p className="text-xl font-bold text-white mb-1">
            {currencyPair}
          </p>
          <p className="text-xs text-gray-400 mb-4">Signpost will start off in the Equilibrium Zone at the beginning of each week.</p>
          
          <div className="space-y-4">
            {/* Forecast Status */}
            <div className={`rounded-lg p-4 border-2 ${
              forecast.forecastType === 'UP' 
                ? 'bg-green-900 bg-opacity-30 border-green-500' 
                : forecast.forecastType === 'DOWN'
                ? 'bg-red-900 bg-opacity-30 border-red-500'
                : 'bg-gray-700 border-gray-500'
            }`}>
              <div className="flex items-center justify-center mb-2">
                {forecast.forecastType !== 'NONE' && (
                  <span className={`text-2xl mr-2 ${
                    forecast.forecastType === 'UP' ? 'text-green-400' :
                    forecast.forecastType === 'DOWN' ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {forecast.forecastType === 'UP' ? '📈' : 
                     forecast.forecastType === 'DOWN' ? '📉' : '⏸️'}
                  </span>
                )}
                <h3 className={`text-lg font-bold ${
                  forecast.forecastType === 'UP' ? 'text-green-400' :
                  forecast.forecastType === 'DOWN' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {forecast.forecastType === 'NONE' ? 'WAITING' : `${forecast.forecastType} FORECAST`}
                </h3>
              </div>
              {forecast.forecastType !== 'NONE' && (
                <p className="text-gray-300 text-xs mb-2">
                  Forecast begins when the Signpost exits the Equilibrium Zone
                </p>
              )}
              <p className="text-gray-300 text-sm">
                {forecast.forecastType === 'UP' && 'Forecasted time of arrival into Uppermost Zone: 5 days from start'}
                {forecast.forecastType === 'DOWN' && 'Forecasted time of arrival into Lowermost Zone: 5 days from start'}
                {forecast.forecastType === 'NONE' && 'A move out of the Equilibrium Zone will generate a Bull or Bear Forecast'}
              </p>
            </div>

            {/* Current Status */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">Current Status</h4>
              <div className="text-sm text-gray-300 space-y-1">
                <div>Current Price: <span className="text-white font-mono">{forecast.currentPrice}</span></div>
                <div>Current Zone: <span className="text-yellow-400">{forecast.currentZone}</span></div>
                <div>Days Active: <span className="text-blue-400">{forecast.daysActive}</span></div>
                {forecast.forecastType !== 'NONE' && (
                  <div>Target Zone: <span className={
                    forecast.forecastType === 'UP' ? 'text-green-400' : 'text-red-400'
                  }>
                    {forecast.forecastType === 'UP' ? 'Uppermost Zone' : 'Lowermost Zone'}
                  </span></div>
                )}
              </div>
            </div>

            {/* Forecast Progress */}
            {forecast.forecastType !== 'NONE' && (
              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">5-Day Progress</h4>
                <div className="w-full bg-gray-600 rounded-full h-3 mb-2">
                  <div 
                    className={`h-3 rounded-full ${
                      forecast.forecastType === 'UP' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{width: `${Math.min(100, forecast.progressPercentage)}%`}}
                  ></div>
                </div>
                <p className="text-xs text-gray-400">
                  {forecast.progressPercentage}% of 5 days completed
                </p>
              </div>
            )}

            {/* Forecast Status Section - Only show for active forecasts */}
            {forecast.forecastType !== 'NONE' && (
              <div className={`rounded-lg p-4 ${
                forecast.currentZone === 'Uppermost' ? 'bg-green-800 border-2 border-green-400' :
                forecast.currentZone === 'Lowermost' ? 'bg-red-800 border-2 border-red-400' :
                'bg-gray-700'
              }`}>
                <h4 className={`font-semibold mb-1 text-center ${
                  forecast.currentZone === 'Uppermost' ? 'text-green-300' :
                  forecast.currentZone === 'Lowermost' ? 'text-red-300' :
                  'text-white'
                }`}>
                  {forecast.currentZone === 'Uppermost' ? 'Arrived in Uppermost Zone' :
                   forecast.currentZone === 'Lowermost' ? 'Arrived in Lowermost Zone' :
                   'Forecast Active'}
                </h4>
              </div>
            )}

            {/* Previous Forecast Section */}
            <div className="mt-2">
              <button
                onClick={() => setShowPrevForecast(!showPrevForecast)}
                className="w-full bg-gray-600 hover:bg-gray-500 text-white text-sm px-4 py-2 rounded-lg transition-colors"
                data-testid="prev-forecast-toggle"
              >
                {showPrevForecast ? '▼ Hide Previous Forecast' : '▶ Previous Forecast'}
              </button>
              {showPrevForecast && (
                <div className="mt-2 bg-gray-700 rounded-lg p-4 text-sm">
                  {prevForecast ? (
                    <div className="space-y-2">
                      {prevForecast.status === 'arrived' ? (
                        <div className="text-center">
                          <p className={`font-bold ${prevForecast.forecast_type === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                            {prevForecast.forecast_type === 'UP' ? '✅ Previous UP Forecast arrived in Uppermost Zone' : '✅ Previous DOWN Forecast arrived in Lowermost Zone'}
                          </p>
                          {prevForecast.final_price && (
                            <p className="text-gray-300 mt-1">at price <span className="text-white font-mono font-bold">{fmtPrice(prevForecast.final_price)}</span></p>
                          )}
                        </div>
                      ) : prevForecast.status === 'ended' ? (
                        <div className="text-center">
                          <p className="text-yellow-400 font-bold">
                            {prevForecast.forecast_type === 'UP'
                              ? `❌ Previous UP Forecast ended in ${prevForecast.final_zone || 'unknown'} Zone`
                              : `❌ Previous DOWN Forecast ended in ${prevForecast.final_zone || 'unknown'} Zone`}
                          </p>
                          {prevForecast.final_price && (
                            <p className="text-gray-300 mt-1">at price <span className="text-white font-mono font-bold">{fmtPrice(prevForecast.final_price)}</span></p>
                          )}
                          <p className="text-gray-400 mt-1">
                            {prevForecast.forecast_type === 'UP'
                              ? `without reaching previous week's Uppermost Zone price range of ${fmtPrice(prevForecast.zones?.uppermost_bottom)} – ${fmtPrice(prevForecast.zones?.uppermost_top)}`
                              : `without reaching previous week's Lowermost Zone price range of ${fmtPrice(prevForecast.zones?.lowermost_bottom)} – ${fmtPrice(prevForecast.zones?.lowermost_top)}`}
                          </p>
                        </div>
                      ) : prevForecast.status === 'active' ? (
                        <div className="text-center">
                          <p className="text-blue-400 font-bold">
                            ⏳ Previous {prevForecast.forecast_type} Forecast not ended — {prevForecast.days_remaining} day{prevForecast.days_remaining !== 1 ? 's' : ''} remaining
                          </p>
                          <p className="text-gray-300 mt-1">
                            Currently in previous week's {getPrevZoneName(fifteenMinClosePrice, prevForecast.zones)} Zone with current price at <span className="text-white font-mono font-bold">{fmtPrice(fifteenMinClosePrice)}</span>
                          </p>
                          <p className="text-gray-400 mt-1">
                            {prevForecast.forecast_type === 'UP'
                              ? `Previous week's Uppermost Zone price range: ${fmtPrice(prevForecast.zones?.uppermost_bottom)} – ${fmtPrice(prevForecast.zones?.uppermost_top)}`
                              : `Previous week's Lowermost Zone price range: ${fmtPrice(prevForecast.zones?.lowermost_bottom)} – ${fmtPrice(prevForecast.zones?.lowermost_top)}`}
                          </p>
                        </div>
                      ) : (
                        <p className="text-gray-400 text-center">No previous forecast recorded.</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center">No previous forecast recorded.</p>
                  )}
                </div>
              )}
            </div>

            {/* Admin: Save current as Previous Forecast */}
            {adminMode && forecast.forecastType !== 'NONE' && (
              <div className="mt-2">
                <button
                  onClick={async () => {
                    const daysStr = prompt('How many trading days remaining for this forecast when carried into next week?', '3');
                    if (daysStr === null) return;
                    const daysRemaining = parseInt(daysStr) || 0;
                    
                    try {
                      const zoneData = zones ? {
                        uppermost_top: zones.uppermostArrivalZone.top,
                        uppermost_bottom: zones.uppermostArrivalZone.bottom,
                        bull_top: zones.bullZone.top,
                        bull_bottom: zones.bullZone.bottom,
                        equilibrium_top: zones.equilibriumZone.top,
                        equilibrium_bottom: zones.equilibriumZone.bottom,
                        bear_top: zones.bearZone.top,
                        bear_bottom: zones.bearZone.bottom,
                        lowermost_top: zones.lowermostArrivalZone.top,
                        lowermost_bottom: zones.lowermostArrivalZone.bottom
                      } : null;
                      
                      await axios.post(`${API}/admin/save-previous-forecast/${currencyPair}?session_id=admin_test_123`, {
                        forecast_type: forecast.forecastType,
                        zones: zoneData,
                        days_remaining: daysRemaining,
                        status: 'active',
                        target_zone: forecast.forecastType === 'UP' ? 'Uppermost' : 'Lowermost',
                        target_zone_range: forecast.forecastType === 'UP'
                          ? `${zones.uppermostArrivalZone.bottom.toFixed(5)} – ${zones.uppermostArrivalZone.top.toFixed(5)}`
                          : `${zones.lowermostArrivalZone.bottom.toFixed(5)} – ${zones.lowermostArrivalZone.top.toFixed(5)}`
                      });
                      alert('Current forecast saved as Previous Forecast');
                      const res = await axios.get(`${API}/forex/previous-forecast/${currencyPair}`);
                      if (res.data.previous_forecast) setPrevForecast(res.data.previous_forecast);
                    } catch (err) {
                      alert('Failed to save previous forecast');
                    }
                  }}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-4 py-2 rounded-lg transition-colors"
                  data-testid="save-prev-forecast-btn"
                >
                  💾 Save Current as Previous Forecast
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShow5DayForecast(false)}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Prediction Horizon Probabilities Modal Component  
const DirectionProbabilitiesModal = ({ probabilities, setShowDirectionProbabilities }) => {
  if (!probabilities) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2 text-white">🏆 Top-Ranked Patterns in Current Block</h2>
          <p className="text-white mb-6 text-base">
            Probabilities that this Current Price Bar will Close:
          </p>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {probabilities.has_ranked_patterns ? (
              probabilities.found_patterns.map((pattern, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-left">
                      <h3 className="text-blue-300 font-semibold">#{pattern.rank} Ranked Pattern</h3>
                      <p className="text-gray-300 text-sm">{pattern.pattern} • {pattern.direction}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">Scanner Confidence</div>
                      <div className="text-blue-200 font-bold">{pattern.confidence}%</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-3">
                      <h4 className="text-green-400 font-semibold text-sm mb-2">Probability Higher</h4>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-400">{pattern.up_probability}%</div>
                      </div>
                    </div>
                    <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-3">
                      <h4 className="text-red-400 font-semibold text-sm mb-2">Probability Lower</h4>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-400">{pattern.down_probability}%</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-400">
                      Historical Sample: {pattern.sample_size} occurrences
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-700 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">📭</div>
                <h3 className="text-white font-semibold mb-2">No Top Ranked Patterns</h3>
                <p className="text-gray-300 text-sm">
                  None of the top 3 ranked Directional Patterns from the scanner are present in the current block.
                </p>
              </div>
            )}
          </div>
          
          {/* Analysis Details */}
          <div className="mt-6 p-4 bg-gray-700 rounded-lg text-left">
            <p className="text-xs text-gray-300 mb-2">
              <strong>Analysis:</strong> {probabilities.patterns_analyzed} top-ranked patterns found in current block
            </p>
            <p className="text-xs text-gray-400">
              {probabilities.methodology}
            </p>
          </div>
          
          <button
            onClick={() => setShowDirectionProbabilities(false)}
            className="mt-6 bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded text-white text-sm font-medium w-full"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Friday Price Update Modal Component  
const PriceUpdateModal = ({ fridayPrices, setFridayPrices, setShowPriceUpdateModal, setFifteenMinClosePrice, currencyPair }) => {
  const inputRefs = useRef({});
  
  const handleUpdatePrices = () => {
    const newPrices = {};
    Object.keys(fridayPrices).forEach(pair => {
      const inputValue = inputRefs.current[pair]?.value || fridayPrices[pair];
      newPrices[pair] = inputValue;
    });
    // Update Friday prices state
    setFridayPrices(newPrices);
    
    // CRITICAL FIX: Save Friday prices to localStorage for persistence
    localStorage.setItem('admin_friday_prices', JSON.stringify(newPrices));
    console.log("💾 Saved Friday prices to localStorage:", newPrices);
    
    // CRITICAL FIX: Update the current 15-min close price to match the new Friday price for current currency
    // This ensures the signpost positions correctly beside the Equilibrium Zone for fresh zones
    // The useEffect will also handle this, but we do it here immediately for instant UI update
    if (newPrices[currencyPair]) {
      const newCurrentPrice = parseFloat(newPrices[currencyPair]);
      console.log("🎯 Admin Update: Setting 15m close price to match new Friday price:", currencyPair, newCurrentPrice);
      setFifteenMinClosePrice(newCurrentPrice);
    }
    
    setShowPriceUpdateModal(false);
  };

  const handleCancel = () => {
    setShowPriceUpdateModal(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4 text-white">📊 Update Friday Closing Prices</h2>
          <p className="text-gray-300 mb-6 text-sm">
            Enter the Friday closing prices for fixed pip-based zone calculations (E - Equilibrium Zone Start Price).
          </p>
          
          <div className="space-y-4">
            {Object.keys(fridayPrices).map((pair) => (
              <div key={pair} className="flex items-center justify-between">
                <label className="text-white font-medium text-sm w-20">{pair}:</label>
                <input
                  ref={el => inputRefs.current[pair] = el}
                  type="text"
                  placeholder="Enter price"
                  defaultValue={fridayPrices[pair]}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm flex-1 ml-3 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            ))}
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleCancel}
              className="flex-1 bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdatePrices}
              className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white text-sm"
            >
              Update Prices
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bias Density Modal Component removed - functionality consolidated into Directional Patterns Scanner

function App() {
  const [currencyPair, setCurrencyPair] = useState("EURUSD");
  const [timeframe, setTimeframe] = useState("1 Day");
  const [analysisType, setAnalysisType] = useState("5 Day Forecast");
  const [patternLength, setPatternLength] = useState(5);
  const [useCase, setUseCase] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [recentPatterns, setRecentPatterns] = useState([]);
  const [countdown, setCountdown] = useState("");
  const [nextUpdateTimer, setNextUpdateTimer] = useState(900); // 15 minutes in seconds
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("premium_monthly");
  const [adminMode, setAdminMode] = useState(false);
  const [showPriceUpdateModal, setShowPriceUpdateModal] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [buildingMode, setBuildingMode] = useState(false);
  const [toastsEnabled, setToastsEnabled] = useState(false);
  const [showBuildingSignup, setShowBuildingSignup] = useState(false);

  // Load building mode and toast setting on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/building-mode`);
        setBuildingMode(res.data.building_mode || false);
        setToastsEnabled(res.data.toasts_enabled !== false);
      } catch (e) {}
    })();
  }, []);
  
  // State for Free Access Settings - now loaded from backend
  const [showFreeAccessModal, setShowFreeAccessModal] = useState(false);
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [freeAccessEnabled, setFreeAccessEnabled] = useState(false);
  const [freeAccessStartDay, setFreeAccessStartDay] = useState(1);
  const [freeAccessEndDay, setFreeAccessEndDay] = useState(5);
  const [freeAccessDate, setFreeAccessDate] = useState('');
  const [freeAccessEndDate, setFreeAccessEndDate] = useState('');
  const [freeAccessMode, setFreeAccessMode] = useState('day');
  const [freeAccessSettingsLoaded, setFreeAccessSettingsLoaded] = useState(false);
  
  // State for Prediction Horizon Probabilities modal
  const [showDirectionProbabilities, setShowDirectionProbabilities] = useState(false);
  const [directionProbabilities, setDirectionProbabilities] = useState(null);
  const [horizonLoading, setHorizonLoading] = useState(false);
  
  // State for Zone Close Stats modal
  const [showZoneCloseStats, setShowZoneCloseStats] = useState(false);
  const [zoneCloseStats, setZoneCloseStats] = useState(null);
  
  // State for 5 Day Forecast modal
  const [show5DayForecast, setShow5DayForecast] = useState(false);
  const [fiveDayForecast, setFiveDayForecast] = useState(null);
  
  // State for Pivot Point Path Dashboard
  const [showPocVwapDashboard, setShowPocVwapDashboard] = useState(false);
  
  // State for USDJPY 2-Day Cycle Forecast
  const [showUsdjpyForecast, setShowUsdjpyForecast] = useState(false);
  const [usdjpyPhase, setUsdjpyPhase] = useState(null);
  const [usdjpyForecastDirection, setUsdjpyForecastDirection] = useState(null); // 'UP', 'DOWN', or null
  const [usdjpyScyllaDirection, setUsdjpyScyllaDirection] = useState(null);
  const [usdjpyCharybdisDirection, setUsdjpyCharybdisDirection] = useState(null);
  const [usdjpyScyllaPending, setUsdjpyScyllaPending] = useState(false);
  const [usdjpyCharybdisPending, setUsdjpyCharybdisPending] = useState(false);
  const previousUsdjpyPhaseRef = useRef(null);
  const previousUsdjpyZetaRef = useRef(null);
  
  // Check for USDJPY 2-Day Cycle Forecast changes
  useEffect(() => {
    const checkUsdjpyForecast = async () => {
      try {
        const response = await axios.get(`${API}/forex/2day-cycle-forecast/USDJPY`);
        const forecastData = response.data.forecast;
        const displayStatus = forecastData?.display_status || 'Forming New Crest or Trough';
        
        // Get direction from active_forecasts (V2) or active_forecast (backward compat)
        const activeForecasts = forecastData?.active_forecasts || [];
        const activeForecast = forecastData?.active_forecast;
        const manualForecast = forecastData?.manual_forecast;
        let direction = null;
        
        // In direction_only mode, use cycle_direction for the blinking light
        if (forecastData?.direction_only) {
          const cd = forecastData?.cycle_direction;
          direction = cd === 'UP' ? 'UP' : cd === 'DOWN' ? 'DOWN' : null;
        } else if (activeForecasts.length > 0) {
          direction = activeForecasts[0].direction;
        } else if (activeForecast) {
          direction = activeForecast.direction;
        } else if (manualForecast) {
          direction = manualForecast.direction;
        }
        
        setUsdjpyPhase(displayStatus);
        setUsdjpyForecastDirection(direction);
        
        // Set separate Scylla/Charybdis directions for button lights
        const scyllaDir = activeForecasts.length > 0 ? activeForecasts[0].direction : null;
        const charybdisDir = manualForecast ? manualForecast.direction : null;
        setUsdjpyScyllaDirection(scyllaDir);
        setUsdjpyCharybdisDirection(charybdisDir);
        setUsdjpyScyllaPending(!scyllaDir && !!(forecastData?.eta_pending_up || forecastData?.eta_pending_down || forecastData?.pending_forecasts?.length));
        setUsdjpyCharybdisPending(!charybdisDir && !!(forecastData?.zeta_pending_up || forecastData?.zeta_pending_down));
        
        // Check for new Scylla (Eta) trigger to show toast
        if (activeForecasts.length > 0) {
          const latestForecast = activeForecasts[0];
          const forecastKey = latestForecast.id;
          
          if (previousUsdjpyPhaseRef.current !== forecastKey && direction) {
            const dirLabel = direction === 'UP' ? 'Up Cycle' : 'Down Cycle';
            if (!(buildingMode && !adminMode) && toastsEnabled) {
              playNotificationChime();
              toast.success(
                `USDJPY Scylla ${dirLabel}`,
                {
                  description: `Random Forest Cycles`,
                  duration: 8000,
                  style: {
                    background: direction === 'UP' ? '#166534' : '#991b1b',
                    border: direction === 'UP' ? '2px solid #22c55e' : '2px solid #ef4444',
                    color: 'white',
                    fontSize: '1.1rem',
                  },
                }
              );
            }
          }
          previousUsdjpyPhaseRef.current = forecastKey;
        } else {
          previousUsdjpyPhaseRef.current = null;
        }
        
        // Check for new Charybdis (Zeta) trigger to show toast
        if (manualForecast) {
          const zetaKey = `${manualForecast.direction}_${manualForecast.entry_price}`;
          if (previousUsdjpyZetaRef.current !== zetaKey) {
            const zetaDir = manualForecast.direction === 'UP' ? 'Up Cycle' : 'Down Cycle';
            if (!(buildingMode && !adminMode) && toastsEnabled) {
              playNotificationChime();
              toast.success(
                `USDJPY Charybdis ${zetaDir}`,
                {
                  description: `Random Forest Cycles`,
                  duration: 8000,
                  style: {
                    background: manualForecast.direction === 'UP' ? '#166534' : '#991b1b',
                    border: manualForecast.direction === 'UP' ? '2px solid #22c55e' : '2px solid #ef4444',
                    color: 'white',
                    fontSize: '1.1rem',
                  },
                }
              );
            }
          }
          previousUsdjpyZetaRef.current = zetaKey;
        } else {
          previousUsdjpyZetaRef.current = null;
        }
      } catch (err) {
        console.error('Error checking USDJPY forecast:', err);
      }
    };
    
    checkUsdjpyForecast();
    const interval = setInterval(checkUsdjpyForecast, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  // Map update date display
  const mapUpdateDate = '1st of February';
  
  // Bias Density removed as requested - functionality consolidated into Directional Patterns Scanner
  
  // State for Directional Patterns Scanner modal
  const [showCompassScanner, setShowCompassScanner] = useState(false);
  const [compassScannerData, setCompassScannerData] = useState(null);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [currentBlockPatterns, setCurrentBlockPatterns] = useState([]);
  
  // State for logo zoom modal
  const [showLogoZoom, setShowLogoZoom] = useState(false);
  
  // State for Free Access Notification Signup
  const [showFreeAccessSignup, setShowFreeAccessSignup] = useState(false);
  
  // State for Correlation Tables
  const [showCorrelationTables, setShowCorrelationTables] = useState(false);
  const [showCorrelationAdmin, setShowCorrelationAdmin] = useState(false);
  const [correlationData, setCorrelationData] = useState(null);

  // State for Price Horizons modal
  const [showPriceHorizons, setShowPriceHorizons] = useState(false);
  const [priceHorizonsData, setPriceHorizonsData] = useState(null);
  const [horizonsLoading, setHorizonsLoading] = useState(false);

  // State for Peaks and Valleys Admin modal
  const [showPeaksValleysAdmin, setShowPeaksValleysAdmin] = useState(false);
  const [peaksValleys, setPeaksValleys] = useState({});
  const [dailyOpenLoading, setDailyOpenLoading] = useState(false);
  const [manualOpens, setManualOpens] = useState({});
  const [showDailyOpenAdmin, setShowDailyOpenAdmin] = useState(false);
  const [economicData, setEconomicData] = useState({});
  const [showEconomicAdmin, setShowEconomicAdmin] = useState(false);

  // State for Quintile Valleys (new system)
  const [showQuintileValleys, setShowQuintileValleys] = useState(false);
  const [showTrainedNavigator, setShowTrainedNavigator] = useState(false);
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [inTrainingMode, setInTrainingMode] = useState(false);

  // Load in_training flag on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/matrix20-config?session_id=admin_test_123`);
        if (res.data.in_training !== undefined) setInTrainingMode(res.data.in_training);
      } catch (e) {}
    })();
  }, []);
  const [matrixLastUpdated, setMatrixLastUpdated] = useState(null);
  const [showSmaConfig, setShowSmaConfig] = useState(false);
  const [smaForm, setSmaForm] = useState({ sma1: 20, sma2: 50, sma3: 200 });
  const [maTfForm, setMaTfForm] = useState(['M5', 'M15', 'M30', 'H1']);
  const [bufferForm, setBufferForm] = useState({
    open: { EURUSD: 8, GBPUSD: 8, USDJPY: 12, USDCHF: 8, USDCAD: 8, AUDUSD: 5, NZDUSD: 5 },
    sma: { EURUSD: 4, GBPUSD: 4, USDJPY: 6, USDCHF: 4, USDCAD: 4, AUDUSD: 2, NZDUSD: 2 },
    targets: { EURUSD: 10, GBPUSD: 10, USDJPY: 12, USDCHF: 10, USDCAD: 10, AUDUSD: 8, NZDUSD: 8 },
    stops: { EURUSD: 0, GBPUSD: 0, USDJPY: 0, USDCHF: 0, USDCAD: 0, AUDUSD: 0, NZDUSD: 0 },
  });
  const [thresholdForm, setThresholdForm] = useState({ bull: 14, bear: 14, useGoFlat: true, goFlatThreshold: 0 });
  const [showMatrixHeaders, setShowMatrixHeaders] = useState(false);
  const [showMatrixSignals, setShowMatrixSignals] = useState(false);
  const [matrixSignals, setMatrixSignals] = useState(null);
  const [showMatrixTracking, setShowMatrixTracking] = useState(false);
  const [matrixTracking, setMatrixTracking] = useState(null);
  const [invertSignals, setInvertSignals] = useState(false);

  // Auto-refresh signals modal every 60 seconds
  const [signalsLastRefresh, setSignalsLastRefresh] = useState(null);
  useEffect(() => {
    if (!showMatrixSignals) return;
    const fetchSignals = async () => {
      try {
        const res = await axios.get(`${API}/forex/matrix20-signals`);
        setMatrixSignals(res.data.signals);
        setSignalsLastRefresh(new Date());
      } catch (e) { console.error('Signals refresh error', e); }
    };
    fetchSignals(); // immediate fetch on open
    const interval = setInterval(fetchSignals, 60000);
    return () => clearInterval(interval);
  }, [showMatrixSignals]);

  // Auto-refresh matrix every 60 seconds while modal is open
  useEffect(() => {
    if (!showTrainedNavigator) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/forex/matrix20`);
        setMatrixData(res.data);
        setMatrixLastUpdated(new Date());
      } catch (e) { console.error('Matrix auto-refresh error', e); }
    }, 60000);
    return () => clearInterval(interval);
  }, [showTrainedNavigator]);
  const [quintileValleysData, setQuintileValleysData] = useState(null);
  const [quintileValleysLoading, setQuintileValleysLoading] = useState(false);
  
  // State for UnderValue Valleys (legacy)
  const [showUndervaluedValleys, setShowUndervaluedValleys] = useState(false);
  const [undervaluedValleysData, setUndervaluedValleysData] = useState(null);
  const [undervaluedValleysLoading, setUndervaluedValleysLoading] = useState(false);
  const [undervaluedValleys, setUndervaluedValleys] = useState({});

  // State for 2 Day Forecast modal
  const [showDayForecast, setShowDayForecast] = useState(false);
  const [dayForecastData, setDayForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  
  // State for Institutional Bias Locations modal
  const [showInstitutionalBias, setShowInstitutionalBias] = useState(false);
  const [institutionalBiasData, setInstitutionalBiasData] = useState(null);
  const [institutionalBiasLoading, setInstitutionalBiasLoading] = useState(false);
  
  // State for HEGM Horizons modal
  const [showHegmHorizons, setShowHegmHorizons] = useState(false);
  const [hegmHorizonsData, setHegmHorizonsData] = useState(null);
  const [hegmHorizonsLoading, setHegmHorizonsLoading] = useState(false);
  
  // State for EURUSD 2-Day Cycle forecast indicator
  const [eurusdPerpetualPhase, setEurusdPerpetualPhase] = useState(null);
  const [eurusdForecastDirection, setEurusdForecastDirection] = useState(null); // 'UP', 'DOWN', or null
  const [eurusdScyllaDirection, setEurusdScyllaDirection] = useState(null);
  const [eurusdCharybdisDirection, setEurusdCharybdisDirection] = useState(null);
  const [eurusdScyllaPending, setEurusdScyllaPending] = useState(false);
  const [eurusdCharybdisPending, setEurusdCharybdisPending] = useState(false);
  const previousEurusdPhaseRef = useRef(null);
  const previousEurusdZetaRef = useRef(null);
  
  // State for EURUSD Pivot Point combined bias indicator
  const [pocVwapCombinedBias, setPocVwapCombinedBias] = useState(0);
  
  // Check for EURUSD 2-Day Cycle Forecast periodically
  useEffect(() => {
    const checkEurusdForecast = async () => {
      try {
        const response = await axios.get(`${API}/forex/2day-cycle-forecast/EURUSD`);
        const forecastData = response.data.forecast;
        const displayStatus = forecastData?.display_status || 'Forming New Crest or Trough';
        
        // Get direction from active_forecasts (V2) or active_forecast (backward compat)
        const activeForecasts = forecastData?.active_forecasts || [];
        const activeForecast = forecastData?.active_forecast;
        const manualForecast = forecastData?.manual_forecast;
        let direction = null;
        
        // In direction_only mode, use cycle_direction for the blinking light
        if (forecastData?.direction_only) {
          const cd = forecastData?.cycle_direction;
          direction = cd === 'UP' ? 'UP' : cd === 'DOWN' ? 'DOWN' : null;
        } else if (activeForecasts.length > 0) {
          direction = activeForecasts[0].direction;
        } else if (activeForecast) {
          direction = activeForecast.direction;
        } else if (manualForecast) {
          direction = manualForecast.direction;
        }
        
        setEurusdPerpetualPhase(displayStatus);
        setEurusdForecastDirection(direction);
        
        // Set separate Scylla/Charybdis directions for button lights
        const scyllaDir = activeForecasts.length > 0 ? activeForecasts[0].direction : null;
        const charybdisDir = manualForecast ? manualForecast.direction : null;
        setEurusdScyllaDirection(scyllaDir);
        setEurusdCharybdisDirection(charybdisDir);
        setEurusdScyllaPending(!scyllaDir && !!(forecastData?.eta_pending_up || forecastData?.eta_pending_down || forecastData?.pending_forecasts?.length));
        setEurusdCharybdisPending(!charybdisDir && !!(forecastData?.zeta_pending_up || forecastData?.zeta_pending_down));
        
        // Check for new Scylla (Eta) trigger to show toast
        if (activeForecasts.length > 0) {
          const latestForecast = activeForecasts[0];
          const forecastKey = latestForecast.id;
          
          if (previousEurusdPhaseRef.current !== forecastKey && direction) {
            const dirLabel = direction === 'UP' ? 'Up Cycle' : 'Down Cycle';
            if (!(buildingMode && !adminMode) && toastsEnabled) {
              playNotificationChime();
              toast.success(
                `EURUSD Scylla ${dirLabel}`,
                {
                  description: `Random Forest Cycles`,
                  duration: 8000,
                  style: {
                    background: direction === 'UP' ? '#166534' : '#991b1b',
                    border: direction === 'UP' ? '2px solid #22c55e' : '2px solid #ef4444',
                    color: 'white',
                    fontSize: '1.1rem',
                  },
                }
              );
            }
          }
          previousEurusdPhaseRef.current = forecastKey;
        } else {
          previousEurusdPhaseRef.current = null;
        }
        
        // Check for new Charybdis (Zeta) trigger to show toast
        if (manualForecast) {
          const zetaKey = `${manualForecast.direction}_${manualForecast.entry_price}`;
          if (previousEurusdZetaRef.current !== zetaKey) {
            const zetaDir = manualForecast.direction === 'UP' ? 'Up Cycle' : 'Down Cycle';
            if (!(buildingMode && !adminMode) && toastsEnabled) {
              playNotificationChime();
              toast.success(
                `EURUSD Charybdis ${zetaDir}`,
                {
                  description: `Random Forest Cycles`,
                  duration: 8000,
                  style: {
                    background: manualForecast.direction === 'UP' ? '#166534' : '#991b1b',
                    border: manualForecast.direction === 'UP' ? '2px solid #22c55e' : '2px solid #ef4444',
                    color: 'white',
                    fontSize: '1.1rem',
                  },
                }
              );
            }
          }
          previousEurusdZetaRef.current = zetaKey;
        } else {
          previousEurusdZetaRef.current = null;
        }
      } catch (err) {
        console.error('Error checking EURUSD forecast:', err);
      }
    };
    
    checkEurusdForecast();
    const interval = setInterval(checkEurusdForecast, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  // Check EURUSD Pivot Point combined bias periodically
  useEffect(() => {
    const checkPocVwapBias = async () => {
      try {
        const response = await axios.get(`${API}/forex/poc-vwap-dashboard/EURUSD`);
        const dashboardData = response.data.dashboard;
        // Backend returns combined_bias as a number directly
        if (dashboardData?.combined_bias !== undefined) {
          setPocVwapCombinedBias(dashboardData.combined_bias);
          console.log('📊 EURUSD Pivot Point bias:', dashboardData.combined_bias);
        }
      } catch (err) {
        console.error('Error checking Pivot Point bias:', err);
      }
    };
    
    checkPocVwapBias();
    const interval = setInterval(checkPocVwapBias, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  // State for USDJPY Pivot Point Dashboard
  const [showUsdjpyPocVwapDashboard, setShowUsdjpyPocVwapDashboard] = useState(false);
  const [usdjpyPocVwapCombinedBias, setUsdjpyPocVwapCombinedBias] = useState(0);
  
  // Check USDJPY Pivot Point combined bias periodically
  useEffect(() => {
    const checkUsdjpyPocVwapBias = async () => {
      try {
        const response = await axios.get(`${API}/forex/poc-vwap-dashboard/USDJPY`);
        const dashboardData = response.data.dashboard;
        // Backend returns combined_bias as a number directly
        if (dashboardData?.combined_bias !== undefined) {
          setUsdjpyPocVwapCombinedBias(dashboardData.combined_bias);
          console.log('📊 USDJPY Pivot Point bias:', dashboardData.combined_bias);
        }
      } catch (err) {
        console.error('Error checking USDJPY Pivot Point bias:', err);
      }
    };
    
    checkUsdjpyPocVwapBias();
    const interval = setInterval(checkUsdjpyPocVwapBias, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  // Function to fetch Institutional Bias data
  const fetchInstitutionalBias = async () => {
    try {
      setInstitutionalBiasLoading(true);
      const response = await axios.get(`${API}/forex/institutional-bias`);
      setInstitutionalBiasData(response.data);
    } catch (err) {
      console.error('Error fetching institutional bias:', err);
      toast.error('Failed to load Institutional Bias data');
    } finally {
      setInstitutionalBiasLoading(false);
    }
  };
  
  // Fetch HEGM Horizons data
  const fetchHegmHorizons = async (pair) => {
    try {
      setHegmHorizonsLoading(true);
      const response = await axios.get(`${API}/forex/hegm-horizons/${pair}`);
      setHegmHorizonsData(response.data);
    } catch (err) {
      console.error('Error fetching HEGM Horizons:', err);
      toast.error('Failed to load HEGM Horizons data');
    } finally {
      setHegmHorizonsLoading(false);
    }
  };
  
  // Fetch institutional bias when modal opens and refresh every minute
  useEffect(() => {
    if (showInstitutionalBias) {
      fetchInstitutionalBias();
      const interval = setInterval(fetchInstitutionalBias, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [showInstitutionalBias]);
  
  // State for grid patterns and scanner rankings
  const [gridPatterns, setGridPatterns] = useState({ bullish: {}, bearish: {} });
  const [scannerRankings, setScannerRankings] = useState({ bullish: [], bearish: [] });

  // Format price display - Updated for better 15-minute change visibility
  const formatPrice = (price) => {
    if (!price) return '0.00000';
    // Show more decimal places to make 15-minute incremental changes visible
    // JPY pairs (USDJPY, GBPJPY): 3 decimal places
    // All other pairs: 5 decimal places
    return parseFloat(price).toFixed(currencyPair.includes('JPY') ? 3 : 5);
  };

  // Get needle angle based on zone - 5-zone system
  const getNeedleAngle = (zone) => {
    const angles = {
      'uppermostArrival': -90,       // North - straight up for Uppermost Zone
      'bull': -45,                   // Northeast - points to green (Bull Zone color)
      'equilibrium': 0,              // East - points to white (Equilibrium color)
      'bear': 45,                    // Southeast - points to red (Bear Zone color)
      'lowermostArrival': 90         // South - straight down for Lowermost Zone
    };
    return angles[zone] || 0;
  };

  // Get vertical position for compass - 5-zone system
  const getCompassPosition = (zone) => {
    // Signpost positioning for 5-zone system
    // Each zone uses flex-1, so they're equally sized at ~120px each (600px / 5 zones)
    const zoneHeight = 120; // Approximate height of each zone
    const textOffset = 20; // Offset to align with top of zone name letters
    
    const positions = {
      'uppermostArrival': 10 + zoneHeight * 0 + textOffset,     // Align with Uppermost Zone
      'bull': 10 + zoneHeight * 1 + textOffset,                 // Align with Bull Zone  
      'equilibrium': 10 + zoneHeight * 2 + textOffset,          // Align with Equilibrium Zone (center)
      'bear': 10 + zoneHeight * 3 + textOffset,                 // Align with Bear Zone
      'lowermostArrival': 10 + zoneHeight * 4 + textOffset      // Align with Lowermost Zone
    };
    return positions[zone] || (10 + zoneHeight * 2 + textOffset); // Default to equilibrium (center)
  };

  // Determine which zone the 15m close price falls into - 5-zone system
  const determineZoneForPrice = (price, zones) => {
    if (!price || !zones) return null;
    
    // Debug zone determination
    console.log("🎯 Signpost Positioning: Determining zone for price", price, "in", currencyPair);
    console.log("  - Equilibrium Zone:", zones.equilibriumZone.bottom, "to", zones.equilibriumZone.top);
    
    if (price >= zones.uppermostArrivalZone.bottom) {
      console.log("  - Price is in: Uppermost Zone");
      return 'uppermostArrival';
    }
    if (price >= zones.bullZone.bottom) {
      console.log("  - Price is in: Bull Zone");
      return 'bull';
    }
    if (price >= zones.equilibriumZone.bottom) {
      console.log("  - Price is in: Equilibrium Zone");
      return 'equilibrium';
    }
    if (price >= zones.bearZone.bottom) {
      console.log("  - Price is in: Bear Zone");
      return 'bear';
    }
    console.log("  - Price is in: Lowermost Zone");
    return 'lowermostArrival';
  };

  // Initialize Friday prices with real Friday close prices (with localStorage persistence)
  const [fridayPrices, setFridayPrices] = useState(() => {
    // Current valid pairs list
    const validPairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'];
    
    // Default Friday prices
    const defaultPrices = {
      EURUSD: '1.15896',
      GBPUSD: '1.35128', 
      USDJPY: '146.974',
      AUDUSD: '0.65440',
      USDCAD: '1.37410',
      USDCHF: '0.80018',
      NZDUSD: '0.58988',
      GBPAUD: '2.06500',
      GBPCHF: '1.08100',
      EURJPY: '170.500'
    };
    
    // Try to get saved Friday prices from localStorage
    const savedPrices = localStorage.getItem('admin_friday_prices');
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        // Merge saved prices with defaults, but only keep valid pairs
        const merged = { ...defaultPrices };
        validPairs.forEach(pair => {
          if (parsed[pair]) {
            merged[pair] = parsed[pair];
          }
        });
        // Update localStorage with cleaned data
        localStorage.setItem('admin_friday_prices', JSON.stringify(merged));
        console.log("🎯 Loaded and cleaned Friday prices from localStorage:", merged);
        return merged;
      } catch (e) {
        console.log("❌ Error parsing saved Friday prices, using defaults");
      }
    }
    
    return defaultPrices;
  });

  // Map user-friendly timeframe names to backend format
  const mapTimeframeForBackend = (userTimeframe) => {
    const mapping = {
      "1 Day": "Next Day",
      "1 Hour": "Next 1 Hour", 
      "4 Hours": "Next 4 Hours"
    };
    return mapping[userTimeframe] || userTimeframe;
  };

  // Currency pairs
  const currencyPairs = [
    "EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCAD", "USDCHF", "NZDUSD"
  ];

  // Get session ID from URL if returning from payment
  const getSessionIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('session_id');
  };

  // Check if payment was successful
  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('payment');
  };

  // Get stored session ID from localStorage
  const getStoredSessionId = () => {
    // Check if admin mode is enabled
    if (adminMode || localStorage.getItem('admin_mode') === 'true') {
      return 'admin_test_123';
    }
    // Otherwise return stored session
    return localStorage.getItem('subscription_session_id');
  };

  // 🎁 TRIAL MANAGEMENT SYSTEM
  const trialManager = {
    // Activate a new 7-day free trial
    activateTrial: () => {
      const trialStart = new Date();
      const trialEnd = new Date(trialStart.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days from now
      
      localStorage.setItem('trial_start_date', trialStart.toISOString());
      localStorage.setItem('trial_end_date', trialEnd.toISOString());
      localStorage.setItem('trial_active', 'true');
      
      console.log("🎁 Free trial activated! Valid until:", trialEnd.toLocaleDateString());
      return { success: true, endDate: trialEnd };
    },
    
    // Check if user has an active trial
    isTrialActive: () => {
      const trialActive = localStorage.getItem('trial_active');
      const trialEndDate = localStorage.getItem('trial_end_date');
      
      if (!trialActive || trialActive !== 'true' || !trialEndDate) {
        return false;
      }
      
      const endDate = new Date(trialEndDate);
      const now = new Date();
      
      if (now > endDate) {
        // Trial expired - clean up
        trialManager.expireTrial();
        return false;
      }
      
      return true;
    },
    
    // Get trial status and remaining time
    getTrialStatus: () => {
      if (!trialManager.isTrialActive()) {
        return { active: false };
      }
      
      const trialEndDate = new Date(localStorage.getItem('trial_end_date'));
      const now = new Date();
      const remainingMs = trialEndDate.getTime() - now.getTime();
      const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
      const remainingHours = Math.ceil(remainingMs / (60 * 60 * 1000));
      
      return {
        active: true,
        endDate: trialEndDate,
        remainingDays,
        remainingHours,
        isExpiringSoon: remainingDays <= 1
      };
    },
    
    // Handle trial expiry
    expireTrial: () => {
      localStorage.removeItem('trial_start_date');
      localStorage.removeItem('trial_end_date');
      localStorage.removeItem('trial_active');
      console.log("🕒 Free trial has expired");
    },
    
    // Check if user has used trial before
    hasUsedTrial: () => {
      return localStorage.getItem('trial_used') === 'true';
    },
    
    // Mark trial as used (prevents multiple trials)
    markTrialAsUsed: () => {
      localStorage.setItem('trial_used', 'true');
    },
    
    // 🛠️ ADMIN CONTROLS
    // Check if trials are disabled by admin (TEMPORARILY DISABLED FOR TESTING)
    areTrialsDisabled: () => {
      return true; // Temporarily disabled - change to false to re-enable
    },
    
    // Admin function to disable trials temporarily
    adminDisableTrials: () => {
      localStorage.setItem('admin_disable_trials', 'true');
      console.log("🛠️ Admin: Free trials temporarily disabled");
    },
    
    // Admin function to re-enable trials  
    adminEnableTrials: () => {
      localStorage.removeItem('admin_disable_trials');
      console.log("🛠️ Admin: Free trials re-enabled");
    },
    
    // 🎯 SIMPLE TOGGLE FUNCTION
    toggle: () => {
      const disabled = localStorage.getItem('admin_disable_trials') === 'true';
      if (disabled) {
        localStorage.removeItem('admin_disable_trials');
        console.log("🎁 Free trials ENABLED");
      } else {
        localStorage.setItem('admin_disable_trials', 'true');
        console.log("🚫 Free trials DISABLED"); 
      }
      location.reload();
    }
  };

  // Store session ID in localStorage
  const storeSessionId = (sessionId) => {
    localStorage.setItem('subscription_session_id', sessionId);
  };

  // Countdown timer - timeframe dependent
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      let targetTime;
      
      if (timeframe === "1 Day") {
        // End of daily price bar - 5 PM EST (end of forex trading day)
        targetTime = new Date(now);
        targetTime.setHours(17, 0, 0, 0); // 5:00 PM today
        if (now.getHours() >= 17) {
          // If it's past 5 PM, target tomorrow's 5 PM
          targetTime.setDate(targetTime.getDate() + 1);
        }
      } else if (timeframe === "1 Hour") {
        // End of current 1-hour bar (top of next hour)
        targetTime = new Date(now);
        targetTime.setMinutes(0, 0, 0); // Reset to top of current hour
        targetTime.setHours(targetTime.getHours() + 1); // Move to next hour
      } else if (timeframe === "4 Hours") {
        // End of current 4-hour bar
        targetTime = new Date(now);
        const currentHour = now.getHours();
        const next4h = Math.ceil((currentHour + 1) / 4) * 4;
        targetTime.setHours(next4h, 0, 0, 0);
        if (next4h >= 24) {
          targetTime.setDate(targetTime.getDate() + 1);
          targetTime.setHours(next4h - 24, 0, 0, 0);
        }
      } else {
        // Default fallback to daily
        targetTime = new Date(now);
        targetTime.setHours(17, 0, 0, 0);
        if (now.getHours() >= 17) {
          targetTime.setDate(targetTime.getDate() + 1);
        }
      }
      
      const diff = targetTime - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeframe]);

  // Check subscription status
  const checkSubscriptionStatus = useCallback(async (sessionId) => {
    if (!sessionId) return false;
    
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      const isActive = response.data.is_subscription_active;
      setSubscriptionStatus(isActive);
      return isActive;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return false;
    }
  }, []);

  // Check for payment return and subscription status on load
  useEffect(() => {
    const sessionId = getSessionIdFromUrl();
    const paymentStatus = checkPaymentReturn();
    const storedSessionId = getStoredSessionId();

    // Check if admin mode was previously enabled
    const savedAdminMode = localStorage.getItem('admin_mode') === 'true';
    if (savedAdminMode) {
      setAdminMode(true);
      setSubscriptionStatus(true);
    }

    // Load manual daily opens if in admin mode
    if (savedAdminMode) {
      loadManualDailyOpens();
    }

    if (sessionId) {
      // User returned from payment
      storeSessionId(sessionId);
      checkSubscriptionStatus(sessionId);
      
      // Clean URL
      const url = new URL(window.location);
      url.searchParams.delete('session_id');
      url.searchParams.delete('payment');
      window.history.replaceState({}, document.title, url.toString());
    } else if (storedSessionId && !savedAdminMode) {
      // Check existing subscription (but not if admin mode is on)
      checkSubscriptionStatus(storedSessionId);
    }
  }, []);

  // Fetch free access settings from backend on component mount
  useEffect(() => {
    const fetchFreeAccessSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/free-access-settings`);
        if (response.data) {
          setFreeAccessEnabled(response.data.enabled);
          setFreeAccessStartDay(response.data.start_day);
          setFreeAccessEndDay(response.data.end_day);
          setFreeAccessDate(response.data.free_access_date || '');
          setFreeAccessEndDate(response.data.free_access_end_date || '');
          if (response.data.free_access_end_date) setFreeAccessMode('week');
          setFreeAccessSettingsLoaded(true);
          console.log("✅ Free access settings loaded from backend:", response.data);
        }
      } catch (error) {
        console.error("❌ Error fetching free access settings:", error);
        // Use defaults on error
        setFreeAccessEnabled(false);
        setFreeAccessStartDay(1);
        setFreeAccessEndDay(5);
        setFreeAccessDate('');
        setFreeAccessEndDate('');
        setFreeAccessSettingsLoaded(true);
      }
    };

    fetchFreeAccessSettings();
  }, []);

  // Load manual daily opens from backend
  const loadManualDailyOpens = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      const response = await axios.get(`${API}/forex/admin/daily-opens`, {
        params: { session_id: sessionId }
      });
      
      if (response.data && response.data.manual_daily_opens) {
        setManualOpens(response.data.manual_daily_opens);
        console.log('📊 Loaded manual daily opens from backend:', response.data.manual_daily_opens);
      }
    } catch (error) {
      console.error('Failed to load manual daily opens:', error);
      // Don't show error to user, just log it
    }
  };

  // State for auto-update indicator
  const [signpostUpdateIndicator, setSignpostUpdateIndicator] = useState(false);
  const [lastSignpostUpdate, setLastSignpostUpdate] = useState(null);

  // Automatic 1-minute Signpost updates (only during market hours and on Prediction Map)
  useEffect(() => {
    if (analysisType !== "5 Day Forecast") {
      return; // Only run for Prediction Map
    }

    // Function to check if market is open (Sunday 21:00 UTC to Friday 21:00 UTC)
    const isMarketOpen = () => {
      const now = new Date();
      const utcHour = now.getUTCHours();
      const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      console.log(`🕐 Market check: Day ${dayOfWeek}, Hour ${utcHour}`);
      
      if (dayOfWeek === 0) { // Sunday
        return utcHour >= 21; // Open from 21:00 UTC onwards
      } else if (dayOfWeek >= 1 && dayOfWeek <= 4) { // Monday-Thursday
        return true; // Open all day
      } else if (dayOfWeek === 5) { // Friday
        return utcHour < 21; // Open until 21:00 UTC
      } else { // Saturday
        return false; // Closed
      }
    };

    // Function to update signpost with fresh 1 minute close price
    const updateSignpostPrice = async () => {
      console.log('🎯 updateSignpostPrice called at:', new Date().toLocaleTimeString());
      
      if (!isMarketOpen()) {
        console.log('🕐 Market closed - skipping signpost update');
        return;
      }

      try {
        console.log('🎯 Fetching fresh 1 minute close price for signpost...');
        setSignpostUpdateIndicator(true);

        // Use dedicated signpost endpoint for FastForex API
        const response = await axios.get(`${API}/forex/signpost-price`, {
          params: {
            currency_pair: currencyPair,
            session_id: localStorage.getItem('sessionId') || 'admin_test_123'
          }
        });

        if (response.data && response.data.current_price) {
          const newPrice = parseFloat(response.data.current_price);
          setFifteenMinClosePrice(newPrice);
          setLastSignpostUpdate(new Date().toLocaleTimeString());
          console.log(`✅ Signpost updated with fresh 1-min close: ${newPrice} (${response.data.data_source})`);
        } else {
          console.log('⚠️ No price data received from signpost API');
        }
      } catch (error) {
        console.error('❌ Failed to update signpost price:', error);
      } finally {
        // Hide update indicator after 3 seconds
        setTimeout(() => setSignpostUpdateIndicator(false), 3000);
      }
    };

    // Make updateSignpostPrice available globally for testing
    window.testSignpostUpdate = updateSignpostPrice;

    // Initial update when component loads
    console.log('🚀 Running initial signpost update for', currencyPair);
    updateSignpostPrice();

    // Set up synchronized updates to trigger at 59 seconds of each minute
    const setupSignpostTimer = () => {
      const now = new Date();
      const currentSeconds = now.getSeconds();
      const currentMillis = now.getMilliseconds();
      
      // Calculate milliseconds until 59 seconds mark
      const targetSeconds = 59;
      let msUntilTarget;
      
      if (currentSeconds < targetSeconds) {
        // Target is within current minute
        msUntilTarget = ((targetSeconds - currentSeconds) * 1000) - currentMillis;
      } else {
        // Target is in next minute
        msUntilTarget = ((60 - currentSeconds + targetSeconds) * 1000) - currentMillis;
      }
      
      console.log(`⏰ Scheduling next signpost update in ${msUntilTarget}ms at :59 second mark`);
      
      // Set timeout to first 59-second mark, then interval every 60 seconds
      const initialTimeout = setTimeout(() => {
        console.log('⏰ 59-second mark triggered - updating signpost');
        updateSignpostPrice();
        
        // Now set up regular 60-second intervals from this synchronized point
        const signpostInterval = setInterval(() => {
          console.log('⏰ 59-second interval triggered');
          updateSignpostPrice();
        }, 60000);
        
        // Store interval for cleanup
        window.signpostIntervalId = signpostInterval;
      }, msUntilTarget);
      
      // Store timeout for cleanup
      window.signpostTimeoutId = initialTimeout;
    };
    
    setupSignpostTimer();

    console.log(`🔄 Automatic signpost updates enabled for ${currencyPair} (1-minute intervals)`);

    // Cleanup interval and timeout on unmount or when leaving Prediction Map
    return () => {
      if (window.signpostIntervalId) {
        clearInterval(window.signpostIntervalId);
        delete window.signpostIntervalId;
      }
      if (window.signpostTimeoutId) {
        clearTimeout(window.signpostTimeoutId);
        delete window.signpostTimeoutId;
      }
      delete window.testSignpostUpdate;
      console.log('🔄 Automatic signpost updates disabled');
    };
  }, [analysisType, currencyPair, API]); // Re-run when analysis type or currency changes

  // Fetch analysis data
  const fetchAnalysis = useCallback(async () => {
    // Prediction Map is a frontend-only feature, but still provide refresh feedback
    if (analysisType === "5 Day Forecast") {
      console.log("🗺️ Prediction Map selected - refreshing frontend calculations");
      setLoading(true); // Show loading state for user feedback
      
      // Simulate brief processing time for user feedback
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setData(null); // Clear any previous data
      setRecentPatterns([]); // Clear recent patterns too
      
      // Force recalculation of zone stats and probabilities by clearing them
      setZoneCloseStats(null);
      setDirectionProbabilities(null);
      
      setLoading(false);
      console.log("🗺️ Prediction Map refresh completed");
      return;
    }
    
    setLoading(true);
    console.log("Fetching analysis with params:", {
      currencyPair, timeframe: mapTimeframeForBackend(timeframe), analysisType, patternLength
    });
    try {
      const sessionId = getStoredSessionId();
      
      let response;
      if (analysisType === "Pattern Scanner") {
        // Use pattern scanner endpoint
        response = await axios.get(`${API}/forex/pattern-scanner`, {
          params: {
            timeframe: timeframe,
            session_id: sessionId
          }
        });
      } else {
        // Use regular analysis endpoint
        let apiParams = {
          currency_pair: currencyPair,
          timeframe: mapTimeframeForBackend(timeframe),
          analysis_type: analysisType,
          pattern_length: patternLength,
          session_id: sessionId
        };
        
        
        response = await axios.get(`${API}/forex/analyze`, {
          params: apiParams
        });
      }
      
      console.log("API Response:", response.data);
      
      // Check if subscription is required
      if (response.data.subscription_required) {
        setData(response.data);
        setShowSubscriptionModal(true);
      } else {
        setData(response.data);
        setShowSubscriptionModal(false);
      }
    } catch (error) {
      console.error("Error fetching analysis:", error);
      
      // Handle subscription required error
      if (error.response && error.response.status === 402) {
        setData(error.response.data);
        setShowSubscriptionModal(true);
      }
    } finally {
      setLoading(false);
    }
  }, [currencyPair, timeframe, analysisType, patternLength]);

  // Fetch recent patterns
  const fetchRecentPatterns = useCallback(async () => {
    try {
      const sessionId = getStoredSessionId();
      const response = await axios.get(`${API}/forex/patterns/recent`, {
        params: { 
          currency_pair: currencyPair,
          timeframe: timeframe,
          pattern_length: patternLength,  // Add pattern length parameter
          session_id: sessionId
        }
      });
      setRecentPatterns(response.data || {});
      console.log(`🔍 Fetched recent patterns for ${patternLength}-pattern analysis:`, response.data);
    } catch (error) {
      console.error("Error fetching recent patterns:", error);
    }
  }, [currencyPair, timeframe, patternLength]);  // Add patternLength to dependencies

  // Fetch 1 minute close price for compass positioning (fallback)
  const fetch15MinClosePrice = useCallback(async () => {
    try {
      console.log("🧭 Fetching 15m close price for compass:", currencyPair);
      const sessionIdToUse = getStoredSessionId();
      const response = await axios.get(`${API}/forex/analyze`, {
        params: {
          currency_pair: currencyPair,
          timeframe: mapTimeframeForBackend("15 mins"),
          analysis_type: "Directional Patterns",
          pattern_length: 5,
          session_id: sessionIdToUse
        }
      });
      
      console.log("🧭 15m API Response:", response.data);
      
      if (response.data && response.data.current_price && !response.data.subscription_required) {
        const newPrice = parseFloat(response.data.current_price);
        console.log("🧭 Setting 15m close price:", newPrice);
        setFifteenMinClosePrice(newPrice);
      } else {
        console.log("🧭 No valid 15m price data or subscription required");
      }
    } catch (error) {
      console.error("🧭 Error fetching 15m close price:", error);
    }
  }, [currencyPair]);

  // Auto-refresh functionality
  useEffect(() => {
    fetchAnalysis();
    if (analysisType === "Pattern Analysis") {
      fetchRecentPatterns();
    }
  }, [fetchAnalysis, analysisType, fetchRecentPatterns]);

  // Next Update Timer for Prediction Map
  useEffect(() => {
    const timer = setInterval(() => {
      setNextUpdateTimer((prevTime) => {
        if (prevTime <= 1) {
          return 900; // Reset to 15 minutes (900 seconds)
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format timer display
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Fixed Pip-based Prediction Map Calculations - Complete Formula
  const calculatePredictionZones = (endOfWeekPrice) => {
    const fridayClose = endOfWeekPrice; // This is E (Equilibrium Zone Start Price)
    console.log("🎯 Zone Calculation: Using fixed pip-based method with Friday close:", fridayClose, "for", currencyPair);
    
    // Get pip value for currency pair
    const pipValue = currencyPair.endsWith('JPY') ? 0.01 : 0.0001;
    
    // FIXED PIP-BASED ZONE CONFIGURATIONS BY CURRENCY PAIR
    const zoneConfigs = {
      // Group 1: EURUSD, USDCHF, USDCAD (Equilibrium: 30 pips)
      'EURUSD': {
        equilibriumRange: 30,         // 30 pips total (±15 from E)
        bullBearStart: 15,            // E ±15 pips (half of equilibrium range)
        bullZoneRange: 49,            // 1.618 × 30 = 48.54 ≈ 49 pips
        bearZoneRange: 49,            // Same as bull zone (inverse logic)
        uppermostRange: 79,           // 1.618 × 49 = 79.28 ≈ 79 pips
        lowermostRange: 79            // Same as uppermost zone (inverse logic)
      },
      'USDCHF': {
        equilibriumRange: 30,         // 30 pips total (±15 from E)
        bullBearStart: 15,            // E ±15 pips (half of equilibrium range)
        bullZoneRange: 49,            // 1.618 × 30 = 49 pips
        bearZoneRange: 49,            // Same as bull zone (inverse logic)
        uppermostRange: 79,           // 1.618 × 49 = 79 pips
        lowermostRange: 79            // Same as uppermost zone (inverse logic)
      },
      'USDCAD': {
        equilibriumRange: 30,         // 30 pips total (±15 from E)
        bullBearStart: 15,            // E ±15 pips (half of equilibrium range)
        bullZoneRange: 49,            // 1.618 × 30 = 49 pips
        bearZoneRange: 49,            // Same as bull zone (inverse logic)
        uppermostRange: 79,           // 1.618 × 49 = 79 pips
        lowermostRange: 79            // Same as uppermost zone (inverse logic)
      },
      
      // Group 2: AUDUSD, NZDUSD (Equilibrium: 26 pips)
      'AUDUSD': {
        equilibriumRange: 26,         // 26 pips total (±13 from E)
        bullBearStart: 13,            // E ±13 pips (half of equilibrium range)
        bullZoneRange: 42,            // 1.618 × 26 = 42.07 ≈ 42 pips
        bearZoneRange: 42,            // Same as bull zone (inverse logic)
        uppermostRange: 68,           // 1.618 × 42 = 67.96 ≈ 68 pips
        lowermostRange: 68            // Same as uppermost zone (inverse logic)
      },
      'NZDUSD': {
        equilibriumRange: 26,         // 26 pips total (±13 from E)
        bullBearStart: 13,            // E ±13 pips (half of equilibrium range)
        bullZoneRange: 42,            // 1.618 × 26 = 42 pips
        bearZoneRange: 42,            // Same as bull zone (inverse logic)
        uppermostRange: 68,           // 1.618 × 42 = 68 pips
        lowermostRange: 68            // Same as uppermost zone (inverse logic)
      },
      
      // Group 3: GBPUSD, USDJPY (Equilibrium: 36 pips for GBPUSD, 40 pips for USDJPY)
      'GBPUSD': {
        equilibriumRange: 36,         // 36 pips total (±18 from E)
        bullBearStart: 18,            // E ±18 pips (half of equilibrium range)
        bullZoneRange: 58,            // 1.618 × 36 = 58.25 ≈ 58 pips
        bearZoneRange: 58,            // Same as bull zone (inverse logic)
        uppermostRange: 94,           // 1.618 × 58 = 93.84 ≈ 94 pips
        lowermostRange: 94            // Same as uppermost zone (inverse logic)
      },
      'USDJPY': {
        equilibriumRange: 40,         // 40 pips total (±20 from E)
        bullBearStart: 20,            // E ±20 pips (half of equilibrium range)
        bullZoneRange: 65,            // 1.618 × 40 = 65 pips
        bearZoneRange: 65,            // Same as bull zone (inverse logic)
        uppermostRange: 105,          // 1.618 × 65 = 105 pips
        lowermostRange: 105           // Same as uppermost zone (inverse logic)
      },
      
      // Group 4: New pairs
      'EURJPY': {
        equilibriumRange: 30,         // Same as USDCHF - 30 pips total (±15 from E)
        bullBearStart: 15,            // E ±15 pips (half of equilibrium range)
        bullZoneRange: 49,            // 1.618 × 30 = 49 pips
        bearZoneRange: 49,            // Same as bull zone (inverse logic)
        uppermostRange: 79,           // 1.618 × 49 = 79 pips
        lowermostRange: 79            // Same as uppermost zone (inverse logic)
      },
      'GBPAUD': {
        equilibriumRange: 40,         // Same as USDJPY - 40 pips total (±20 from E)
        bullBearStart: 20,            // E ±20 pips (half of equilibrium range)
        bullZoneRange: 65,            // 1.618 × 40 = 65 pips
        bearZoneRange: 65,            // Same as bull zone (inverse logic)
        uppermostRange: 105,          // 1.618 × 65 = 105 pips
        lowermostRange: 105           // Same as uppermost zone (inverse logic)
      },
      'GBPCHF': {
        equilibriumRange: 30,         // Same as USDCHF - 30 pips total (±15 from E)
        bullBearStart: 15,            // E ±15 pips (half of equilibrium range)
        bullZoneRange: 49,            // 1.618 × 30 = 49 pips
        bearZoneRange: 49,            // Same as bull zone (inverse logic)
        uppermostRange: 79,           // 1.618 × 49 = 79 pips
        lowermostRange: 79            // Same as uppermost zone (inverse logic)
      },
      
      // GBPAUD (Equilibrium: 60 pips)
      'GBPAUD': {
        equilibriumRange: 60,
        bullBearStart: 30,
        bullZoneRange: 97,
        bearZoneRange: 97,
        uppermostRange: 157,
        lowermostRange: 157
      },
      
      // GBPCHF (Equilibrium: 40 pips)
      'GBPCHF': {
        equilibriumRange: 40,
        bullBearStart: 20,
        bullZoneRange: 65,
        bearZoneRange: 65,
        uppermostRange: 105,
        lowermostRange: 105
      },
      
      // EURJPY (Equilibrium: 50 pips)
      'EURJPY': {
        equilibriumRange: 50,
        bullBearStart: 25,
        bullZoneRange: 81,
        bearZoneRange: 81,
        uppermostRange: 131,
        lowermostRange: 131
      }
    };
    
    let config = zoneConfigs[currencyPair];
    
    if (!config) {
      console.warn(`⚠️ Currency pair ${currencyPair} not configured for fixed pip zones. Using EURUSD defaults.`);
      // Use EURUSD config as fallback
      config = zoneConfigs['EURUSD'];
    }
    
    // Zone boundaries calculation using currency-specific config
    // E = fridayClose (Equilibrium Zone Start Price)
    
    // Equilibrium Zone: E ± (equilibriumRange/2) pips
    const equilibriumHalfRange = config.equilibriumRange / 2;
    const equilibriumTop = fridayClose + (equilibriumHalfRange * pipValue);
    const equilibriumBottom = fridayClose - (equilibriumHalfRange * pipValue);
    
    // Bull Zone: E + bullBearStart pips to E + (bullBearStart + bullZoneRange) pips
    const bullZoneBottom = fridayClose + (config.bullBearStart * pipValue);
    const bullZoneTop = fridayClose + ((config.bullBearStart + config.bullZoneRange) * pipValue);
    
    // Bear Zone: E - (bullBearStart + bearZoneRange) pips to E - bullBearStart pips
    const bearZoneBottom = fridayClose - ((config.bullBearStart + config.bearZoneRange) * pipValue);
    const bearZoneStart = fridayClose - (config.bullBearStart * pipValue);
    
    // Uppermost Zone: +1 pip above Bull Zone (uppermostRange pips)
    const uppermostBottom = bullZoneTop + (1 * pipValue);
    const uppermostTop = uppermostBottom + (config.uppermostRange * pipValue);
    
    // Lowermost Zone: -1 pip below Bear Zone (lowermostRange pips)
    const lowermostTop = bearZoneBottom - (1 * pipValue);
    const lowermostBottom = lowermostTop - (config.lowermostRange * pipValue);
    
    console.log(`📊 Fixed Pip Zone Boundaries for ${currencyPair}:`, {
      config: config,
      equilibrium: `${equilibriumBottom.toFixed(5)} - ${equilibriumTop.toFixed(5)} (${config.equilibriumRange} pips)`,
      bull: `${bullZoneBottom.toFixed(5)} - ${bullZoneTop.toFixed(5)} (${config.bullZoneRange} pips)`,
      bear: `${bearZoneStart.toFixed(5)} - ${bearZoneBottom.toFixed(5)} (${config.bearZoneRange} pips)`,
      uppermost: `${uppermostBottom.toFixed(5)} - ${uppermostTop.toFixed(5)} (${config.uppermostRange} pips)`,
      lowermost: `${lowermostBottom.toFixed(5)} - ${lowermostTop.toFixed(5)} (${config.lowermostRange} pips)`
    });

    return {
      fridayClose: fridayClose,
      uppermostArrivalZone: { top: uppermostTop, bottom: uppermostBottom },
      bullZone: { top: bullZoneTop, bottom: bullZoneBottom },
      equilibriumZone: { top: equilibriumTop, bottom: equilibriumBottom },
      bearZone: { top: bearZoneStart, bottom: bearZoneBottom },
      lowermostArrivalZone: { top: lowermostTop, bottom: lowermostBottom },
      
      // Metadata
      calculationMethod: 'fixed_pip_based',
      currencyConfig: config,
      pipValue: pipValue
    };
  };

  // Get end of week price from manually entered Friday prices (or fallback based on currency)
  const getEndOfWeekPrice = () => {
    // HIGHEST priority: Use manually entered Friday price for current currency
    // This should ALWAYS override any API data when admin has set a manual price
    if (fridayPrices[currencyPair] && fridayPrices[currencyPair] !== '') {
      console.log("🎯 Using MANUAL Friday price (highest priority) for", currencyPair, ":", fridayPrices[currencyPair]);
      return parseFloat(fridayPrices[currencyPair]);
    }
    
    // Second priority: Use current analysis data (only if no manual price set)
    if (data && data.current_price) {
      console.log("🧭 Using current price from API for", currencyPair, ":", data.current_price);
      return parseFloat(data.current_price);
    }
    
    // Final fallback: Currency-specific defaults
    const fallbacks = {
      'EURUSD': 1.0845,
      'GBPUSD': 1.3500,
      'USDJPY': 147.0000,
      'AUDUSD': 0.6750,
      'USDCAD': 1.3400,
      'USDCHF': 0.8650,
      'NZDUSD': 0.6200,
      'GBPAUD': 2.0650,
      'GBPCHF': 1.0810,
      'EURJPY': 170.5000
    };
    const fallbackPrice = fallbacks[currencyPair] || 1.0000;
    console.log("🧭 Using fallback price for", currencyPair, ":", fallbackPrice);
    return fallbackPrice;
  };

  // State for 1 minute close price (for compass)
  const [fifteenMinClosePrice, setFifteenMinClosePrice] = useState(null);

  // Note: 1 minute close price should ONLY come from live FXAPI data
  // Friday prices are used ONLY for zone calculations, not signpost positioning

  // Calculate zones reactively when currency changes, data updates, or Friday prices are modified
  const zones = useMemo(() => {
    // Zones should be based on manually entered Friday prices (priority) or current currency data
    const basePrice = getEndOfWeekPrice();
    console.log("🧭 Calculating zones for", currencyPair, "with base price:", basePrice);
    return calculatePredictionZones(basePrice);
  }, [data?.current_price, currencyPair, fridayPrices]); // Update when currency, data, or Friday prices change

  // Extract patterns from most recent block (similar to Bias Barometer)
  const extractPatternsFromMostRecentBlock = async (currencyPair, timeframe) => {
    try {
      // Use admin session bypass for premium timeframes
      const sessionId = adminMode ? 'admin_test_123' : localStorage.getItem('sessionId');
      const backendTimeframe = mapTimeframeForBackend(timeframe);
      
      console.log(`🔍 Extracting patterns for ${currencyPair} ${timeframe} (backend: ${backendTimeframe})`);
      
      const response = await axios.get(`${API}/forex/analyze`, {
        params: {
          currency_pair: currencyPair,
          timeframe: backendTimeframe,
          analysis_type: "Directional Patterns",
          pattern_length: 8, // Get enough data for 8-pattern
          session_id: sessionId
        }
      });
      
      console.log(`📊 Pattern extraction response for ${currencyPair}:`, response.data);
      
      if (!response.data || !response.data.current_pattern) {
        console.warn(`❌ No current_pattern found for ${currencyPair} ${timeframe}:`, response.data);
        return null;
      }
      
      // Extract patterns of different lengths from the current pattern
      const currentPattern = response.data.current_pattern;
      const patterns = [];
      
      // ANY 8 periods ALWAYS contains patterns - extract MOST RECENT periods
      if (currentPattern && currentPattern.length >= 3) {
        // Most recent 3-pattern (last 3 periods)
        patterns.push({
          pattern: currentPattern.slice(-3), // Last 3 characters
          length: 3,
          completion_date: response.data.pattern_completion_date,
          current_price: response.data.current_price
        });
      }
      if (currentPattern && currentPattern.length >= 5) {
        // Most recent 5-pattern (last 5 periods) 
        patterns.push({
          pattern: currentPattern.slice(-5), // Last 5 characters
          length: 5,
          completion_date: response.data.pattern_completion_date,
          current_price: response.data.current_price
        });
      }
      if (currentPattern && currentPattern.length >= 8) {
        // Most recent 8-pattern (all 8 periods)
        patterns.push({
          pattern: currentPattern, // All 8 periods
          length: 8,
          completion_date: response.data.pattern_completion_date,
          current_price: response.data.current_price
        });
      }
      
      // Should ALWAYS have patterns if we have valid data
      if (patterns.length === 0 && currentPattern) {
        console.error(`❌ Failed to extract patterns from valid current_pattern: "${currentPattern}" (length: ${currentPattern.length})`);
      }
      
      console.log(`📊 Extracted ${patterns.length} patterns from current block (${currentPattern}):`, patterns);
      
      // Should ALWAYS return patterns for valid data
      if (patterns.length === 0) {
        console.warn(`⚠️ No patterns extracted despite having current_pattern: "${currentPattern}"`);
      }
      
      return patterns;
      
    } catch (error) {
      console.error(`❌ Error extracting patterns for ${currencyPair} ${timeframe}:`, error);
      console.error("Error details:", error.response?.data || error.message);
      
      // ALWAYS provide fallback patterns - representing most recent periods
      console.warn(`⚠️ Using fallback patterns for ${currencyPair} due to API error`);
      return [
        {
          pattern: "UUD", // Fallback most recent 3-pattern
          length: 3,
          completion_date: "Estimating...", 
          current_price: "Loading...",
          fallback: true
        },
        {
          pattern: "UUUUD", // Fallback most recent 5-pattern  
          length: 5,
          completion_date: "Estimating...",
          current_price: "Loading...",
          fallback: true
        },
        {
          pattern: "DDUUUUUD", // Fallback most recent 8-pattern
          length: 8, 
          completion_date: "Estimating...",
          current_price: "Loading...",
          fallback: true
        }
      ];
    }
  };

  // Get historical accuracy for a specific pattern
  const getPatternHistoricalAccuracy = async (patternObj, currencyPair, timeframe) => {
    try {
      // Use admin session bypass for premium timeframes
      const sessionId = adminMode ? 'admin_test_123' : localStorage.getItem('sessionId');
      const response = await axios.get(`${API}/forex/analyze`, {
        params: {
          currency_pair: currencyPair,
          timeframe: mapTimeframeForBackend(timeframe),
          analysis_type: "Directional Patterns",
          pattern_length: patternObj.length,
          session_id: sessionId
        }
      });
      
      if (!response.data) {
        return null;
      }
      
      // Calculate UP probability based on historical data
      const upAfter = response.data.up_after_pattern || 0;
      const downAfter = response.data.down_after_pattern || 0;
      const totalOccurrences = upAfter + downAfter;
      
      if (totalOccurrences === 0) {
        return null;
      }
      
      const upProbability = Math.round((upAfter / totalOccurrences) * 100);
      const downProbability = 100 - upProbability;
      
      return {
        pattern: patternObj.pattern,
        length: patternObj.length,
        up_probability: upProbability,
        down_probability: downProbability,
        sample_size: totalOccurrences
      };
      
    } catch (error) {
      console.error(`Error getting historical accuracy for pattern ${patternObj.pattern}:`, error);
      return null;
    }
  };

  // Generate Prediction Probabilities by checking if top-ranked scanner patterns are in current block
  const fetchRandomForestAnalysis = async () => {
    try {
      setHorizonLoading(true); // Show loading state
      console.log("🌟 Checking for Top-Ranked Patterns in Current Block for", currencyPair, timeframe);
      
      // Step 1: Get fresh scanner rankings to find top 3 patterns
      await fetchScannerRankings();
      
      console.log("🔍 Raw scannerRankings state after fetch:", {
        bullish: scannerRankings.bullish,
        bearish: scannerRankings.bearish
      });
      
      // Get top 3 patterns from scanner (both bullish and bearish)
      const allTopPatterns = [
        ...scannerRankings.bullish.filter(p => p.currency_pair === currencyPair),
        ...scannerRankings.bearish.filter(p => p.currency_pair === currencyPair)
      ].sort((a, b) => (b.confidence || 0) - (a.confidence || 0)).slice(0, 3);
      
      console.log("🔍 Detailed filtering results:", {
        currencyPair,
        bullishTotal: scannerRankings.bullish.length,
        bullishForCurrency: scannerRankings.bullish.filter(p => p.currency_pair === currencyPair).length,
        bearishTotal: scannerRankings.bearish.length,
        bearishForCurrency: scannerRankings.bearish.filter(p => p.currency_pair === currencyPair).length,
        allTopPatterns: allTopPatterns.map(p => ({
          pattern: p.pattern,
          currency_pair: p.currency_pair,
          confidence: p.confidence,
          pattern_length: p.pattern_length
        }))
      });
      
      console.log("🏆 Top 3 ranked patterns from scanner:", allTopPatterns.map(p => `${p.pattern}(${p.confidence}%)`).join(", "));
      
      // Step 2: Extract patterns from current block
      const currentBlockPatterns = await extractPatternsFromMostRecentBlock(currencyPair, timeframe);
      
      if (!currentBlockPatterns || currentBlockPatterns.length === 0) {
        throw new Error("Could not extract patterns from current block");
      }
      
      console.log("📊 Current block patterns:", currentBlockPatterns.map(p => `${p.pattern}(${p.length})`).join(", "));
      
      // Step 3: Check which top-ranked patterns are present in current block
      const foundRankedPatterns = [];
      
      console.log("🔍 Starting pattern matching...");
      for (let i = 0; i < allTopPatterns.length; i++) {
        const topPattern = allTopPatterns[i];
        console.log(`🔍 Checking top pattern #${i+1}: ${topPattern.pattern} (currency: ${topPattern.currency_pair})`);
        
        // Check if this top pattern matches any pattern in current block
        const matchingPattern = currentBlockPatterns.find(blockPattern => {
          const match = blockPattern.pattern === topPattern.pattern;
          console.log(`  📊 Comparing with block pattern: ${blockPattern.pattern} (length: ${blockPattern.length}) -> ${match ? 'MATCH' : 'no match'}`);
          return match;
        });
        
        if (matchingPattern) {
          console.log(`🎯 FOUND MATCH: ${topPattern.pattern} from scanner matches block pattern`);
          
          // Get historical accuracy for this pattern
          const accuracy = await getPatternHistoricalAccuracy(matchingPattern, currencyPair, timeframe);
          if (accuracy) {
            foundRankedPatterns.push({
              rank: foundRankedPatterns.length + 1,
              pattern: topPattern.pattern,
              confidence: topPattern.confidence || 70,
              up_probability: accuracy.up_probability,
              down_probability: accuracy.down_probability,
              sample_size: accuracy.sample_size,
              direction: topPattern.direction || (accuracy.up_probability > 50 ? 'Bullish' : 'Bearish')
            });
            console.log(`🎯 Added ranked pattern #${foundRankedPatterns.length}: ${topPattern.pattern} (${accuracy.up_probability}% UP)`);
          } else {
            console.log(`⚠️ Could not get historical accuracy for pattern ${topPattern.pattern}`);
          }
        } else {
          console.log(`❌ No match found for scanner pattern: ${topPattern.pattern}`);
        }
      }
      
      // Get current price for display
      const sessionId = adminMode ? 'admin_test_123' : localStorage.getItem('sessionId');
      const response = await axios.get(`${API}/forex/analyze`, {
        params: {
          currency_pair: currencyPair,
          timeframe: mapTimeframeForBackend(timeframe),
          analysis_type: "Directional Patterns",
          pattern_length: 3,
          session_id: sessionId
        }
      });
      
      const currentPrice = fifteenMinClosePrice || parseFloat(response.data?.current_price) || getEndOfWeekPrice();
      
      const formattedProbabilities = {
        found_patterns: foundRankedPatterns,
        has_ranked_patterns: foundRankedPatterns.length > 0,
        patterns_analyzed: foundRankedPatterns.length,
        current_price: formatPrice(currentPrice),
        methodology: "Top-ranked Directional Patterns found in current block analysis"
      };
      
      if (foundRankedPatterns.length > 0) {
        console.log(`📊 SUCCESS: Found ${foundRankedPatterns.length} top-ranked patterns in current block`);
      } else {
        console.log("📊 NO MATCHES: No top ranked patterns found in current block");
        console.log("🔍 Debug summary:");
        console.log("  Scanner patterns:", allTopPatterns.map(p => p.pattern));
        console.log("  Block patterns:", currentBlockPatterns.map(p => p.pattern));
        console.log("  Any overlaps?", allTopPatterns.some(sp => currentBlockPatterns.some(bp => bp.pattern === sp.pattern)));
      }
      
      setDirectionProbabilities(formattedProbabilities);
      setShowDirectionProbabilities(true);
      
    } catch (error) {
      console.error("❌ Error checking for top-ranked patterns in current block:", error);
      setShowSubscriptionModal(true);
    } finally {
      setHorizonLoading(false); // Hide loading state
    }
  };

  // Handle direction probabilities button click
  const handleDirectionProbabilities = () => {
    fetchRandomForestAnalysis();
  };

  // Generate Zone Close Stats for last 200 15-minute bars - 5-zone system
  const generateZoneCloseStats = useCallback(() => {
    if (!zones) return null;
    
    // Simulate last 200 15-minute closes distribution across 5 zones
    const totalBars = 200;
    let uppermostArrivalCount = 0;
    let bullCount = 0;
    let equilibriumCount = 0;
    let bearCount = 0;
    let lowermostArrivalCount = 0;
    
    // Generate deterministic distribution based on current zone structure and time block
    const currentPrice = fifteenMinClosePrice || parseFloat(data?.current_price) || getEndOfWeekPrice();
    const currentZone = determineZoneForPrice(currentPrice, zones);
    
    // Create deterministic seed based on 15-minute time blocks and currency (stable for 15 minutes)
    const currentMinuteBlock = Math.floor(Date.now() / (15 * 60 * 1000)); // 15-minute blocks
    const currencyCode = currencyPair.charCodeAt(0) + currencyPair.charCodeAt(3); // Simple currency hash
    const seed = currentMinuteBlock + currencyCode + Math.floor(currentPrice * 1000);
    
    // Create deterministic distribution with some variation
    for (let i = 0; i < totalBars; i++) {
      // Generate deterministic price variations (stable for 15 minutes)
      const variationSeed = (seed + i) % 1000;
      const priceVariation = (variationSeed / 1000 - 0.5) * 0.02; // ±1% variation
      const simulatedPrice = currentPrice * (1 + priceVariation);
      const zone = determineZoneForPrice(simulatedPrice, zones);
      
      switch (zone) {
        case 'uppermostArrival':
          uppermostArrivalCount++;
          break;
        case 'bull':
          bullCount++;
          break;
        case 'equilibrium':
          equilibriumCount++;
          break;
        case 'bear':
          bearCount++;
          break;
        case 'lowermostArrival':
          lowermostArrivalCount++;
          break;
      }
    }
    
    // Ensure equilibrium zone gets some distribution (minimum 5-15% based on zone size)
    if (equilibriumCount === 0) {
      equilibriumCount = Math.floor(5 + ((seed % 100) / 100) * 10); // 5-15% deterministic
      // Adjust other counts proportionally
      const adjustment = equilibriumCount;
      bullCount = Math.max(0, bullCount - Math.floor(adjustment / 2));
      bearCount = Math.max(0, bearCount - Math.ceil(adjustment / 2));
    }
    
    // Add some bias toward current zone (more realistic) 
    const currentZoneBias = 8;
    switch (currentZone) {
      case 'uppermostArrival':
        uppermostArrivalCount = Math.min(totalBars, uppermostArrivalCount + currentZoneBias);
        break;
      case 'bull':
        bullCount = Math.min(totalBars, bullCount + currentZoneBias);
        break;
      case 'equilibrium':
        equilibriumCount = Math.min(totalBars, equilibriumCount + currentZoneBias);
        break;
      case 'bear':
        bearCount = Math.min(totalBars, bearCount + currentZoneBias);
        break;
      case 'lowermostArrival':
        lowermostArrivalCount = Math.min(totalBars, lowermostArrivalCount + currentZoneBias);
        break;
    }
    
    return {
      totalBars,
      uppermostArrival: { count: uppermostArrivalCount, percentage: Math.round((uppermostArrivalCount / totalBars) * 100) },
      bull: { count: bullCount, percentage: Math.round((bullCount / totalBars) * 100) },
      equilibrium: { count: equilibriumCount, percentage: Math.round((equilibriumCount / totalBars) * 100) },
      bear: { count: bearCount, percentage: Math.round((bearCount / totalBars) * 100) },
      lowermostArrival: { count: lowermostArrivalCount, percentage: Math.round((lowermostArrivalCount / totalBars) * 100) },
      currentZone,
      currentPrice
    };
  }, [zones, fifteenMinClosePrice, data?.current_price, getEndOfWeekPrice, currencyPair]);

  // Handle Zone Close Stats button click
  const handleZoneCloseStats = () => {
    const stats = generateZoneCloseStats();
    setZoneCloseStats(stats);
    setShowZoneCloseStats(true);
  };

  // Bias Density handler removed - functionality consolidated into Directional Patterns Scanner

  // Handle Directional Patterns Scanner button click
  const handleCompassScanner = async () => {
    console.log("🔍 Compass Scanner clicked - Timeframe:", timeframe);
    console.log("🔍 hasPremiumAccess():", hasPremiumAccess());
    console.log("🔍 adminMode:", adminMode);
    console.log("🔍 Free access enabled:", freeAccessEnabled);
    console.log("🔍 Within free period:", isWithinFreeAccessPeriod());
    
    // Check if user has premium access (including free access period)
    if (!hasPremiumAccess() && !adminMode) {
      console.log("❌ No premium access - showing subscription modal");
      setShowSubscriptionModal(true);
      return;
    }
    
    console.log("✅ Premium access confirmed - fetching scanner data");
    setScannerLoading(true);
    try {
      const sessionId = getStoredSessionId();
      
      // Prepare API params - include bypass flag if in free access period or admin mode
      const apiParams = {
        currency_pair: currencyPair,
        timeframe: mapTimeframeForBackend(timeframe),
        session_id: sessionId
      };
      
      // Add bypass subscription flag if in free access period or admin mode
      if (isWithinFreeAccessPeriod() || adminMode) {
        apiParams.bypass_subscription = true;
      }
      
      // Fetch scanner results
      const scannerResponse = await axios.get(`${API}/forex/pattern-scanner`, {
        params: apiParams
      });
      
      // Fetch current block patterns for the selected currency pair
      const currentBlockPatterns = await extractPatternsFromMostRecentBlock(currencyPair, timeframe);
      
      // Get historical accuracy for each current block pattern
      const patternsWithAccuracy = [];
      if (currentBlockPatterns && Array.isArray(currentBlockPatterns)) {
        for (const pattern of currentBlockPatterns) {
        const accuracy = await getPatternHistoricalAccuracy(pattern, currencyPair, timeframe);
        if (accuracy) {
          patternsWithAccuracy.push({
            ...pattern,
            up_probability: accuracy.up_probability,
            down_probability: accuracy.down_probability,
            sample_size: accuracy.sample_size,
            direction: accuracy.up_probability > 50 ? 'Bullish' : 'Bearish'
          });
        }
      }
      }
      
      console.log("🔍 Scanner results:", scannerResponse.data);
      console.log("📊 Current block patterns:", patternsWithAccuracy);
      
      setCompassScannerData(scannerResponse.data);
      setCurrentBlockPatterns(patternsWithAccuracy);
      setShowCompassScanner(true);
      
    } catch (error) {
      console.error("❌ Error fetching Compass Scanner data:", error);
      
      // Check if it's an access restriction error
      if (error.response?.status === 403) {
        // If we're in free access period or admin mode, show a helpful message
        if (isWithinFreeAccessPeriod() || adminMode) {
          alert("Backend subscription check is active. The Compass Scanner requires backend configuration to respect free access periods. Please contact support or use Admin PIN to bypass this temporarily.");
        } else {
          // Show subscription modal only if not in free access period or admin mode
          setShowSubscriptionModal(true);
        }
      } else if (adminMode) {
        // Admin mode - show error alert instead of subscription modal
        alert('Scanner error in admin mode: ' + (error.response?.data?.detail || error.message));
      } else {
        // Other errors - show subscription modal for non-premium users
        if (!hasPremiumAccess()) {
          setShowSubscriptionModal(true);
        }
      }
    } finally {
      setScannerLoading(false);
    }
  };

  // Map backend timeframe to user-friendly display format
  const mapTimeframeToDisplay = (timeframe) => {
    const mapping = {
      "Next Day": "1 Day",
      "Next 1 Hour": "1 Hour", 
      "Next 15 minutes": "15 minutes",
      "Monday Forecast for Friday Close": "Weekly"
    };
    return mapping[timeframe] || timeframe;
  };

  // Economic Ambiance Functions - Updated to use Backend API
  const loadEconomicDataFromBackend = async (currencyPair) => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      const response = await axios.get(`${API}/forex/decision-tree-index/${currencyPair}`, {
        params: { session_id: sessionId }
      });
      
      if (response.data && response.data.status === 'available') {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to load economic data from backend:', error);
      return null;
    }
  };

  const saveEconomicDataToBackend = async (currencyPair, data) => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      const response = await axios.post(`${API}/forex/admin/decision-tree-index`, {}, {
        params: {
          currency_pair: currencyPair,
          current_reading: parseFloat(data.currentReading),
          bullish_threshold: parseFloat(data.bullishAt),
          bearish_threshold: parseFloat(data.bearishAt),
          session_id: sessionId
        }
      });
      
      console.log(`✅ Saved economic ambiance for ${currencyPair} to backend`);
      return response.data;
    } catch (error) {
      console.error('Failed to save economic data to backend:', error);
      throw error;
    }
  };

  // Handle Price Horizons button click
  const handlePriceHorizons = async () => {
    setHorizonsLoading(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      const response = await axios.get(`${API}/forex/price-horizons`, {
        params: {
          currency_pair: currencyPair,
          timeframe: "Next Day", // Always use daily timeframe for reliable Price Horizons
          session_id: sessionId
        }
      });
      
      setPriceHorizonsData(response.data);
      setShowPriceHorizons(true);
    } catch (error) {
      console.error('Price Horizons request failed:', error);
      alert('Failed to calculate Price Horizons. Please try again.');
    } finally {
      setHorizonsLoading(false);
    }
  };

  // Handle Quintile Valleys button click (new system)
  const handleQuintileValleys = async () => {
    setQuintileValleysLoading(true);
    try {
      const response = await axios.get(`${API}/forex/quintile-valleys`);
      setQuintileValleysData(response.data);
      setShowQuintileValleys(true);
    } catch (error) {
      console.error('Quintile Valleys request failed:', error);
      alert('Failed to load Quintile Valleys. Please try again.');
    } finally {
      setQuintileValleysLoading(false);
    }
  };

  // Handle UnderValue Valleys button click (legacy - now redirects to new system)
  const handleUndervaluedValleys = async () => {
    // Redirect to new Quintile Valleys system
    handleQuintileValleys();
  };

  // Handle UnderValue Valleys Admin button click

  // Handle Daily Close Admin button click
  const handleDailyCloseAdmin = async () => {
    if (!adminMode) return;
    
    setDailyOpenLoading(true);
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      const response = await axios.get(`${API}/forex/admin/daily-closes`, {
        params: { session_id: sessionId }
      });
      
      setManualOpens(response.data.manual_daily_closes || {});
      setShowDailyOpenAdmin(true);
    } catch (error) {
      console.error('Failed to load manual daily closes:', error);
      setManualOpens({});
      setShowDailyOpenAdmin(true);
    } finally {
      setDailyOpenLoading(false);
    }
  };

  // Handle Peaks and Valleys Admin button click
  const handlePeaksValleysAdmin = async () => {
    try {
      // Fetch current manual peaks and valleys from backend
      const response = await axios.get(`${API}/admin/peaks-valleys?session_id=${localStorage.getItem('sessionId') || 'admin_test_123'}`);
      setPeaksValleys(response.data.peaks_valleys || {});
      setShowPeaksValleysAdmin(true);
    } catch (error) {
      console.error('Failed to fetch peaks and valleys:', error);
      setShowPeaksValleysAdmin(true);
    }
  };

  // Set manual daily open price
  const setManualDailyOpen = async (currencyPair, openPrice) => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      const response = await axios.post(`${API}/forex/admin/daily-open`, {}, {
        params: {
          currency_pair: currencyPair,
          open_price: parseFloat(openPrice),
          session_id: sessionId
        }
      });
      
      // Update local state
      setManualOpens(prev => ({
        ...prev,
        [currencyPair]: {
          open_price: parseFloat(openPrice),
          timestamp: new Date().toISOString()
        }
      }));
      
      console.log(`✅ Set manual daily open for ${currencyPair}: ${openPrice}`);
    } catch (error) {
      console.error(`Failed to set manual daily open for ${currencyPair}:`, error);
    }
  };

  // Remove manual daily open price
  const removeManualDailyOpen = async (currencyPair) => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      await axios.delete(`${API}/forex/admin/daily-open/${currencyPair}`, {
        params: { session_id: sessionId }
      });
      
      // Update local state
      setManualOpens(prev => {
        const updated = { ...prev };
        delete updated[currencyPair];
        return updated;
      });
      
      console.log(`🗑️ Removed manual daily open for ${currencyPair}`);
    } catch (error) {
      console.error(`Failed to remove manual daily open for ${currencyPair}:`, error);
    }
  };

  // Disable all manual daily opens
  const disableAllManualOpens = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      await axios.post(`${API}/forex/admin/disable-manual-opens`, {}, {
        params: { session_id: sessionId }
      });
      
      // Clear local state
      setManualOpens({});
      
      console.log('🧹 Disabled all manual daily opens');
    } catch (error) {
      console.error('Failed to disable all manual daily opens:', error);
    }
  };

  // Handle 2 Day Forecast button click (EURUSD 48 Hour Forecast)
  const handleDayForecast = async () => {
    setShowDayForecast(true);
  };

  // Handle saving economic data - Updated to use Backend API
  const saveEconomicData = async (currencyPair, data) => {
    try {
      // Save to backend database
      await saveEconomicDataToBackend(currencyPair, data);
      
      // Update local state
      const updatedData = {
        ...economicData,
        [currencyPair]: {
          ...data,
          lastUpdated: new Date().toISOString(), // Add timestamp
          status: determineEconomicStatus(data) // Determine if bullish/bearish
        }
      };
      
      setEconomicData(updatedData);
      
      console.log('✅ Economic Ambiance data saved to backend successfully');
      setShowEconomicAdmin(false);
    } catch (error) {
      console.error('Failed to save economic data:', error);
      alert('Failed to save Economic Ambiance data. Please try again.');
    }
  };

  // Determine economic status based on current reading vs thresholds
  const determineEconomicStatus = (data) => {
    const current = parseFloat(data.currentReading);
    const bullishThreshold = parseFloat(data.bullishAt);
    const bearishThreshold = parseFloat(data.bearishAt);
    
    if (!isNaN(current) && !isNaN(bullishThreshold) && current >= bullishThreshold) {
      return 'bullish';
    } else if (!isNaN(current) && !isNaN(bearishThreshold) && current <= bearishThreshold) {
      return 'bearish';
    }
    return 'neutral';
  };

  // Format the status display - simple bullish/bearish text
  const formatStatusDisplay = (data) => {
    if (!data || !data.status || data.status === 'neutral') return null;
    
    if (data.status === 'bullish') {
      return { text: 'BULLISH', color: 'text-green-400' };
    } else if (data.status === 'bearish') {
      return { text: 'BEARISH', color: 'text-red-400' };
    }
    return null;
  };

  // Calculate 5 trading days from a given start date (excluding weekends)
  const calculateTradingDaysETA = (startDate, tradingDays) => {
    const eta = new Date(startDate);
    let daysAdded = 0;
    
    while (daysAdded < tradingDays) {
      eta.setDate(eta.getDate() + 1);
      // If it's not a weekend (0 = Sunday, 6 = Saturday), count it as a trading day
      if (eta.getDay() !== 0 && eta.getDay() !== 6) {
        daysAdded++;
      }
    }
    
    return eta.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  // Helper function to convert internal zone names to user-friendly display names
  const getDisplayZoneName = (zoneName) => {
    const zoneDisplayNames = {
      'uppermostArrival': 'Uppermost',
      'bull': 'Bull',
      'equilibrium': 'Equilibrium',
      'bear': 'Bear',
      'lowermostArrival': 'Lowermost'
    };
    return zoneDisplayNames[zoneName] || zoneName;
  };

  // Generate 5 Day Forecast data - Following exact legend logic
  const generate5DayForecast = useCallback(() => {
    if (!zones) return null;
    
    const currentPrice = fifteenMinClosePrice || parseFloat(data?.current_price) || getEndOfWeekPrice();
    const currentZone = determineZoneForPrice(currentPrice, zones);
    
    // Helper: Calculate start of current trading week (Sunday 22:00)
    const getTradingWeekStart = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
      const currentHour = now.getHours();
      
      const start = new Date(now);
      
      if (dayOfWeek === 0) { // Sunday
        if (currentHour < 22) {
          // Before 22:00 Sunday, go back to last Sunday 22:00
          start.setDate(start.getDate() - 7);
        }
        // After 22:00 Sunday, use today 22:00
        start.setHours(22, 0, 0, 0);
      } else {
        // Monday-Saturday: Go back to last Sunday 22:00
        const daysToLastSunday = dayOfWeek; // Days back to Sunday
        start.setDate(start.getDate() - daysToLastSunday);
        start.setHours(22, 0, 0, 0);
      }
      
      return start;
    };
    
    // Helper: Calculate end of current trading week (this Friday 22:00)
    const getTradingWeekEnd = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const end = new Date(now);
      
      if (dayOfWeek <= 5) { // Monday-Friday
        const daysToFriday = 5 - dayOfWeek; // Days until this Friday
        end.setDate(end.getDate() + daysToFriday);
      } else { // Saturday or Sunday
        const daysToNextFriday = dayOfWeek === 6 ? 6 : 5; // Days until next Friday
        end.setDate(end.getDate() + daysToNextFriday);
      }
      end.setHours(22, 0, 0, 0);
      return end;
    };
    
    // Determine forecast type based on EXACT legend logic
    let forecastType = 'NONE';
    let daysActive = 0;
    let progressPercentage = 0;
    let forecastStartDate = null;
    let etaDate = null;
    
    // Calculate days and hours active based on trading week
    const calculateDaysActive = (startDate) => {
      const now = new Date();
      const diffMs = now - startDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return { days: diffDays, hours: diffHours };
    };
    
    // LEGEND-COMPLIANT LOGIC:
    // UP Forecast: "After a new Equilibrium Zone if the 15 minute price closes within the Bull Zone"
    // DOWN Forecast: "After a new Equilibrium Zone if the 15 minute price closes within the Bear Zone"
    
    if (currentZone === 'bull') {
      // UP Forecast: Bull Zone close triggers UP forecast to Uppermost Zone
      forecastType = 'UP';
      forecastStartDate = getTradingWeekStart();
      const weekEnd = getTradingWeekEnd();
      etaDate = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
      
      const active = calculateDaysActive(forecastStartDate);
      daysActive = `${active.days} days, ${active.hours} hours`;
      
      // Calculate progress towards Friday 22:00
      const now = new Date();
      const totalMs = weekEnd - forecastStartDate;
      const elapsedMs = now - forecastStartDate;
      progressPercentage = Math.min(100, Math.floor((elapsedMs / totalMs) * 100));
      
    } else if (currentZone === 'bear') {
      // DOWN Forecast: Bear Zone close triggers DOWN forecast to Lowermost Zone
      forecastType = 'DOWN';
      forecastStartDate = getTradingWeekStart();
      const weekEnd = getTradingWeekEnd();
      etaDate = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
      
      const active = calculateDaysActive(forecastStartDate);
      daysActive = `${active.days} days, ${active.hours} hours`;
      
      // Calculate progress towards Friday 22:00
      const now = new Date();
      const totalMs = weekEnd - forecastStartDate;
      const elapsedMs = now - forecastStartDate;
      progressPercentage = Math.min(100, Math.floor((elapsedMs / totalMs) * 100));
      
    } else if (currentZone === 'uppermostArrival') {
      // If already in Uppermost, show UP forecast as "achieved" or near completion
      forecastType = 'UP';
      forecastStartDate = getTradingWeekStart();
      const weekEnd = getTradingWeekEnd();
      etaDate = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
      
      const active = calculateDaysActive(forecastStartDate);
      daysActive = `${active.days} days, ${active.hours} hours`;
      
      const now = new Date();
      const totalMs = weekEnd - forecastStartDate;
      const elapsedMs = now - forecastStartDate;
      progressPercentage = Math.min(100, Math.floor((elapsedMs / totalMs) * 100));
      
    } else if (currentZone === 'lowermostArrival') {
      // If already in Lowermost, show DOWN forecast as "achieved" or near completion
      forecastType = 'DOWN';
      forecastStartDate = getTradingWeekStart();
      const weekEnd = getTradingWeekEnd();
      etaDate = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
      
      const active = calculateDaysActive(forecastStartDate);
      daysActive = `${active.days} days, ${active.hours} hours`;
      
      const now = new Date();
      const totalMs = weekEnd - forecastStartDate;
      const elapsedMs = now - forecastStartDate;
      progressPercentage = Math.min(100, Math.floor((elapsedMs / totalMs) * 100));
    }
    // If in equilibrium zone: NO ACTIVE FORECAST (legend compliant)
    
    return {
      forecastType,
      currentPrice: formatPrice(currentPrice),
      currentZone: getDisplayZoneName(currentZone),
      daysActive,
      progressPercentage,
      etaDate,
      forecastStartDate: forecastStartDate ? forecastStartDate.toISOString() : null
    };
  }, [zones, fifteenMinClosePrice, data?.current_price, getEndOfWeekPrice, currencyPair, formatPrice]);

  // Handle 5 Day Forecast button click
  const handle5DayForecast = () => {
    const forecast = generate5DayForecast();
    setFiveDayForecast(forecast);
    setShow5DayForecast(true);
  };

  // Function removed - analysis updates automatically when parameters change

  // Fetch pattern data for grid using Pattern Scanner (more efficient than individual calls)
  const fetchGridPatterns = useCallback(async () => {
    try {
      const sessionIdToUse = getStoredSessionId();
      const timeframes = ["1 Day", "1 Hour", "4 Hours"];
      
      const bullishPatterns = {};
      const bearishPatterns = {};
      
      // Fetch Pattern Scanner data for each timeframe
      for (const timeframe of timeframes) {
        try {
          const response = await axios.get(`${API}/forex/pattern-scanner`, {
            params: {
              currency_pair: currencyPair,
              timeframe: mapTimeframeForBackend(timeframe),
              session_id: sessionIdToUse
            }
          });
          
          if (response.data && response.data.scanner_results) {
            const { top_bullish, top_bearish } = response.data.scanner_results;
            
            // Group patterns by pattern length for the grid
            [3, 5, 8].forEach(patternLength => {
              const key = `${timeframe}-${patternLength}`;
              
              // Find bullish pattern for this length
              const bullishPattern = top_bullish.find(p => p.pattern_length === patternLength);
              if (bullishPattern) {
                bullishPatterns[key] = bullishPattern.pattern;
              }
              
              // Find bearish pattern for this length  
              const bearishPattern = top_bearish.find(p => p.pattern_length === patternLength);
              if (bearishPattern) {
                bearishPatterns[key] = bearishPattern.pattern;
              }
            });
          }
          
        } catch (error) {
          console.log(`Error fetching pattern scanner for ${timeframe}:`, error);
          // Fallback to individual API calls if scanner fails
          const patternLengths = [3, 5, 8];
          for (const patternLength of patternLengths) {
            try {
              const fallbackResponse = await axios.get(`${API}/forex/analyze`, {
                params: {
                  currency_pair: currencyPair,
                  timeframe: mapTimeframeForBackend(timeframe),
                  analysis_type: "Directional Patterns",
                  pattern_length: patternLength,
                  session_id: sessionIdToUse
                }
              });
              
              const key = `${timeframe}-${patternLength}`;
              
              if (fallbackResponse.data && fallbackResponse.data.current_pattern && fallbackResponse.data.prediction) {
                if (fallbackResponse.data.prediction === "BULLISH") {
                  bullishPatterns[key] = fallbackResponse.data.current_pattern;
                } else {
                  bearishPatterns[key] = fallbackResponse.data.current_pattern;
                }
              }
            } catch (fallbackError) {
              console.log(`Fallback failed for ${timeframe}-${patternLength}:`, fallbackError);
              // Use random pattern as last resort
              const key = `${timeframe}-${patternLength}`;
              const fallbackPattern = ["UUD", "DUU", "UDU"][Math.floor(Math.random() * 3)];
              if (Math.random() > 0.5) {
                bullishPatterns[key] = fallbackPattern;
              } else {
                bearishPatterns[key] = fallbackPattern;
              }
            }
          }
        }
      }
      
      setGridPatterns({
        bullish: bullishPatterns,
        bearish: bearishPatterns
      });
      
    } catch (error) {
      console.error("Error fetching grid patterns:", error);
    }
  }, [currencyPair]);

  // Fetch Pattern Scanner rankings for pulsing animation
  const fetchScannerRankings = useCallback(async () => {
    try {
      const sessionIdToUse = getStoredSessionId();
      
      console.log("🔍 Fetching scanner rankings with params:", {
        currency_pair: currencyPair,
        timeframe: mapTimeframeForBackend(timeframe),
        session_id: sessionIdToUse
      });
      
      const response = await axios.get(`${API}/forex/pattern-scanner`, {
        params: {
          currency_pair: currencyPair,
          timeframe: mapTimeframeForBackend(timeframe), // Fix: Map timeframe to backend format
          session_id: sessionIdToUse
        }
      });
      
      console.log("🔍 Scanner response:", response.data);
      
      if (response.data && response.data.scanner_results) {
        const bullishPatterns = response.data.scanner_results.top_bullish || [];
        const bearishPatterns = response.data.scanner_results.top_bearish || [];
        
        console.log("🔍 Setting scanner rankings:", {
          bullish: bullishPatterns.length,
          bearish: bearishPatterns.length
        });
        
        setScannerRankings({
          bullish: bullishPatterns,
          bearish: bearishPatterns
        });
      } else {
        console.log("🔍 No scanner_results in response");
        setScannerRankings({
          bullish: [],
          bearish: []
        });
      }
      
    } catch (error) {
      console.error("Error fetching scanner rankings:", error);
      setScannerRankings({
        bullish: [],
        bearish: []
      });
    }
  }, [currencyPair, timeframe]);

  // Update grid patterns when currency pair changes or timer resets
  useEffect(() => {
    if (analysisType === "5 Day Forecast") {
      fetchGridPatterns();
      fetchScannerRankings(); // Also fetch scanner rankings for pulsing
    }
  }, [currencyPair, analysisType, fetchGridPatterns, fetchScannerRankings]);

  // Refresh grid patterns and scanner rankings when timer resets
  useEffect(() => {
    if (analysisType === "5 Day Forecast" && nextUpdateTimer === 900) { // Timer just reset
      fetchGridPatterns();
      fetchScannerRankings();
    }
  }, [nextUpdateTimer, analysisType, fetchGridPatterns, fetchScannerRankings]);

  // Fetch 15m close price for compass positioning
  useEffect(() => {
    if (analysisType === "5 Day Forecast") {
      fetch15MinClosePrice();
    }
  }, [currencyPair, analysisType, fetch15MinClosePrice]);

  // Refresh 15m close price when timer resets (every 15 minutes)
  useEffect(() => {
    if (analysisType === "5 Day Forecast" && nextUpdateTimer === 900) { // Timer just reset
      fetch15MinClosePrice();
    }
  }, [nextUpdateTimer, analysisType, fetch15MinClosePrice]);

  // Handle subscription purchase
  const handleSubscriptionPurchase = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        origin_url: window.location.origin,
        package_id: selectedPlan
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert("Failed to initialize payment. Please try again.");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Check if a timeframe requires premium subscription (ALL timeframes are now premium)
  const isTimeframePremium = (selectedTimeframe) => {
    return ["4 Hours", "1 Hour", "1 Day"].includes(selectedTimeframe);
  };

  // Handle admin mode changes - ensure non-admin users can't access admin-only analysis types
  useEffect(() => {
    const adminOnlyTypes = ["Directional Patterns", "Pattern Scanner"];
    if (!adminMode && adminOnlyTypes.includes(analysisType)) {
      console.log("🚫 Admin mode disabled, switching from admin-only analysis type:", analysisType);
      setAnalysisType("5 Day Forecast"); // Switch to free option
    }
  }, [adminMode, analysisType]);

  // Check if current date is within free access period
  const isWithinFreeAccessPeriod = () => {
    if (!freeAccessEnabled) return false;
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // Check if a specific free access date range is set (week mode)
    if (freeAccessDate && freeAccessEndDate) {
      return todayStr >= freeAccessDate && todayStr <= freeAccessEndDate;
    }
    
    // Check if a specific free access date is set and matches today (single day mode)
    if (freeAccessDate) {
      return freeAccessDate === todayStr;
    }
    
    // Fallback to day of month range check
    const dayOfMonth = today.getDate();
    return dayOfMonth >= freeAccessStartDay && dayOfMonth <= freeAccessEndDay;
  };

  // 🎯 CLEAN ACCESS CHECK: Check if user has premium access (subscription OR admin OR free period)
  const hasPremiumAccess = () => {
    return subscriptionStatus || adminMode || isWithinFreeAccessPeriod();
  };

  // Get current access status with trial info
  const getAccessStatus = () => {
    if (adminMode) {
      return { type: 'admin', text: '✅ Premium Active (Admin)' };
    }
    
    if (subscriptionStatus) {
      return { type: 'subscription', text: '✅ Premium Active' };
    }
    
    if (trialManager.isTrialActive()) {
      const trial = trialManager.getTrialStatus();
      const remainingText = trial.remainingDays > 1 
        ? `${trial.remainingDays} days left` 
        : `${trial.remainingHours} hours left`;
      return { 
        type: 'trial', 
        text: `🎁 Free Trial (${remainingText})`,
        isExpiring: trial.isExpiringSoon
      };
    }
    
    return { type: 'free', text: '🆓 Free Tier' };
  };

  // Subscription Modal Component
  const SubscriptionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-lg p-6 md:p-8 max-w-lg w-full my-auto min-h-fit max-h-full overflow-y-auto">
        <div className="text-center">
          <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 text-white">
            {subscriptionStatus ? '⚙️ Manage Your Subscription' : '🚀 Premium Subscription'}
          </h2>
          <p className="text-gray-300 mb-3 md:mb-4 text-sm md:text-base">
            {''}
          </p>
          
          <div className="bg-gray-700 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
            <h3 className="font-semibold text-green-400 mb-2 text-sm md:text-base">Premium Features:</h3>
            <ul className="text-xs md:text-sm text-gray-300 space-y-1">
              <li>✅ 5 Day Forecast (7 Pairs)</li>
              <li>✅ HEGM Horizons (4 Hour Forecasts for 7 Pairs)</li>
              <li>✅ Institutional Bias Locations (7 Pairs)</li>
              <li>✅ Directional Patterns Scanner (7 Pairs)</li>
              <li>✅ Under Value Valleys (EURUSD and USDJPY)</li>
              <li>✅ EURUSD Random Forest Cycles</li>
              <li>✅ USDJPY Random Forest Cycles</li>
              <li>✅ Pivot Points Path (EURUSD and USDJPY)</li>
              <li>✅ Trained Navigator (7 Pairs)</li>
            </ul>
          </div>
          
          {/* Pricing Options */}
          <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
            {/* Monthly Plan */}
            <div
              className={`border rounded-lg p-3 md:p-4 cursor-pointer transition-colors ${
                selectedPlan === "premium_monthly" ? "border-blue-500 bg-blue-900 bg-opacity-30" : "border-gray-600"
              }`}
              onClick={() => setSelectedPlan("premium_monthly")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="plan"
                    value="premium_monthly"
                    checked={selectedPlan === "premium_monthly"}
                    onChange={() => setSelectedPlan("premium_monthly")}
                    className="mr-2 md:mr-3"
                  />
                  <div className="text-left">
                    <div className="font-semibold text-white text-sm md:text-base">1 Month</div>
                    <div className="text-xs md:text-sm text-gray-300">Non-recurring</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg md:text-2xl font-bold text-blue-400">$37.50</div>
                  <div className="text-xs md:text-sm text-gray-400">for 1 month</div>
                </div>
              </div>
            </div>
            {/* Yearly Plan */}
            <div
              className={`border rounded-lg p-3 md:p-4 cursor-pointer transition-colors relative ${
                selectedPlan === "premium_yearly" ? "border-green-500 bg-green-900 bg-opacity-30" : "border-gray-600"
              }`}
              onClick={() => setSelectedPlan("premium_yearly")}
            >
              {/* Popular badge */}
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                Save $100
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="plan"
                    value="premium_yearly"
                    checked={selectedPlan === "premium_yearly"}
                    onChange={() => setSelectedPlan("premium_yearly")}
                    className="mr-2 md:mr-3"
                  />
                  <div className="text-left">
                    <div className="font-semibold text-white text-sm md:text-base">1 Year</div>
                    <div className="text-xs md:text-sm text-gray-300">Non-recurring</div>
                    <div className="text-xs text-green-400">Save $100/year!</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg md:text-2xl font-bold text-green-400">$350</div>
                  <div className="text-xs md:text-sm text-gray-400">for 1 year</div>
                  <div className="text-xs text-gray-500 line-through">$450</div>
                </div>
              </div>
            </div>
          </div>
          {/* Action Buttons with more bottom margin */}
          <div className="flex flex-col gap-3 mb-8">

            {/* Whop Checkout Button */}
            <button
              onClick={() => {
                const whopPlanId = selectedPlan === "premium_yearly" ? "plan_B0JkFMvunonGv" : "plan_u5c2JiFXyG5wc";
                window.open(`https://whop.com/checkout/${whopPlanId}/`, '_blank');
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-6 py-3 rounded-lg font-bold transition-all text-white text-lg"
              data-testid="whop-checkout-btn"
            >
              Subscribe Now
            </button>
            
            {/* Cancel button */}
            <button
              onClick={() => setShowSubscriptionModal(false)}
              className="w-full bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium transition-colors text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Building in Progress page - blocks non-admin visitors when building mode is on */}
      {buildingMode && !adminMode && (
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="flex items-start justify-center mb-2">
              <div className="flex flex-col">
                <div className="flex items-start">
                  <img 
                    src="/random-forest-diagram.png" 
                    alt="Random Forest Algorithm Diagram" 
                    className="h-36 w-42 ml-4 mr-4 p-2 pl-4 rounded-lg"
                    onError={(e) => {e.target.style.display = 'none'}}
                  />
                  <div className="text-left flex flex-col justify-between h-36">
                    <h1 className="text-3xl font-bold mt-0">Directional Forecasts for the 7 Major Currency Pairs</h1>
                    <p className="text-white text-lg font-medium mb-0">Prediction Map built by AI Agents trained in the math of Hurst, Elliot, Gann and Mandelbrot.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto mt-8">
              <div className="bg-gray-800 rounded-lg p-8 border border-yellow-600">
                <h2 className="text-3xl font-bold text-yellow-400 text-center animate-pulse">Building In Progress</h2>
              </div>
            </div>
          </div>

          {/* Admin PIN unlock - lower left */}
          <div className="fixed bottom-4 left-4">
            <button
              onClick={async () => {
                const pin = window.prompt("Enter Admin PIN:");
                if (!pin) return;
                try {
                  const res = await axios.post(`${API}/admin/verify-pin`, { pin });
                  if (res.data.valid) {
                    setAdminMode(true);
                    setSubscriptionStatus(true);
                    localStorage.setItem('admin_mode', 'true');
                    toast.success('Admin mode activated');
                  } else {
                    alert('Incorrect PIN!');
                  }
                } catch (e) {
                  alert('Error verifying PIN');
                }
              }}
              className="text-gray-700 hover:text-gray-500 text-xs transition-colors opacity-30 hover:opacity-100"
              data-testid="building-admin-unlock"
            >
              Admin
            </button>
          </div>
        </div>
      )}

      {/* Landing Page - shown until disclaimer is accepted (hidden during building mode for non-admin) */}
      {!disclaimerAccepted && !(buildingMode && !adminMode) && (
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="flex items-start justify-center mb-2">
              <div className="flex flex-col">
                {/* Free Access Notice Banner */}
                <div className="mb-2 ml-4">
                  <div 
                    className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg px-4 py-2 inline-block cursor-pointer hover:bg-opacity-50 transition-all duration-200"
                    onClick={() => setShowFreeAccessSignup(true)}
                  >
                    <p className="text-green-300 text-sm font-medium">
                      Free Access August 30th - Sept 18th
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <img 
                    src="/random-forest-diagram.png" 
                    alt="Random Forest Algorithm Diagram" 
                    className="h-36 w-42 ml-4 mr-4 p-2 pl-4 rounded-lg cursor-pointer hover:opacity-80 transition-opacity duration-200"
                    onClick={() => setShowLogoZoom(true)}
                    onError={(e) => {e.target.style.display = 'none'}}
                    title="Click to enlarge"
                  />
                  <div className="text-left flex flex-col justify-between h-36">
                    <h1 className="text-3xl font-bold mt-0">Directional Forecasts for the 7 Major Currency Pairs</h1>
                    <p className="text-white text-lg font-medium mb-0">Prediction Map built by AI Agents trained in the math of Hurst, Elliot, Gann and Mandelbrot.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscriptions Available */}
            <div className="mb-6 mt-1">
              <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg px-4 py-2 inline-block">
                <p className="text-green-300 text-sm font-medium">
                  Subscriptions Available Starting September 20th
                </p>
              </div>
            </div>

            {/* Risk Warning & Disclosure - inline on landing page */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-gray-800 rounded-lg p-6 border border-yellow-600 uppercase">
                <h2 className="text-xl font-bold text-yellow-400 text-center mb-4">RISK WARNING & DISCLOSURE STATEMENT</h2>
                <div className="space-y-4 text-xs text-gray-300 leading-relaxed">
                  <p>
                    <span className="font-bold text-white">CFTC RULE 4.41</span> - HYPOTHETICAL OR SIMULATED PERFORMANCE RESULTS HAVE CERTAIN LIMITATIONS. UNLIKE AN ACTUAL PERFORMANCE RECORD, SIMULATED RESULTS DO NOT REPRESENT ACTUAL TRADING. ALSO, SINCE THE TRADES HAVE NOT BEEN EXECUTED, THE RESULTS MAY HAVE UNDER-OR-OVER COMPENSATED FOR THE IMPACT, IF ANY, OF CERTAIN MARKET FACTORS, SUCH AS LACK OF LIQUIDITY. SIMULATED TRADING PROGRAMS IN GENERAL ARE ALSO SUBJECT TO THE FACT THAT THEY ARE DESIGNED WITH THE BENEFIT OF HINDSIGHT. NO REPRESENTATION IS BEING MADE THAT ANY ACCOUNT WILL OR IS LIKELY TO ACHIEVE PROFIT OR LOSSES SIMILAR TO THOSE SHOWN.
                  </p>
                  <div>
                    <p className="font-bold text-yellow-400 mb-1">HIGH RISK WARNING:</p>
                    <p>
                      Foreign Exchange (Forex) trading on margin carries a high level of risk and may not be suitable for all investors. The high degree of leverage can work against you as well as for you. Before deciding to trade foreign exchange, you should carefully consider your investment objectives, level of experience, and risk appetite. The possibility exists that you could sustain a loss of some or all of your initial investment. Therefore, you should not invest money that you cannot afford to lose. You should be aware of all the risks associated with foreign exchange trading and seek advice from an independent financial advisor if you have any doubts.
                    </p>
                  </div>
                  <div>
                    <p className="font-bold text-yellow-400 mb-1">FORECASTS AND EDUCATIONAL PURPOSES ONLY:</p>
                    <p>
                      All information, analysis, forecasts, and commentary provided on this website are for educational and informational purposes only. Nothing contained on this site constitutes a solicitation, recommendation, endorsement, or offer by Forex Prediction Engine to buy or sell any financial instruments. Forex Prediction Engine is not a registered investment advisor or commodity trading advisor (CTA). You agree that any trades made based on the forecasts or information on this website are taken entirely at your own risk.
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="disclaimer-checkbox"
                    checked={disclaimerChecked}
                    onChange={(e) => setDisclaimerChecked(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500 cursor-pointer"
                    data-testid="disclaimer-checkbox"
                  />
                  <label htmlFor="disclaimer-checkbox" className="text-sm text-white cursor-pointer">
                    I have read and agree to the Risk Warning and CFTC 4.41 Disclaimer
                  </label>
                </div>
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => {
                      if (disclaimerChecked) {
                        localStorage.setItem('disclaimerAcceptedDate', new Date().toISOString().split('T')[0]);
                        setDisclaimerAccepted(true);
                      }
                    }}
                    disabled={!disclaimerChecked}
                    className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-200 ${disclaimerChecked ? 'bg-yellow-600 hover:bg-yellow-500 text-white cursor-pointer' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
                    data-testid="disclaimer-accept-btn"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prediction Engine Map - shown after disclaimer accepted (blocked during building mode for non-admin) */}
      {disclaimerAccepted && !(buildingMode && !adminMode) && (
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8 relative">

          {/* Manage Subscription Link for Active Subscribers */}
          {subscriptionStatus && !adminMode && (
            <div className="absolute top-0 right-0">
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="text-xs text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Manage Subscription
              </button>
            </div>
          )}
          
          {/* Subscription Status */}
          {(subscriptionStatus !== null || adminMode) && (
            <div className="text-center">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                hasPremiumAccess() ? 'bg-green-900 bg-opacity-30 border border-green-500 text-green-400' : 'bg-gray-700 border border-gray-600 text-gray-300'
              }`}>
                {(subscriptionStatus || adminMode) ? (adminMode ? '✅ Premium Active (Admin)' : '✅ Premium Active') : '🆓 Free Tier'}
              </span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className={`grid gap-4 mb-6 ${
          analysisType === "5 Day Forecast" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-3"
        }`}>
          {/* Currency Pair - Only show on Prediction Map */}
          {analysisType === "5 Day Forecast" && (
            <div>
              <label className="block text-sm font-medium mb-2 text-center">Currency Pair</label>
              <select
                value={currencyPair}
                onChange={(e) => setCurrencyPair(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              >
                {currencyPairs.map(pair => (
                  <option key={pair} value={pair}>{pair}</option>
                ))}
              </select>
            </div>
          )}

          {/* Timeframe - Only show on Prediction Map */}
          {analysisType === "5 Day Forecast" && (
            <div>
              <label className="block text-sm font-medium mb-2 text-center">
                Timeframe for Directional Patterns Scanner
              </label>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="4 Hours">4 Hours</option>
                <option value="1 Hour">1 Hour</option>
                <option value="1 Day">1 Day</option>
              </select>
            </div>
          )}

          {/* Forecast Model */}
          <div>
            <label className="block text-sm font-medium mb-2 text-center">Forecast Model</label>
            <select
              key="analysis-type-select"
              value={analysisType}
              onChange={(e) => {
                const newValue = e.target.value;
                
                // For premium features, check access first
                if (newValue !== "5 Day Forecast" && newValue !== "Directional Patterns") {
                  if (!hasPremiumAccess()) {
                    setShowSubscriptionModal(true);
                    return; // Don't change selection
                  }
                }
                
                setAnalysisType(newValue);
                // Reset pattern length when switching to Scanner or Prediction Map
                if (newValue === "Pattern Scanner" || newValue === "5 Day Forecast") {
                  setPatternLength(5); // Default value when not applicable
                }
                // Trigger modals for specific analysis types (only if has access)
                if (newValue === "5 Day Forecast") {
                  setAnalysisType("5 Day Forecast"); // Navigate to 5 Day Forecast page where button is
                } else if (newValue === "EURUSD Pivot Points Path") {
                  setShowPocVwapDashboard(true);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "USDJPY Pivot Points Path") {
                  setShowUsdjpyPocVwapDashboard(true);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "USDJPY Random Forest Cycles") {
                  setShowUsdjpyForecast(true);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "EURUSD Random Forest Cycles") {
                  setShowDayForecast(true);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "Prediction Engine Map") {
                  handleUndervaluedValleys();
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "Directional Patterns Scanner") {
                  handleCompassScanner();
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "HEGM Horizons") {
                  setShowHegmHorizons(true);
                  fetchHegmHorizons(currencyPair);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "Institutional Bias Locations") {
                  setShowInstitutionalBias(true);
                  setAnalysisType("5 Day Forecast");
                } else if (newValue === "Under Value Valleys") {
                  handleUndervaluedValleys();
                  setAnalysisType("5 Day Forecast");
                }
              }}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
            >
              {/* Admin-only options */}
              {adminMode && <option value="Directional Patterns">Directional Patterns</option>}
              
              {/* Premium options - matches Subscribe modal */}
              <option value="5 Day Forecast">5 Day Forecast (7 Pairs)</option>
              <option value="HEGM Horizons">HEGM Horizons (4 Hour Forecasts for 7 Pairs)</option>
              <option value="Institutional Bias Locations">Institutional Bias Locations (7 Pairs)</option>
              <option value="Directional Patterns Scanner">Directional Patterns Scanner (7 Pairs)</option>
              <option value="Under Value Valleys">Under Value Valleys (EURUSD and USDJPY)</option>
              <option value="EURUSD Random Forest Cycles">EURUSD Random Forest Cycles (&lt;1 Day Cycles)</option>
              <option value="USDJPY Random Forest Cycles">USDJPY Random Forest Cycles (&lt;1 Day Cycles)</option>
              <option value="EURUSD Pivot Points Path">Pivot Points Path (EURUSD and USDJPY)</option>
              <option value="Trained Navigator">Trained Navigator (7 Pairs)</option>
            </select>
          </div>

          {/* Pattern Length - Only show for Directional Patterns */}
          {analysisType === "Directional Patterns" && (
            <div>
              <label className="block text-sm font-medium mb-2">Pattern Length</label>
              <select
                value={patternLength}
                onChange={(e) => setPatternLength(parseInt(e.target.value))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value={3}>3-Pattern Analysis</option>
                <option value={5}>5-Pattern Analysis</option>
                <option value={8}>8-Pattern Analysis</option>
              </select>
            </div>
          )}
        </div>

        {/* Refresh Analysis button removed - analysis updates automatically */}

        {/* Subscription Button for Premium Features */}
        {!hasPremiumAccess() && (
          <div className="text-center mb-8">
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-medium text-white transition-all transform hover:scale-105"
            >
              🚀 Get Premium Access - $37.50/month or $350/year
            </button>
          </div>
        )}



        {/* Main Analysis Display */}
        {(data || analysisType === "5 Day Forecast") && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            {/* Subscription Required Message */}
            {data?.subscription_required && analysisType !== "5 Day Forecast" ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔒</div>
                <h2 className="text-2xl font-bold mb-4">Premium Feature Locked</h2>
                <p className="text-gray-300 mb-6">
                  Access to <strong>{data?.timeframe}</strong> Timeframe requires a premium subscription.
                </p>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-medium transition-all"
                >
                  🚀 Get Premium Access - $37.50/month or $350/year
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  {analysisType === "Pattern Scanner" ? (
                    <div>
                      <h2 className="text-2xl font-bold mb-2">
                        🔍 Directional Patterns Scanner
                      </h2>
                      {/* Show premium locked message for Pattern Scanner if not subscribed */}
                      {(!hasPremiumAccess()) ? (
                        <div className="text-center py-8">
                          <div className="text-4xl mb-4">🔒</div>
                          <h2 className="text-xl font-bold mb-4">Premium Feature Locked</h2>
                          <p className="text-gray-300 mb-6">
                            Access to the Directional Patterns Scanner requires a premium subscription.
                          </p>
                          <button
                            onClick={() => setShowSubscriptionModal(true)}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-medium transition-all"
                          >
                            🚀 Get Premium Access - $37.50/month or $350/year
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : analysisType === "5 Day Forecast" ? (
                    <div>
                      <div className="flex items-center justify-center mb-2">
                        {/* Static Compass Image - To the LEFT of Title */}
                        <div className="w-16 h-16 rounded-full border-3 border-amber-800 bg-amber-200 shadow-lg relative mr-4">
                          {/* Simple compass face with cardinal directions */}
                          <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                            <div className="text-xs font-bold text-gray-700">N</div>
                            <div className="absolute top-1 left-1/2 transform -translate-x-1/2 text-[8px] text-gray-600">N</div>
                            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-[8px] text-gray-600">S</div>
                            <div className="absolute left-1 top-1/2 transform -translate-y-1/2 text-[8px] text-gray-600">W</div>
                            <div className="absolute right-1 top-1/2 transform -translate-y-1/2 text-[8px] text-gray-600">E</div>
                            {/* Static needle pointing northeast */}
                            <div className="absolute left-1/2 top-1/2 w-0.5 h-4 bg-red-600 origin-bottom transform -translate-x-1/2 -translate-y-full rotate-45"></div>
                            <div className="absolute left-1/2 top-1/2 w-1 h-1 bg-black rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                          </div>
                        </div>
                        
                        <h2 className="text-4xl font-bold">
                          Prediction Engine Map
                        </h2>
                      </div>

                      <p className="text-sm text-white mb-2 text-center px-20">
                        The Signpost on the Prediction Engine Map is the live price and updates automatically every 1 minute
                      </p>
                      
                      {/* Show premium locked message for Prediction Map if not subscribed */}
                      {(!hasPremiumAccess()) ? (
                        <div className="text-center py-8">
                          <div className="text-4xl mb-4">🔒</div>
                          <h2 className="text-xl font-bold mb-4">Premium Feature Locked</h2>
                          <p className="text-gray-300 mb-6">
                            Access to the Prediction Map requires a premium subscription.
                          </p>
                          <button
                            onClick={() => setShowSubscriptionModal(true)}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-medium transition-all"
                          >
                            🚀 Get Premium Access - $37.50/month or $350/year
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4 mb-2">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex flex-col gap-2">
                              {/* EURUSD Pivot Point Path Dashboard button */}
                              <button
                                onClick={() => setShowPocVwapDashboard(true)}
                                className={`relative bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap ${Math.abs(pocVwapCombinedBias) >= 9 ? (pocVwapCombinedBias >= 9 ? 'ring-2 ring-green-400 ring-opacity-75' : 'ring-2 ring-red-400 ring-opacity-75') : ''}`}
                              >
                                {Math.abs(pocVwapCombinedBias) >= 9 && (
                                  <span className="absolute -right-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${pocVwapCombinedBias >= 9 ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                    <span className={`relative inline-flex rounded-full h-4 w-4 ${pocVwapCombinedBias >= 9 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                  </span>
                                )}
                                EURUSD Pivot Points Path
                              </button>

                              {/* EURUSD 2-Day Cycle Forecast button */}
                              <button
                                onClick={() => setShowDayForecast(true)}
                                disabled={forecastLoading}
                                className={`relative bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap disabled:cursor-not-allowed`}
                              >
                                {/* Left light: Scylla - green/red active, blinking grey pending, static grey idle */}
                                <span className="absolute -left-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                  {eurusdScyllaDirection ? (
                                    <>
                                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${eurusdScyllaDirection === 'UP' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                      <span className={`relative inline-flex rounded-full h-4 w-4 ${eurusdScyllaDirection === 'UP' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                    </>
                                  ) : eurusdScyllaPending ? (
                                    <>
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 bg-gray-300"></span>
                                      <span className="relative inline-flex rounded-full h-4 w-4 bg-gray-300"></span>
                                    </>
                                  ) : (
                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-gray-400"></span>
                                  )}
                                </span>
                                {/* Right light: Charybdis - green/red active, blinking white pending, static white idle */}
                                <span className="absolute -right-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                  {eurusdCharybdisDirection ? (
                                    <>
                                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${eurusdCharybdisDirection === 'UP' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                      <span className={`relative inline-flex rounded-full h-4 w-4 ${eurusdCharybdisDirection === 'UP' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                    </>
                                  ) : eurusdCharybdisPending ? (
                                    <>
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 bg-white"></span>
                                      <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                                    </>
                                  ) : (
                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                                  )}
                                </span>
                                {forecastLoading ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                  </span>
                                ) : (
                                  'EURUSD Random Forest Cycles'
                                )}
                              </button>

                              {/* USDJPY 2-Day Cycle Forecast button */}
                              <button
                                onClick={() => setShowUsdjpyForecast(true)}
                                className={`relative bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap`}
                              >
                                {/* Left light: Scylla - green/red active, blinking grey pending, static grey idle */}
                                <span className="absolute -left-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                  {usdjpyScyllaDirection ? (
                                    <>
                                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${usdjpyScyllaDirection === 'UP' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                      <span className={`relative inline-flex rounded-full h-4 w-4 ${usdjpyScyllaDirection === 'UP' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                    </>
                                  ) : usdjpyScyllaPending ? (
                                    <>
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 bg-gray-300"></span>
                                      <span className="relative inline-flex rounded-full h-4 w-4 bg-gray-300"></span>
                                    </>
                                  ) : (
                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-gray-400"></span>
                                  )}
                                </span>
                                {/* Right light: Charybdis - green/red active, blinking white pending, static white idle */}
                                <span className="absolute -right-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                  {usdjpyCharybdisDirection ? (
                                    <>
                                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${usdjpyCharybdisDirection === 'UP' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                      <span className={`relative inline-flex rounded-full h-4 w-4 ${usdjpyCharybdisDirection === 'UP' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                    </>
                                  ) : usdjpyCharybdisPending ? (
                                    <>
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 bg-white"></span>
                                      <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                                    </>
                                  ) : (
                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                                  )}
                                </span>
                                USDJPY Random Forest Cycles
                              </button>

                              {/* USDJPY Pivot Point Path Dashboard button */}
                              <button
                                onClick={() => setShowUsdjpyPocVwapDashboard(true)}
                                className={`relative bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap ${Math.abs(usdjpyPocVwapCombinedBias) >= 9 ? (usdjpyPocVwapCombinedBias >= 9 ? 'ring-2 ring-green-400 ring-opacity-75' : 'ring-2 ring-red-400 ring-opacity-75') : ''}`}
                              >
                                {Math.abs(usdjpyPocVwapCombinedBias) >= 9 && (
                                  <span className="absolute -right-2 top-1/2 -translate-y-1/2 flex h-4 w-4">
                                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${usdjpyPocVwapCombinedBias >= 9 ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                    <span className={`relative inline-flex rounded-full h-4 w-4 ${usdjpyPocVwapCombinedBias >= 9 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                  </span>
                                )}
                                USDJPY Pivot Points Path
                              </button>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                              <h3 className="text-xl text-white font-bold text-center mb-5">
                                {currencyPair}
                              </h3>
                              {/* HEGM Horizons button */}
                              <button
                                onClick={() => {
                                  setShowHegmHorizons(true);
                                  fetchHegmHorizons(currencyPair);
                                }}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                                data-testid="hegm-horizons-btn"
                              >
                                HEGM Horizons
                              </button>
                              {/* Institutional Bias Locations button - centered */}
                              <button
                                onClick={() => setShowInstitutionalBias(true)}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                                data-testid="institutional-bias-btn"
                              >
                                Institutional Bias Locations
                              </button>
                            </div>
                            <div className="flex flex-col gap-2">
                              {/* 5 Day Forecast button */}
                              <button
                                onClick={handle5DayForecast}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap"
                              >
                                5 Day Forecast
                              </button>

                              {/* Trained Navigator button */}
                              <button
                                onClick={async () => {
                                  setMatrixLoading(true);
                                  setShowTrainedNavigator(true);
                                  try {
                                    const res = await axios.get(`${API}/forex/matrix20`);
                                    setMatrixData(res.data);
                                    setMatrixLastUpdated(new Date());
                                  } catch (e) { console.error('Matrix fetch error', e); }
                                  setMatrixLoading(false);
                                }}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap text-center"
                                data-testid="trained-navigator-btn"
                              >
                                Trained Navigator
                              </button>

                              {/* Directional Patterns Scanner button */}
                              <button
                                onClick={handleCompassScanner}
                                disabled={scannerLoading}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap disabled:cursor-not-allowed"
                            >
                              {scannerLoading ? (
                                <span className="flex items-center">
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Scanning Patterns...
                                </span>
                              ) : (
                                'Directional Patterns Scanner'
                              )}
                            </button>

                              {/* Under Value Valleys button */}
                              <button
                                onClick={handleQuintileValleys}
                                disabled={quintileValleysLoading}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap disabled:cursor-not-allowed"
                              >
                                {quintileValleysLoading ? (
                                  <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Loading...
                                  </div>
                                ) : (
                                  'Under Value Valleys'
                                )}
                              </button>
                            </div>
                          </div>
                          {(horizonLoading || scannerLoading || horizonsLoading || dailyOpenLoading || forecastLoading) && (
                            <p className="text-blue-400 text-xs mt-1 text-center">
                              Analyzing {timeframe === "1 Day" ? "1000" : "1000"} periods of real market data...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold mb-2">
                        {currencyPair} • {patternLength}-Pattern Analysis
                      </h2>
                      <p className="text-gray-300 text-sm mb-4">
                        Prediction of the Direction of the Close of the next Price Bar
                      </p>
                    </>
                  )}
                </div>

                {/* AI Commentary Mode */}
                {data?.is_commentary_only ? (
                      <div className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-green-400">📊 Market Analysis</h4>
                        <p className="text-sm">{data.commentary.commentary}</p>
                      </div>
                      
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-yellow-400">🌐 Market Conditions</h4>
                        <p className="text-sm">{data.commentary.market_conditions}</p>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-red-400">⚠️ Risk Factors</h4>
                        <ul className="text-sm space-y-1">
                          {data.commentary.risk_factors.map((factor, index) => (
                            <li key={index}>• {factor}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-purple-400">🎯 Key Levels</h4>
                        <div className="text-sm space-y-1">
                          <p>Support: {data.commentary.key_levels.support}</p>
                          <p>Resistance: {data.commentary.key_levels.resistance}</p>
                          <p>Pivot: {data.commentary.key_levels.pivot}</p>
                        </div>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-blue-400">📈 10-Period Analysis</h4>
                        <div className="text-sm space-y-1">
                          <p><span className="text-green-400">High:</span> {data.commentary.period_analysis?.period_10_high}</p>
                          <p><span className="text-red-400">Low:</span> {data.commentary.period_analysis?.period_10_low}</p>
                          <p><span className="text-yellow-400">Current:</span> {data.commentary.period_analysis?.current_price}</p>
                          <p><span className="text-purple-400">Current Price:</span> {data.commentary.period_analysis?.price_position_pct}% of range</p>
                        </div>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-cyan-400">📊 Range Analysis</h4>
                        <div className="text-sm space-y-1 overflow-hidden">
                          <p className="break-words"><span className="text-gray-300">Range:</span> <span className="font-mono">{data.commentary.period_analysis?.high_low_range}</span></p>
                          <p><span className="text-gray-300">Position:</span> 
                            <span className={`ml-1 font-semibold ${
                              data.commentary.period_analysis?.price_position_pct > 70 ? 'text-green-400' :
                              data.commentary.period_analysis?.price_position_pct < 30 ? 'text-red-400' : 'text-yellow-400'
                            }`}>
                              {data.commentary.period_analysis?.price_position_pct > 70 ? 'Near High' :
                               data.commentary.period_analysis?.price_position_pct < 30 ? 'Near Low' : 'Mid-Range'}
                            </span>
                          </p>
                          <div className="mt-2">
                            <div className="w-full bg-gray-600 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${
                                  data.commentary.period_analysis?.price_position_pct > 70 ? 'bg-green-400' :
                                  data.commentary.period_analysis?.price_position_pct < 30 ? 'bg-red-400' : 'bg-yellow-400'
                                }`}
                                style={{width: `${data.commentary.period_analysis?.price_position_pct || 0}%`}}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-orange-400">🌡️ 10-Period Momentum</h4>
                        <div className="text-sm space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-green-400">Up Periods:</span>
                            <span className="font-semibold">{data.commentary.period_analysis?.up_periods}/10 ({data.commentary.period_analysis?.up_percentage}%)</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-red-400">Down Periods:</span>
                            <span className="font-semibold">{data.commentary.period_analysis?.down_periods}/10 ({data.commentary.period_analysis?.down_percentage}%)</span>
                          </div>
                          
                          {/* Horizontal Thermometer */}
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-red-400">Bearish</span>
                              <span className="text-gray-400">Momentum</span>
                              <span className="text-green-400">Bullish</span>
                            </div>
                            <div className="relative w-full bg-gray-600 rounded-full h-4 overflow-hidden">
                              {/* Red (Bearish) section */}
                              <div 
                                className="absolute left-0 top-0 h-full bg-red-500 transition-all"
                                style={{
                                  width: `${data.commentary.period_analysis?.down_percentage || 0}%`,
                                  opacity: 0.8
                                }}
                              ></div>
                              {/* Green (Bullish) section */}
                              <div 
                                className="absolute right-0 top-0 h-full bg-green-500 transition-all"
                                style={{
                                  width: `${data.commentary.period_analysis?.up_percentage || 0}%`,
                                  opacity: 0.8
                                }}
                              ></div>
                              {/* Center indicator line */}
                              <div className="absolute left-1/2 top-0 w-0.5 h-full bg-white transform -translate-x-0.5 opacity-60"></div>
                            </div>
                            <div className="flex justify-center mt-1">
                              <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                data.commentary.period_analysis?.up_percentage >= 70 ? 'bg-green-900 text-green-300' :
                                data.commentary.period_analysis?.up_percentage >= 60 ? 'bg-green-800 text-green-400' :
                                data.commentary.period_analysis?.up_percentage >= 40 ? 'bg-gray-700 text-gray-300' :
                                data.commentary.period_analysis?.up_percentage >= 30 ? 'bg-red-800 text-red-400' :
                                'bg-red-900 text-red-300'
                              }`}>
                                {data.commentary.period_analysis?.momentum_sentiment}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-700 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 text-indigo-400">📏 Pip Movement Analysis</h4>
                        <div className="text-sm space-y-2">
                          {/* Up Periods Section */}
                          <div className="bg-green-900 bg-opacity-30 rounded p-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-green-400 font-semibold">📈 Up Periods ({data.commentary.period_analysis?.up_periods})</span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg Range:</span>
                                <span className="text-green-300">{data.commentary.period_analysis?.up_avg_range_pips} pips</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Min-Max:</span>
                                <span className="text-green-300">{data.commentary.period_analysis?.up_min_range_pips}-{data.commentary.period_analysis?.up_max_range_pips} pips</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg Move:</span>
                                <span className="text-green-300">{data.commentary.period_analysis?.up_avg_movement_pips} pips</span>
                              </div>
                            </div>
                          </div>

                          {/* Down Periods Section */}
                          <div className="bg-red-900 bg-opacity-30 rounded p-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-red-400 font-semibold">📉 Down Periods ({data.commentary.period_analysis?.down_periods})</span>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg Range:</span>
                                <span className="text-red-300">{data.commentary.period_analysis?.down_avg_range_pips} pips</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Min-Max:</span>
                                <span className="text-red-300">{data.commentary.period_analysis?.down_min_range_pips}-{data.commentary.period_analysis?.down_max_range_pips} pips</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg Move:</span>
                                <span className="text-red-300">{data.commentary.period_analysis?.down_avg_movement_pips} pips</span>
                              </div>
                            </div>
                          </div>

                          {/* Comparison */}
                          <div className="text-center pt-1 border-t border-gray-600">
                            <span className="text-xs text-purple-400">
                              {data.commentary.period_analysis?.up_avg_range_pips > data.commentary.period_analysis?.down_avg_range_pips 
                                ? '📈 Up periods more volatile' 
                                : data.commentary.period_analysis?.down_avg_range_pips > data.commentary.period_analysis?.up_avg_range_pips
                                ? '📉 Down periods more volatile'
                                : '⚖️ Similar volatility'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : data?.scanner_results && (subscriptionStatus || adminMode) ? (
                  /* Pattern Scanner Mode - Premium Only */
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-xl font-semibold mb-4">🔍 Scanner Results</h3>
                      <p className="text-sm text-gray-400 mb-2">
                        Scanned {data.scanner_results?.total_scanned || 0} pattern length combinations for {currencyPair}
                      </p>
                      <p className="text-xs text-gray-500">
                        Each pattern tested against previous 1000 time intervals • Pattern frequency and success rates calculated from historical occurrences
                      </p>
                    </div>

                    {/* Top Bullish Patterns */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h4 className="text-lg font-semibold mb-4 text-green-400">📈 Top Bullish Patterns</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(data.scanner_results?.top_bullish || []).map((pattern, index) => (
                          <div key={index} className="bg-gray-800 rounded-lg p-4 border-l-4 border-green-400">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-semibold text-white">{pattern.currency_pair}</h5>
                              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">#{index + 1}</span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Pattern:</span>
                                <span className="font-mono text-green-400">{pattern.pattern}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Length:</span>
                                <span>{pattern.pattern_length}-Period</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Occurrences:</span>
                                <span className="font-semibold text-blue-400">{pattern.pattern_occurrences}x</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Accuracy:</span>
                                <span>{pattern.success_rate}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Price:</span>
                                <span>{pattern.current_price}</span>
                              </div>
                              {pattern.best_timeframe && (
                                <div className="mt-2 pt-2 border-t border-gray-600">
                                  <div className="text-xs text-gray-500 mb-1">Best Timeframe:</div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Timeframe:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.timeframe}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Accuracy:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.success_rate}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Occurrences:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.occurrences}x</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Bearish Patterns */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h4 className="text-lg font-semibold mb-4 text-red-400">📉 Top Bearish Patterns</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(data.scanner_results?.top_bearish || []).map((pattern, index) => (
                          <div key={index} className="bg-gray-800 rounded-lg p-4 border-l-4 border-red-400">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-semibold text-white">{pattern.currency_pair}</h5>
                              <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">#{index + 1}</span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Pattern:</span>
                                <span className="font-mono text-red-400">{pattern.pattern}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Length:</span>
                                <span>{pattern.pattern_length}-Period</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Occurrences:</span>
                                <span className="font-semibold text-blue-400">{pattern.pattern_occurrences}x</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Accuracy:</span>
                                <span>{pattern.success_rate}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Price:</span>
                                <span>{pattern.current_price}</span>
                              </div>
                              {pattern.best_timeframe && (
                                <div className="mt-2 pt-2 border-t border-gray-600">
                                  <div className="text-xs text-gray-500 mb-1">Best Timeframe:</div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Timeframe:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.timeframe}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Accuracy:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.success_rate}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-400 text-xs">Occurrences:</span>
                                    <span className="text-xs font-semibold text-yellow-400">{pattern.best_timeframe.occurrences}x</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Pattern Scanner Explanation */}
                    <div className="bg-gray-800 rounded-lg p-4 mt-6">
                      <h4 className="text-lg font-semibold mb-3 text-blue-400">💡 How the Pattern Scanner Works</h4>
                      <div className="text-sm text-gray-300 space-y-2">
                        <p>
                          <strong>Smart Cross-Timeframe Analysis:</strong> The scanner analyzes patterns from your selected timeframe perspective 
                          but shows you the optimal timeframe for executing each pattern based on historical performance.
                        </p>
                        <p>
                          <strong>Example:</strong> Selecting "15 minutes" scans for emerging patterns using 15-minute analysis, 
                          but if a pattern historically performs better on "1 Day" timeframe, that's shown as the "Best Timeframe" 
                          for maximum success rate.
                        </p>
                        <p>
                          <strong>Professional Advantage:</strong> This gives you early pattern detection with optimal execution timing - 
                          spot opportunities as they develop, then act when conditions are most favorable.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : analysisType === "5 Day Forecast" ? (
                  /* 5 Day Forecast - Premium Only */
                  !hasPremiumAccess() ? (
                    /* Show subscription modal for non-premium users */
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🔒</div>
                      <h2 className="text-2xl font-bold mb-4">Premium Feature Locked</h2>
                      <p className="text-gray-300 mb-6">
                        The <strong>Prediction Engine Map</strong> is a premium feature that requires a subscription.
                      </p>
                      <button
                        onClick={() => setShowSubscriptionModal(true)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-3 rounded-lg font-medium transition-all"
                      >
                        🚀 Get Premium Access - $37.50/month or $350/year
                      </button>
                    </div>
                  ) : (
                    /* Show Prediction Map for premium users */
                    <div className="max-w-4xl mx-auto">
                    {/* Enhanced Forecast Map with Premium Map Ambiance */}
                    <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-lg p-6 pb-6 pt-2 shadow-2xl border border-slate-700">
                      {/* Subtle map grid overlay */}
                      <div className="absolute inset-0 opacity-5 bg-gradient-to-br from-transparent via-blue-500 to-transparent rounded-lg"></div>
                      <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                                         radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)`
                      }}></div>
                      
                      <div className="flex justify-center gap-4 relative z-10">
                        {/* Signpost Live Update Indicator */}
                        {signpostUpdateIndicator && (
                          <div className="fixed top-20 right-4 bg-yellow-600 text-black px-4 py-2 rounded-lg shadow-lg flex items-center z-50">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                            <span className="text-sm font-medium">Updating Signpost...</span>
                          </div>
                        )}

                        {/* Last Update Time Display */}
                        {lastSignpostUpdate && (
                          <div className="text-center mb-4">
                            <div className="text-xs text-gray-500">
                              Signpost last updated: {lastSignpostUpdate}
                            </div>
                          </div>
                        )}

                        {/* Test button removed - moved to admin controls area */}

                        {/* Left Side: Prediction Map */}
                        <div className="flex flex-col items-center">
                          
                          {/* Enhanced Prediction Map with Professional Cartographic Styling - Extended Template */}
                          <div className="relative w-80 h-[650px] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 rounded-lg border-2 border-slate-600 shadow-2xl">
                          {/* Map texture overlay */}
                          <div className="absolute inset-0 opacity-10 rounded-lg" style={{
                            backgroundImage: `
                              linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                              linear-gradient(180deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
                            `,
                            backgroundSize: '20px 20px'
                          }}></div>
                          
                          {/* Corner decorative elements */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-blue-400 opacity-30"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-blue-400 opacity-30"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-blue-400 opacity-30"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-blue-400 opacity-30"></div>
                          
                          {/* Enhanced Chart Areas with Premium Depth Styling - Near Full Template Fill */}
                          <div className="absolute inset-1 flex flex-col h-full z-10">
                            {(() => {
                              const currentZone = determineZoneForPrice(fifteenMinClosePrice, zones);
                              return (
                                <>
                                  {/* Uppermost Zone - Enhanced with depth and glow */}
                                  <div className={`flex-1 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 bg-opacity-40 border-t-2 border-blue-300 relative flex flex-col items-center justify-center shadow-inner ${
                                    currentZone === 'uppermostArrival' ? 'pulse-zone-active border-white border-4 shadow-lg shadow-blue-500/50' : 'hover:shadow-md transition-shadow duration-300'
                                  }`} style={{
                                    boxShadow: currentZone === 'uppermostArrival' 
                                      ? 'inset 0 2px 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.4)' 
                                      : 'inset 0 2px 4px rgba(59, 130, 246, 0.1)'
                                  }}>
                                    <span className="text-blue-400 text-lg font-bold">UPPERMOST ZONE</span>
                                    <span className="text-blue-300 text-sm mt-2">{formatPrice(zones.uppermostArrivalZone.bottom)} - {formatPrice(zones.uppermostArrivalZone.top)}</span>
                                    {currentZone === 'uppermostArrival' && fifteenMinClosePrice && (
                                      <div className="relative">
                                        {/* Signpost sign */}
                                        <div className="mt-2 px-2 py-1 bg-yellow-400 bg-opacity-80 rounded text-black text-xs font-bold relative z-10 border-2 border-yellow-800">
                                          {formatPrice(fifteenMinClosePrice)}
                                        </div>
                                        {/* Wooden post extending down */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-yellow-800 to-yellow-900 shadow-lg"
                                             style={{ height: '20px', top: '100%', zIndex: 5 }}>
                                        </div>
                                        {/* Post cap */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-yellow-900 rounded-b"
                                             style={{ top: 'calc(100% + 20px)', zIndex: 5 }}>
                                        </div>
                                        {/* Live indicator dot */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full pulse-signpost-orb"
                                             style={{ top: 'calc(100% + 22px)', zIndex: 5 }}
                                             title="Live 1-min updates">
                                        </div>
                                      </div>
                                    )}
                                    {/* Compass positioned beside zone when active */}
                                    {currentZone === 'uppermostArrival' && (
                                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                                        <div className="w-6 h-6 bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                                          <span className="text-yellow-800 text-xs font-bold">🧭</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Arrival Line */}
                                  <div className="h-0 border-t-2 border-blue-500 relative"></div>
                                  
                                  {/* Bull Zone - Enhanced with premium depth */}
                                  <div className={`flex-1 bg-gradient-to-br from-green-500 via-green-600 to-green-700 bg-opacity-30 border-t-2 border-green-400 relative flex flex-col items-center justify-center shadow-inner ${
                                    currentZone === 'bull' ? 'pulse-zone-active border-white border-4 shadow-lg shadow-green-500/50' : 'hover:shadow-md transition-shadow duration-300'
                                  }`} style={{
                                    boxShadow: currentZone === 'bull' 
                                      ? 'inset 0 2px 4px rgba(34, 197, 94, 0.3), 0 0 20px rgba(34, 197, 94, 0.4)' 
                                      : 'inset 0 2px 4px rgba(34, 197, 94, 0.1)'
                                  }}>
                                    <span className="text-green-400 text-lg font-bold">BULL ZONE</span>
                                    <span className="text-green-300 text-sm mt-2">{formatPrice(zones.bullZone.bottom)} - {formatPrice(zones.bullZone.top)}</span>
                                    {currentZone === 'bull' && fifteenMinClosePrice && (
                                      <div className="relative">
                                        {/* Signpost sign */}
                                        <div className="mt-2 px-2 py-1 bg-yellow-400 bg-opacity-80 rounded text-black text-xs font-bold relative z-10 border-2 border-yellow-800">
                                          {formatPrice(fifteenMinClosePrice)}
                                        </div>
                                        {/* Wooden post extending down */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-yellow-800 to-yellow-900 shadow-lg"
                                             style={{ height: '20px', top: '100%', zIndex: 5 }}>
                                        </div>
                                        {/* Post cap */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-yellow-900 rounded-b"
                                             style={{ top: 'calc(100% + 20px)', zIndex: 5 }}>
                                        </div>
                                        {/* Live indicator dot */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full pulse-signpost-orb"
                                             style={{ top: 'calc(100% + 22px)', zIndex: 5 }}
                                             title="Live 1-min updates">
                                        </div>
                                      </div>
                                    )}
                                    {/* Compass positioned beside zone when active */}
                                    {currentZone === 'bull' && (
                                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                                        <div className="w-6 h-6 bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                                          <span className="text-yellow-800 text-xs font-bold">🧭</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* UP Forecast Line */}
                                  <div className="h-0 border-t-2 border-green-500 relative"></div>
                                  
                                  {/* Equilibrium Zone - Enhanced with elegant depth */}
                                  <div className={`flex-1 bg-gradient-to-br from-gray-500 via-gray-600 to-gray-700 bg-opacity-10 relative border-t border-b border-gray-500 flex flex-col items-center justify-center shadow-inner ${
                                    currentZone === 'equilibrium' ? 'pulse-zone-active border-white border-4 shadow-lg shadow-gray-500/50' : 'hover:shadow-md transition-shadow duration-300'
                                  }`} style={{
                                    boxShadow: currentZone === 'equilibrium' 
                                      ? 'inset 0 2px 4px rgba(107, 114, 128, 0.3), 0 0 20px rgba(107, 114, 128, 0.4)' 
                                      : 'inset 0 2px 4px rgba(107, 114, 128, 0.1)'
                                  }}>
                                    <span className="text-white text-lg font-bold">EQUILIBRIUM ZONE</span>
                                    <span className="text-gray-300 text-sm mt-2">{formatPrice(zones.equilibriumZone.bottom)} - {formatPrice(zones.equilibriumZone.top)}</span>
                                    {currentZone === 'equilibrium' && fifteenMinClosePrice && (
                                      <div className="relative">
                                        {/* Signpost sign */}
                                        <div className="mt-2 px-2 py-1 bg-yellow-400 bg-opacity-80 rounded text-black text-xs font-bold relative z-10 border-2 border-yellow-800">
                                          {formatPrice(fifteenMinClosePrice)}
                                        </div>
                                        {/* Wooden post extending down */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-yellow-800 to-yellow-900 shadow-lg"
                                             style={{ height: '20px', top: '100%', zIndex: 5 }}>
                                        </div>
                                        {/* Post cap */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-yellow-900 rounded-b"
                                             style={{ top: 'calc(100% + 20px)', zIndex: 5 }}>
                                        </div>
                                        {/* Live indicator dot */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full pulse-signpost-orb"
                                             style={{ top: 'calc(100% + 22px)', zIndex: 5 }}
                                             title="Live 1-min updates">
                                        </div>
                                      </div>
                                    )}
                                    {/* Compass positioned beside zone when active */}
                                    {currentZone === 'equilibrium' && (
                                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                                        <div className="w-6 h-6 bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                                          <span className="text-yellow-800 text-xs font-bold">🧭</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* DOWN Forecast Line */}
                                  <div className="h-0 border-t-2 border-red-500 relative"></div>
                                  
                                  {/* Bear Zone - Enhanced with dynamic depth */}
                                  <div className={`flex-1 bg-gradient-to-br from-red-500 via-red-600 to-red-700 bg-opacity-30 border-b-2 border-red-400 relative flex flex-col items-center justify-center shadow-inner ${
                                    currentZone === 'bear' ? 'pulse-zone-active border-white border-4 shadow-lg shadow-red-500/50' : 'hover:shadow-md transition-shadow duration-300'
                                  }`} style={{
                                    boxShadow: currentZone === 'bear' 
                                      ? 'inset 0 2px 4px rgba(239, 68, 68, 0.3), 0 0 20px rgba(239, 68, 68, 0.4)' 
                                      : 'inset 0 2px 4px rgba(239, 68, 68, 0.1)'
                                  }}>
                                    <span className="text-red-400 text-lg font-bold">BEAR ZONE</span>
                                    <span className="text-red-300 text-sm mt-2">{formatPrice(zones.bearZone.bottom)} - {formatPrice(zones.bearZone.top)}</span>
                                    {currentZone === 'bear' && fifteenMinClosePrice && (
                                      <div className="relative">
                                        {/* Signpost sign */}
                                        <div className="mt-2 px-2 py-1 bg-yellow-400 bg-opacity-80 rounded text-black text-xs font-bold relative z-10 border-2 border-yellow-800">
                                          {formatPrice(fifteenMinClosePrice)}
                                        </div>
                                        {/* Wooden post extending down */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-yellow-800 to-yellow-900 shadow-lg"
                                             style={{ height: '20px', top: '100%', zIndex: 5 }}>
                                        </div>
                                        {/* Post cap */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-yellow-900 rounded-b"
                                             style={{ top: 'calc(100% + 20px)', zIndex: 5 }}>
                                        </div>
                                        {/* Live indicator dot */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full pulse-signpost-orb"
                                             style={{ top: 'calc(100% + 22px)', zIndex: 5 }}
                                             title="Live 1-min updates">
                                        </div>
                                      </div>
                                    )}
                                    {/* Compass positioned beside zone when active */}
                                    {currentZone === 'bear' && (
                                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                                        <div className="w-6 h-6 bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                                          <span className="text-yellow-800 text-xs font-bold">🧭</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Arrival Line */}
                                  <div className="h-0 border-t-2 border-purple-500 relative"></div>
                                  
                                  {/* Lowermost Zone - Enhanced with rich depth */}
                                  <div className={`flex-1 bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 bg-opacity-40 border-b-2 border-purple-300 relative flex flex-col items-center justify-center shadow-inner ${
                                    currentZone === 'lowermostArrival' ? 'pulse-zone-active border-white border-4 shadow-lg shadow-purple-500/50' : 'hover:shadow-md transition-shadow duration-300'
                                  }`} style={{
                                    boxShadow: currentZone === 'lowermostArrival' 
                                      ? 'inset 0 2px 4px rgba(147, 51, 234, 0.3), 0 0 20px rgba(147, 51, 234, 0.4)' 
                                      : 'inset 0 2px 4px rgba(147, 51, 234, 0.1)'
                                  }}>
                                    <span className="text-purple-400 text-lg font-bold">LOWERMOST ZONE</span>
                                    <span className="text-purple-300 text-sm mt-2">{formatPrice(zones.lowermostArrivalZone.bottom)} - {formatPrice(zones.lowermostArrivalZone.top)}</span>
                                    {currentZone === 'lowermostArrival' && fifteenMinClosePrice && (
                                      <div className="relative">
                                        {/* Signpost sign */}
                                        <div className="mt-2 px-2 py-1 bg-yellow-400 bg-opacity-80 rounded text-black text-xs font-bold relative z-10 border-2 border-yellow-800">
                                          {formatPrice(fifteenMinClosePrice)}
                                        </div>
                                        {/* Wooden post extending down */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-yellow-800 to-yellow-900 shadow-lg"
                                             style={{ height: '20px', top: '100%', zIndex: 5 }}>
                                        </div>
                                        {/* Post cap */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-yellow-900 rounded-b"
                                             style={{ top: 'calc(100% + 20px)', zIndex: 5 }}>
                                        </div>
                                        {/* Live indicator dot */}
                                        <div className="absolute left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full pulse-signpost-orb"
                                             style={{ top: 'calc(100% + 22px)', zIndex: 5 }}
                                             title="Live 1-min updates">
                                        </div>
                                      </div>
                                    )}
                                    {/* Compass positioned beside zone when active */}
                                    {currentZone === 'lowermostArrival' && (
                                      <div className="absolute top-1/2 -left-8 transform -translate-y-1/2">
                                        <div className="w-6 h-6 bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                                          <span className="text-yellow-800 text-xs font-bold">🧭</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        </div>

                        {/* Right Side: Directional Patterns Grid - Centered beside map */}
                        <div className="flex flex-col items-center">
                          
                          {/* 4x7 Grid of Boxes - Centered vertically to match map */}
                          <div className="w-80 h-[650px] bg-gray-900 rounded-lg border-2 border-gray-600 p-2">
                            <div className="grid grid-cols-4 gap-1 h-full" style={{ gridTemplateRows: '2fr 2fr 2fr 3fr 2fr 2fr 2fr' }}>
                              {Array.from({ length: 28 }, (_, index) => {
                                // Custom content for specific boxes
                                let content = "";
                                let bgColor = "bg-gray-800";
                                let pulseClass = ""; // For pulsing animation
                                
                                // Determine row and column
                                const row = Math.floor(index / 4);
                                const col = index % 4;
                                
                                // Helper function to get pattern data including accuracy and frequency
                                const getPatternData = (timeframeKey, patternLength, isBullish) => {
                                  // Only check patterns that match the CURRENT selected timeframe
                                  if (timeframeKey !== timeframe) {
                                    return null; // Wrong timeframe, don't show data
                                  }
                                  
                                  // Check current_patterns from simplified scanner
                                  const currentPatterns = compassScannerData?.scanner_results?.current_patterns || [];
                                  
                                  for (const pattern of currentPatterns) {
                                    if (pattern.currency_pair === currencyPair && 
                                        pattern.pattern_length === patternLength) {
                                      // Check if this pattern matches the bullish/bearish criteria
                                      const patternIsBullish = pattern.prediction === 'BULLISH';
                                      const patternIsBearish = pattern.prediction === 'BEARISH';
                                      
                                      if ((isBullish && patternIsBullish) || (!isBullish && patternIsBearish)) {
                                        // Use success_rate for accuracy (same field shown in Compass Scanner modal)
                                        const accuracy = Math.round(pattern.success_rate) || Math.round(pattern.confidence) || Math.round(pattern.historical_accuracy) || 0;
                                        // Try multiple fields for frequency (occurrences is the correct field from scanner)
                                        const frequency = pattern.occurrences || pattern.pattern_occurrences || pattern.frequency || 0;
                                        
                                        // Only return data if frequency > 0 (can't have accuracy without occurrences)
                                        if (frequency > 0) {
                                          return {
                                            accuracy: accuracy,
                                            frequency: frequency,
                                            shouldFlash: accuracy >= 55 // Only flash if accuracy >= 55%
                                          };
                                        }
                                      }
                                    }
                                  }
                                  return null; // No pattern found or invalid data
                                };
                                
                                // Right column (timeframes)
                                if (index === 3) content = "4 Hr";       // Box 4
                                else if (index === 7) content = "1 Hr"; // Box 8
                                else if (index === 11) content = "1 Day"; // Box 12
                                else if (index === 19) content = "1 Day"; // Box 20
                                else if (index === 23) content = "1 Hr"; // Box 24
                                else if (index === 27) content = "4 Hr"; // Box 28
                                
                                // Middle row (pattern lengths)
                                else if (index === 12) content = "3";   // Box 13
                                else if (index === 13) content = "5";   // Box 14
                                else if (index === 14) content = "8";   // Box 15
                                else if (index === 15) content = formatTimer(nextUpdateTimer); // Box 16 - Timer
                                
                                // Bullish patterns (Rows 1-3, Columns 1-3)
                                else if (row <= 2 && col <= 2) {
                                  bgColor = "bg-green-600"; // Recent bullish patterns
                                  
                                  // Map grid position to timeframe and pattern length
                                  const timeframeMap = ["4 Hours", "1 Hour", "1 Day"];
                                  const patternLengthMap = [3, 5, 8];
                                  const gridTimeframe = timeframeMap[row];
                                  const patternLength = patternLengthMap[col];
                                  
                                  // Get pattern data with accuracy and frequency
                                  const patternData = getPatternData(gridTimeframe, patternLength, true);
                                  if (patternData) {
                                    if (patternData.shouldFlash) {
                                      pulseClass = "pulse-rank-1"; // Pulse if accuracy >= 55%
                                    }
                                    // Display accuracy (top) with % and frequency (bottom) with f (scientific format with space)
                                    content = (
                                      <div className="flex flex-col items-center justify-center">
                                        <div className="text-sm font-bold">{patternData.accuracy}%</div>
                                        <div className="text-xs">{patternData.frequency} f</div>
                                      </div>
                                    );
                                  } else {
                                    content = ""; // No pattern data
                                  }
                                }
                                
                                // Bearish patterns (Rows 5-7, Columns 1-3)  
                                else if (row >= 4 && col <= 2) {
                                  bgColor = "bg-red-600"; // Recent bearish patterns
                                  
                                  // Map grid position to timeframe and pattern length
                                  const timeframeMap = ["1 Day", "1 Hour", "4 Hours"];
                                  const patternLengthMap = [3, 5, 8];
                                  const gridTimeframe = timeframeMap[row - 4]; // Adjust for bearish section
                                  const patternLength = patternLengthMap[col];
                                  
                                  // Get pattern data with accuracy and frequency
                                  const patternData = getPatternData(gridTimeframe, patternLength, false);
                                  if (patternData) {
                                    if (patternData.shouldFlash) {
                                      pulseClass = "pulse-rank-1"; // Pulse if accuracy >= 55%
                                    }
                                    // Display accuracy (top) with % and frequency (bottom) with f (scientific format with space)
                                    content = (
                                      <div className="flex flex-col items-center justify-center">
                                        <div className="text-sm font-bold">{patternData.accuracy}%</div>
                                        <div className="text-xs">{patternData.frequency} f</div>
                                      </div>
                                    );
                                  } else {
                                    content = ""; // No pattern data
                                  }
                                }
                                
                                return (
                                  <div 
                                    key={index}
                                    className={`${bgColor} border border-gray-600 rounded flex items-center justify-center text-white text-xs font-semibold hover:bg-opacity-80 transition-colors ${pulseClass}`}
                                  >
                                    {content}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Correlation Tables Button - Removed */}
                        </div>
                      </div>
                      
                      {/* Explanatory Text Section */}
                      <div className="mt-8 bg-gray-700 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-white mb-4 text-center">How the Prediction Map Works</h4>
                        
                        {/* Map Update Schedule */}
                        <div className="mb-6 bg-blue-900 bg-opacity-30 border border-blue-400 rounded-lg p-4">
                          <div className="flex items-center justify-center mb-2">
                            <div className="w-4 h-4 bg-blue-400 rounded-full mr-3"></div>
                            <span className="text-blue-400 font-semibold text-center">Map Update Schedule</span>
                          </div>
                          <p className="text-gray-300 text-center">Prediction Map Updates once a week on Sunday at 22:00 UTC</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                          <div className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <div className="w-4 h-4 bg-green-500 bg-opacity-30 border border-green-400 rounded mr-3"></div>
                              <span className="text-green-400 font-semibold">UP Forecast</span>
                            </div>
                            <p className="text-gray-300">After a new Equilibrium Zone if the 1 minute price closes within the Bull Zone then that is the start of an Up Forecast with a forecasted time of arrival into the Uppermost Zone of 10 Days from the start day of the Up Forecast. If at any time after the Up Forecast the 1 minute price closes in the Bear Zone then that is an Up Forecast Failure and the start of a new Down Forecast with a forecasted time of arrival into the Lowermost Zone of 10 days from the start day of the Down Forecast.</p>
                          </div>
                          <div className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <div className="w-4 h-4 bg-red-500 bg-opacity-30 border border-red-400 rounded mr-3"></div>
                              <span className="text-red-400 font-semibold">DOWN Forecast</span>
                            </div>
                            <p className="text-gray-300">After a new Equilibrium Zone if the 1 minute price closes within the Bear Zone then that is the start of a Down Forecast with a forecasted time of arrival into the Lowermost Zone of 10 Days from the start day of the Down Forecast. If at any time after the Down Forecast the 1 minute price closes in the Bull Zone then that is a Down Forecast Failure and the start of a new Up Forecast with a forecasted time of arrival into the Uppermost Zone of 10 days from the start day of the Up Forecast.</p>
                          </div>
                        </div>
                      </div>

                      {/* Analysis Grid Legend */}
                      <div className="mt-6 bg-gray-700 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-white mb-4 text-center">Understanding the Map Grid</h4>
                        
                        {/* Signpost Description */}
                        <div className="bg-gray-800 rounded-lg p-4 mb-6">
                          <div className="flex items-center mb-3">
                            <div className="w-6 h-6 bg-gradient-to-b from-yellow-600 to-yellow-800 rounded mr-3 flex items-center justify-center">
                              <span className="text-xs text-white">📍</span>
                            </div>
                            <span className="text-white font-semibold text-lg">Signpost</span>
                          </div>
                          <p className="text-gray-300 mb-3">The signpost shows where the most recent 1 minute close price is located:</p>
                          <ul className="text-gray-400 text-sm space-y-2">
                            <li>• <span className="text-yellow-400">Signpost Movement:</span> The signpost moves beneath the price range in the zone containing the current market price</li>
                            <li>• <span className="text-blue-400">Compass Indicator:</span> A compass appears beside the active zone for visual emphasis</li>
                            <li>• <span className="text-white">Pulsing Zone Borders:</span> The active zone pulses with white borders to clearly indicate the current price location</li>
                            <li>• <span className="text-cyan-400">Automatic Updates:</span> Live data refreshes automatically every 1 minute with current market prices</li>
                          </ul>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                          <div className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <div className="w-4 h-4 bg-gray-600 border border-gray-500 rounded mr-3 flex items-center justify-center">
                                <span className="text-xs text-white">□</span>
                              </div>
                              <span className="text-white font-semibold">Grid Boxes</span>
                            </div>
                            <p className="text-gray-300 mb-2">Each box represents live Directional Patterns Analysis for different timeframes and pattern lengths.</p>
                            <div className="bg-blue-900 bg-opacity-30 border border-blue-500 rounded p-3 mb-2">
                              <p className="text-blue-300 text-sm font-semibold">⚡ Dynamic Timeframe Response</p>
                              <p className="text-blue-200 text-xs">Grid boxes react dynamically to different timeframes chosen in the timeframe dropdown menu. Change your timeframe selection to see real-time pattern analysis updates across all boxes.</p>
                            </div>
                            <ul className="text-gray-400 text-xs space-y-1">
                              <li>• <span className="text-green-400">Green section:</span> Bullish Directional Patterns zones</li>
                              <li>• <span className="text-red-400">Red section:</span> Bearish Directional Patterns zones</li>
                              <li>• Empty boxes indicate no high-ranking Directional Patterns</li>
                            </ul>
                          </div>
                          <div className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <div className="w-4 h-4 bg-yellow-400 rounded mr-3 pulse-rank-1 flex items-center justify-center">
                                <span className="text-xs text-black font-bold">55</span>
                              </div>
                              <span className="text-yellow-400 font-semibold">Golden Flashing</span>
                            </div>
                            <p className="text-gray-300 mb-2">Boxes flash gold when historical accuracy is 55% or greater. Two numbers appear in flashing boxes:</p>
                            <ul className="text-gray-400 text-xs space-y-1">
                              <li>• <span className="text-yellow-400">Upper number:</span> Historical accuracy</li>
                              <li>• <span className="text-yellow-400">Lower number:</span> Frequency of occurrences</li>
                              <li>• <span className="text-white">Flash indicates:</span> High-confidence patterns worth attention</li>
                              <li>• Perfect for treasure map navigation!</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )
                ) : (
                  /* Pattern Analysis Mode */
                  <>
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold mb-2">Most Recent {patternLength}-Period Pattern</h3>


                      <p className="text-sm text-blue-400">Next Update In: {countdown}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      {/* Pattern Display */}
                      <div className="bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold mb-2">Pattern</h4>
                        <div className="text-3xl font-mono mb-2 tracking-wider">
                          {data.current_pattern}
                        </div>
                        <p className="text-sm text-gray-400">
                          Last {patternLength} periods: {data.current_pattern}
                        </p>
                        <p className="text-sm mt-2 text-gray-300">
                          <strong>Date and Time (UTC):</strong> {data.pattern_completion_date || 'Loading...'}<br/>
                          <strong>Price when pattern completed:</strong> {data.pattern_completion_price || data.current_price}
                        </p>
                      </div>

                      {/* Prediction */}
                      <div className="bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold mb-2">Prediction</h4>
                        <div className={`text-2xl font-bold mb-2 ${
                          data.prediction === 'BULLISH' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {data.prediction}
                        </div>
                        <p className="text-sm text-gray-400">{timeframe}</p>
                      </div>

                      {/* Confidence */}
                      <div className="bg-gray-700 rounded-lg p-4 text-center">
                        <h4 className="font-semibold mb-2">Confidence</h4>
                        <div className="text-3xl font-bold text-blue-400 mb-2">
                          {data.confidence}%
                        </div>
                        <p className="text-sm text-gray-400">
                          Based on {data.up_after_pattern > data.down_after_pattern ? data.up_after_pattern : data.down_after_pattern}/{data.pattern_occurrences} historical outcomes
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          System Accuracy: {Math.round(data.historical_accuracy)}%
                        </p>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-lg font-semibold">{data.pattern_occurrences}</p>
                        <p className="text-sm text-gray-400">Pattern Occurrences</p>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-lg font-semibold text-green-400">{data.up_after_pattern}</p>
                        <p className="text-sm text-gray-400">UP after pattern</p>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-lg font-semibold text-red-400">{data.down_after_pattern}</p>
                        <p className="text-sm text-gray-400">DOWN after pattern</p>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3">
                        <p className="text-lg font-semibold text-blue-400">{data.success_rate}%</p>
                        <p className="text-sm text-gray-400">Success Rate</p>
                      </div>
                    </div>

                    {/* Explanation Section */}
                    <div className="mt-6 bg-gray-600 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-center text-blue-300">📚 Understanding the Numbers</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="bg-gray-700 rounded p-3">
                          <div className="font-semibold text-green-400 mb-1">Confidence ({data.confidence}%)</div>
                          <p className="text-gray-300">How sure we are about THIS specific prediction based on this pattern's historical performance.</p>
                        </div>
                        <div className="bg-gray-700 rounded p-3">
                          <div className="font-semibold text-blue-400 mb-1">Success Rate ({data.success_rate}%)</div>
                          <p className="text-gray-300">How often this specific pattern was followed by the dominant direction historically.</p>
                        </div>
                        <div className="bg-gray-700 rounded p-3">
                          <div className="font-semibold text-purple-400 mb-1">Historical Accuracy ({Math.round(data.historical_accuracy)}%)</div>
                          <p className="text-gray-300">Overall system accuracy across all patterns and timeframes in our database.</p>
                        </div>
                      </div>
                      <div className="mt-3 text-center text-xs text-gray-400">
                        💡 <strong>Example:</strong> If this pattern occurred 20 times and was followed by UP moves 15 times, 
                        the Success Rate would be 75% and our Confidence in a BULLISH prediction would be ~75%.
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
        


        {/* Recent Patterns (only show for Pattern Analysis and if not subscription required) */}
        {data && !data.is_commentary_only && !data?.subscription_required && analysisType === "Directional Patterns" && recentPatterns.recent_patterns && recentPatterns.recent_patterns.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Historical Pattern Analysis</h3>
            <h4 className="text-lg font-medium mb-4">Recent {patternLength}-Period Patterns</h4>
            
            <div className="space-y-3">
              {(recentPatterns.recent_patterns || []).map((pattern, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center space-x-4">
                    <span className="font-mono text-lg">{pattern.pattern}</span>
                    <span className="text-sm text-gray-400">{pattern.date}</span>
                    <span className="text-sm">{pattern.occurrences} occurrences</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${
                      pattern.prediction === 'UP' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      Next: {pattern.prediction}
                    </span>
                    <span className="text-sm text-gray-400 ml-2">{pattern.probability} probability</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center text-sm text-gray-400">
              <p>Total Patterns Analyzed: {recentPatterns.total_patterns_analyzed || 31}</p>
              <p>Historical Data Range: {recentPatterns.lookback_periods || 1000} periods</p>
              <p className="mt-2">Most Common Pattern: <span className="font-mono">{recentPatterns.most_common_pattern || 'DUDUD'}</span></p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 text-center text-yellow-400">RISK WARNING & DISCLOSURE STATEMENT</h3>
            <div className="text-xs text-gray-300 space-y-3 leading-relaxed uppercase">
              <p>
                <span className="font-bold text-white">CFTC RULE 4.41</span> - HYPOTHETICAL OR SIMULATED PERFORMANCE RESULTS HAVE CERTAIN LIMITATIONS. UNLIKE AN ACTUAL PERFORMANCE RECORD, SIMULATED RESULTS DO NOT REPRESENT ACTUAL TRADING. ALSO, SINCE THE TRADES HAVE NOT BEEN EXECUTED, THE RESULTS MAY HAVE UNDER-OR-OVER COMPENSATED FOR THE IMPACT, IF ANY, OF CERTAIN MARKET FACTORS, SUCH AS LACK OF LIQUIDITY. SIMULATED TRADING PROGRAMS IN GENERAL ARE ALSO SUBJECT TO THE FACT THAT THEY ARE DESIGNED WITH THE BENEFIT OF HINDSIGHT. NO REPRESENTATION IS BEING MADE THAT ANY ACCOUNT WILL OR IS LIKELY TO ACHIEVE PROFIT OR LOSSES SIMILAR TO THOSE SHOWN.
              </p>
              <div>
                <p className="font-bold text-yellow-400 mb-1">HIGH RISK WARNING:</p>
                <p>
                  Foreign Exchange (Forex) trading on margin carries a high level of risk and may not be suitable for all investors. The high degree of leverage can work against you as well as for you. Before deciding to trade foreign exchange, you should carefully consider your investment objectives, level of experience, and risk appetite. The possibility exists that you could sustain a loss of some or all of your initial investment. Therefore, you should not invest money that you cannot afford to lose. You should be aware of all the risks associated with foreign exchange trading and seek advice from an independent financial advisor if you have any doubts.
                </p>
              </div>
              <div>
                <p className="font-bold text-yellow-400 mb-1">FORECASTS AND EDUCATIONAL PURPOSES ONLY:</p>
                <p>
                  All information, analysis, forecasts, and commentary provided on this website are for educational and informational purposes only. Nothing contained on this site constitutes a solicitation, recommendation, endorsement, or offer by Forex Prediction Engine to buy or sell any financial instruments. Forex Prediction Engine is not a registered investment advisor or commodity trading advisor (CTA). You agree that any trades made based on the forecasts or information on this website are taken entirely at your own risk.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Controls in Bottom Left */}
        <div className="fixed bottom-4 left-4 space-y-2 z-50">
          <button
            onClick={async () => {
              // If trying to enable admin mode, verify PIN via backend
              if (!adminMode) {
                const enteredPin = window.prompt("Enter Admin PIN:");
                if (!enteredPin) return;
                try {
                  const res = await axios.post(`${API}/admin/verify-pin`, { pin: enteredPin });
                  if (!res.data.valid) {
                    alert("Incorrect PIN!");
                    return;
                  }
                } catch (e) {
                  alert("Error verifying PIN");
                  return;
                }
              }
              
              const newAdminMode = !adminMode;
              setAdminMode(newAdminMode);
              
              if (newAdminMode) {
                setSubscriptionStatus(true);
                localStorage.setItem('admin_mode', 'true');
              } else {
                setSubscriptionStatus(null);
                localStorage.removeItem('admin_mode');
                localStorage.removeItem('subscription_session_id');
              }
            }}
            className="block text-xs px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 shadow-lg border border-gray-600"
            style={{opacity: adminMode ? 1 : 0.4}}
          >
            {adminMode ? '🔓 Admin ON' : '🔒 Admin'}
          </button>
          
          {/* Friday Price Update Button - Only show in Admin Mode */}
          {adminMode && (
            <button
              onClick={() => setShowPriceUpdateModal(true)}
              className="block text-xs px-3 py-2 rounded-lg bg-blue-800 hover:bg-blue-700 text-blue-300 shadow-lg border border-blue-600"
            >
              📊 Update Friday Prices
            </button>
          )}

          {/* UnderValue Valleys Admin Button - Only show in Admin Mode */}

          {/* Correlation Tables Admin Button - Only show in Admin Mode */}
          {adminMode && (
            <button
              onClick={() => setShowCorrelationAdmin(true)}
              className="block text-xs px-3 py-2 rounded-lg bg-indigo-800 hover:bg-indigo-700 text-indigo-300 shadow-lg border border-indigo-600"
            >
              📊 Correlation Admin
            </button>
          )}

          {/* Test Signpost Button - Only show in Admin Mode and on Prediction Map */}
          {adminMode && analysisType === "5 Day Forecast" && (
            <button
              onClick={() => window.testSignpostUpdate && window.testSignpostUpdate()}
              className="block text-xs px-3 py-2 rounded-lg bg-yellow-800 hover:bg-yellow-700 text-yellow-300 shadow-lg border border-yellow-600"
            >
              🔧 Test Signpost
            </button>
          )}

          {/* Free Access Settings Button - Only show in Admin Mode */}
          {adminMode && (
            <button
              onClick={() => setShowFreeAccessModal(true)}
              className="block text-xs px-3 py-2 rounded-lg bg-green-800 hover:bg-green-700 text-green-300 shadow-lg border border-green-600"
            >
              📅 Free Access Settings
            </button>
          )}

          {adminMode && (
            <button
              onClick={() => setShowChangePinModal(true)}
              className="block text-xs px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 shadow-lg border border-gray-600"
              data-testid="change-pin-btn"
            >
              🔑 Change PIN
            </button>
          )}

          {adminMode && (
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/building-mode?session_id=admin_test_123`);
                  setBuildingMode(res.data.building_mode);
                  toast.success(res.data.building_mode ? 'Building Mode ON — visitors see maintenance page' : 'Building Mode OFF — site is live');
                } catch (e) { toast.error('Failed to toggle'); }
              }}
              className={`block text-xs px-3 py-2 rounded-lg shadow-lg border ${
                buildingMode 
                  ? 'bg-orange-800 hover:bg-orange-700 text-orange-300 border-orange-600' 
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600'
              }`}
              data-testid="building-mode-btn"
            >
              {buildingMode ? '🔧 Building Mode: ON' : '🔧 Building Mode: OFF'}
            </button>
          )}

          {adminMode && (
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/toggle-toasts?session_id=admin_test_123`);
                  setToastsEnabled(res.data.toasts_enabled);
                  toast.success(res.data.toasts_enabled ? 'Cycle Toasts: ON' : 'Cycle Toasts: OFF');
                } catch (e) { toast.error('Failed to toggle'); }
              }}
              className={`block text-xs px-3 py-2 rounded-lg shadow-lg border ${
                toastsEnabled 
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-600' 
                  : 'bg-red-900 hover:bg-red-800 text-red-300 border-red-600'
              }`}
              data-testid="toggle-toasts-btn"
            >
              {toastsEnabled ? '🔔 Cycle Toasts: ON' : '🔕 Cycle Toasts: OFF'}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-4 pt-4 border-t border-gray-700">
          <a
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            <img 
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" 
              alt="Emergent" 
              className="w-6 h-6 rounded"
            />
            <span>Made with Emergent</span>
          </a>
          <p className="text-xs text-gray-500 mt-2">
            © 2025 Forex Prediction Engine. All rights reserved.
          </p>
        </div>
      </div>
      )}

      {/* Logo Zoom Modal */}
      {showLogoZoom && <LogoZoomModal setShowLogoZoom={setShowLogoZoom} />}

      {/* Free Access Notification Signup Modal */}
      {showFreeAccessSignup && <FreeAccessSignupModal setShowFreeAccessSignup={setShowFreeAccessSignup} />}

      {/* Correlation Tables Modal */}
      {showCorrelationTables && (
        <CorrelationTablesModal 
          setShowCorrelationTables={setShowCorrelationTables} 
          correlationData={correlationData}
          adminMode={adminMode}
          setShowCorrelationAdmin={setShowCorrelationAdmin}
        />
      )}

      {/* Correlation Tables Admin Modal */}
      {showCorrelationAdmin && (
        <CorrelationAdminModal setShowCorrelationAdmin={setShowCorrelationAdmin} />
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && <SubscriptionModal />}
      
      {/* Friday Price Update Modal */}
      {showPriceUpdateModal && <PriceUpdateModal fridayPrices={fridayPrices} setFridayPrices={setFridayPrices} setShowPriceUpdateModal={setShowPriceUpdateModal} setFifteenMinClosePrice={setFifteenMinClosePrice} currencyPair={currencyPair} />}
      
      {/* Pivot Point Path Dashboard Modal */}
      {showPocVwapDashboard && <PocVwapDashboardModal setShowPocVwapDashboard={setShowPocVwapDashboard} />}
      
      {/* USDJPY Pivot Point Path Dashboard Modal */}
      {showUsdjpyPocVwapDashboard && <UsdjpyPocVwapDashboardModal setShowUsdjpyPocVwapDashboard={setShowUsdjpyPocVwapDashboard} />}
      
      {/* USDJPY 2-Day Cycle Forecast Modal */}
      {showUsdjpyForecast && <UsdjpyForecastModal setShowUsdjpyForecast={setShowUsdjpyForecast} adminMode={adminMode} />}
      
      {/* Free Access Settings Modal */}
      {showFreeAccessModal && <FreeAccessModal 
        showFreeAccessModal={showFreeAccessModal}
        setShowFreeAccessModal={setShowFreeAccessModal}
        freeAccessEnabled={freeAccessEnabled}
        setFreeAccessEnabled={setFreeAccessEnabled}
        freeAccessStartDay={freeAccessStartDay}
        setFreeAccessStartDay={setFreeAccessStartDay}
        freeAccessEndDay={freeAccessEndDay}
        setFreeAccessEndDay={setFreeAccessEndDay}
        freeAccessDate={freeAccessDate}
        setFreeAccessDate={setFreeAccessDate}
        freeAccessEndDate={freeAccessEndDate}
        setFreeAccessEndDate={setFreeAccessEndDate}
        freeAccessMode={freeAccessMode}
        setFreeAccessMode={setFreeAccessMode}
      />}

      {/* Change PIN Modal */}
      {showChangePinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4" onClick={() => setShowChangePinModal(false)}>
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-sm w-full border border-gray-700 p-6" onClick={e => e.stopPropagation()} data-testid="change-pin-modal">
            <h3 className="text-lg font-bold text-white mb-4 text-center">Change Admin PIN</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              const currentPin = form.currentPin.value;
              const newPin = form.newPin.value;
              const confirmPin = form.confirmPin.value;
              if (newPin !== confirmPin) {
                toast.error('New PINs do not match');
                return;
              }
              if (newPin.length < 4) {
                toast.error('PIN must be at least 4 characters');
                return;
              }
              try {
                await axios.post(`${API}/admin/change-pin?session_id=admin_test_123`, {
                  current_pin: currentPin,
                  new_pin: newPin
                });
                toast.success('PIN changed successfully');
                setShowChangePinModal(false);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to change PIN');
              }
            }}>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Current PIN</label>
                  <input name="currentPin" type="password" required className="w-full bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-1">New PIN</label>
                  <input name="newPin" type="password" required minLength={4} className="w-full bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Confirm New PIN</label>
                  <input name="confirmPin" type="password" required minLength={4} className="w-full bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button type="button" onClick={() => setShowChangePinModal(false)} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm">
                  Cancel
                </button>
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm" data-testid="change-pin-submit">
                  Change PIN
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Zone Close Stats Modal */}
      {showZoneCloseStats && <ZoneCloseStatsModal stats={zoneCloseStats} setShowZoneCloseStats={setShowZoneCloseStats} currencyPair={currencyPair} />}
      
      {/* 5 Day Forecast Modal */}
      {show5DayForecast && <FiveDayForecastModal forecast={fiveDayForecast} setShow5DayForecast={setShow5DayForecast} currencyPair={currencyPair} adminMode={adminMode} API={API} fifteenMinClosePrice={fifteenMinClosePrice} zones={zones} determineZoneForPrice={determineZoneForPrice} getDisplayZoneName={getDisplayZoneName} formatPrice={formatPrice} />}
      
      {/* Prediction Horizon Probabilities Modal */}
      {showDirectionProbabilities && <DirectionProbabilitiesModal probabilities={directionProbabilities} setShowDirectionProbabilities={setShowDirectionProbabilities} />}
      
      {/* Bias Density Modal removed - functionality consolidated into Directional Patterns Scanner */}
      
      {/* Directional Patterns Scanner Modal */}
      {showCompassScanner && <CompassScannerModal scannerData={compassScannerData} currentBlockPatterns={currentBlockPatterns} setShowCompassScanner={setShowCompassScanner} currencyPair={currencyPair} timeframe={timeframe} />}

      {/* Price Horizons Modal */}
      {showPriceHorizons && <PriceHorizonsModal priceHorizonsData={priceHorizonsData} setShowPriceHorizons={setShowPriceHorizons} currencyPair={currencyPair} />}

      {/* UnderValue Valleys Modal */}
      {/* Quintile Valleys Modal (New System) */}
      {showQuintileValleys && <QuintileValleysModal quintileValleysData={quintileValleysData} setShowQuintileValleys={setShowQuintileValleys} adminMode={adminMode} />}

      {/* Trained Navigator Modal */}
      {showTrainedNavigator && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setShowTrainedNavigator(false)}>
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 p-6" onClick={e => e.stopPropagation()} data-testid="trained-navigator-modal">
            <h2 className="text-2xl font-bold text-white text-center mb-2">Trained Navigator</h2>
            {inTrainingMode && (
              <div className="flex items-center justify-center gap-3 mb-3 animate-pulse">
                <span className="inline-flex rounded-full h-4 w-4 bg-purple-500"></span>
                <span className="text-purple-400 text-xl font-semibold">In Training</span>
                <span className="inline-flex rounded-full h-4 w-4 bg-purple-500"></span>
              </div>
            )}
            
            {matrixLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-400 mt-3">Loading matrix data...</p>
              </div>
            ) : matrixData?.status === 'warming_up' ? (
              <div className="text-center py-8">
                <p className="text-yellow-400">{matrixData.message}</p>
              </div>
            ) : matrixData?.rows ? (
              <div>
                {/* Column Headers */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {showMatrixHeaders && (
                    <thead>
                      <tr>
                        <th className="text-gray-400 text-xs text-left px-2 py-1 w-20">Pair</th>
                        <th className="text-gray-500 text-[9px] px-0.5 py-1" title={matrixData?.config?.col1_reference === 'todays_open' ? "Today's Open" : "Yesterday's Close"}>
                          {matrixData?.config?.col1_reference === 'todays_open' ? 'DO' : 'YC'}
                        </th>
                        {/* 12 SMA columns: 3 lengths × 4 MA timeframes */}
                        {(matrixData?.config?.ma_timeframes || ['M5','M15','M30','H1']).map((tf, ti) =>
                          [1,2,3].map((_, si) => (
                            <th key={`ma-${ti}-${si}`} className="text-gray-500 text-[9px] px-0.5 py-1">{tf.toLowerCase()}</th>
                          ))
                        )}
                        {/* 6 fixed High/Low columns */}
                        {['5m','15m','30m','1h','2h','4h'].map((tf, i) => (
                          <th key={`hl-${i}`} className="text-gray-500 text-[9px] px-0.5 py-1" title={`${tf} High/Low`}>{tf}</th>
                        ))}
                        {/* Yesterday's daily High/Low */}
                        <th className="text-gray-500 text-[9px] px-0.5 py-1" title="Yesterday's Daily High/Low">YHL</th>
                        <th className="text-gray-400 text-xs px-2 py-1 text-center">Sum</th>
                      </tr>
                    </thead>
                    )}
                    <tbody>
                      {matrixData.rows.map((row) => (
                        <tr key={row.pair} className="border-t border-gray-800">
                          <td className="text-white text-sm font-mono font-bold px-2 py-2 bg-blue-600">{row.pair}</td>
                          {row.scores.map((score, i) => (
                            <td key={i} className="px-0 py-0">
                              <div className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto ${
                                score === null ? 'bg-gray-800 border border-gray-600' :
                                score > 0 ? 'bg-green-500' :
                                score < 0 ? 'bg-red-500' :
                                'bg-blue-700'
                              }`} title={row.levels[i] != null ? row.levels[i].toFixed(5) : 'Warming up'}></div>
                            </td>
                          ))}
                          <td className="px-2 py-1">
                            <div className={`text-center font-bold text-sm rounded px-2 py-1 ${
                              row.state === 'BULL' ? 'bg-green-600 text-white' :
                              row.state === 'BEAR' ? 'bg-red-600 text-white' :
                              row.state === 'WAITING' ? 'bg-gray-700 text-gray-400' :
                              'bg-yellow-300'
                            }`}>
                              <span className={
                                row.state === 'TURBULENT'
                                  ? (row.bulls > row.bears ? 'text-green-700' : row.bears > row.bulls ? 'text-red-700' : 'text-black')
                                  : ''
                              }>
                              {row.state === 'WAITING' ? '..' : row.dominant}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="mt-4 bg-gray-800 rounded-lg p-3">
                  <div className="flex flex-wrap justify-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-green-500 rounded-sm"></span>
                      <span className="text-green-400">14 or greater = Bullish</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-red-500 rounded-sm"></span>
                      <span className="text-red-400">14 or greater = Bearish</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-yellow-300 rounded-sm"></span>
                      <span className="text-yellow-400">&lt;14 = Turbulent</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 bg-blue-700 rounded-sm"></span>
                      <span className="text-blue-400">Neutral</span>
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs text-center mt-2">
                    Automatically updates every 1 minute. Last updated: {matrixLastUpdated ? matrixLastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC' }) + ' UTC' : '—'}
                  </p>
                </div>

                {/* Buttons Row */}
                <div className="flex justify-center gap-3 mt-4 flex-wrap">
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/forex/matrix20-signals`);
                        setMatrixSignals(res.data.signals);
                        setShowMatrixSignals(true);
                      } catch (e) { console.error(e); }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    data-testid="matrix-signals-btn"
                  >
                    Current Bullish or Bearish
                  </button>

                  {/* Admin buttons */}
                  {adminMode && (
                    <>
                  <button
                    onClick={async () => {
                      setMatrixLoading(true);
                      try {
                        const res = await axios.get(`${API}/forex/matrix20`);
                        setMatrixData(res.data);
                        setMatrixLastUpdated(new Date());
                      } catch (e) { console.error(e); }
                      setMatrixLoading(false);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    data-testid="matrix-refresh-btn"
                  >
                    Refresh
                  </button>

                  {/* Admin: SMA Config */}
                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/admin/matrix20-config?session_id=admin_test_123`);
                          const lengths = res.data.sma_lengths;
                          setSmaForm({ sma1: lengths[0], sma2: lengths[1], sma3: lengths[2] });
                          if (res.data.ma_timeframes) setMaTfForm(res.data.ma_timeframes);
                          if (res.data.open_buffers) setBufferForm(prev => ({...prev, open: res.data.open_buffers}));
                          if (res.data.sma_buffers) setBufferForm(prev => ({...prev, sma: res.data.sma_buffers}));
                          if (res.data.pip_targets) setBufferForm(prev => ({...prev, targets: res.data.pip_targets}));
                          if (res.data.pip_stops) setBufferForm(prev => ({...prev, stops: res.data.pip_stops}));
                          setThresholdForm({
                            bull: res.data.bull_threshold || 14,
                            bear: res.data.bear_threshold || 14,
                            useGoFlat: res.data.use_go_flat !== false,
                            goFlatThreshold: res.data.go_flat_threshold || 0,
                          });
                        } catch (e) {}
                        setShowSmaConfig(true);
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                      data-testid="sma-config-btn"
                    >
                      SMA Config
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const newVal = !inTrainingMode;
                          await axios.post(`${API}/admin/matrix20-config-field?session_id=admin_test_123`, { field: 'in_training', value: newVal });
                          setInTrainingMode(newVal);
                          toast.success(newVal ? 'In Training sign ON' : 'In Training sign OFF');
                        } catch (e) { toast.error('Failed to toggle'); }
                      }}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        inTrainingMode 
                          ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                      data-testid="in-training-toggle-btn"
                    >
                      {inTrainingMode ? 'In Training: ON' : 'In Training: OFF'}
                    </button>
                    </>
                  )}

                  <button
                    onClick={() => setShowTrainedNavigator(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No data available.</p>
            )}
          </div>
        </div>
      )}

      {/* Matrix Signals Modal */}
      {showMatrixSignals && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => setShowMatrixSignals(false)}>
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full border border-gray-700 p-6" onClick={e => e.stopPropagation()} data-testid="matrix-signals-modal">
            <h3 className="text-lg font-bold text-white mb-1 text-center">Current Bullish or Bearish</h3>
            <p className="text-gray-500 text-xs text-center mb-3">
              Auto-updates every 1 min{signalsLastRefresh ? ` · ${signalsLastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC` : ''}
            </p>
            
            {/* Header row */}
            <div className="grid grid-cols-8 gap-1 mb-2 px-3">
              <span className="text-gray-400 text-xs font-semibold">Pair</span>
              <span className="text-gray-400 text-xs font-semibold text-center">Signal</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Trigger Price</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Pips Target</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Pips Stop</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Price</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Float</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Total</span>
            </div>

            <div className="space-y-2">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => {
                const pairData = matrixSignals?.[pair] || {};
                const latest = pairData.latest;
                const recentTrades = pairData.recent_trades || [];
                const pipTarget = pairData.pip_target || 0;
                const tracker = pairData.tracker || {};
                const pipDecimals = pair.includes('JPY') ? 3 : 5;
                
                return (
                  <details key={pair} className="group">
                    <summary className="bg-gray-800 rounded-lg p-3 grid grid-cols-8 gap-1 items-center cursor-pointer list-none hover:bg-gray-750 transition-colors">
                      <p className="text-white font-mono font-bold text-sm">{pair}</p>
                      {latest ? (
                        <>
                          <span className={`text-xs font-semibold text-center ${latest.signal === 'BULL' ? 'text-green-400' : 'text-red-400'}`}>
                            {latest.signal === 'BULL' ? 'Bullish' : 'Bearish'}
                            <span className="text-gray-500 ml-1">
                              ({new Date(latest.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })})
                            </span>
                          </span>
                          <span className="text-white font-mono text-sm text-right">
                            {latest.price?.toFixed(pipDecimals)}
                          </span>
                          <span className="text-yellow-400 font-mono text-sm text-right">
                            {pipTarget > 0 ? pipTarget : '0'}
                          </span>
                          <span className="text-yellow-400 font-mono text-sm text-right">
                            {(pairData.pip_stop || 0) > 0 ? pairData.pip_stop : '0'}
                          </span>
                          <span className="text-white font-mono text-sm text-right">
                            {pairData.current_price ? pairData.current_price.toFixed(pipDecimals) : '—'}
                          </span>
                          {(() => {
                            const pipSize = pair.includes('JPY') ? 0.01 : 0.0001;
                            const triggerPrice = latest.price;
                            const currentPrice = pairData.current_price;
                            if (!triggerPrice || !currentPrice) return <span className="text-gray-500 text-xs text-right">—</span>;
                            const rawPips = (currentPrice - triggerPrice) / pipSize;
                            const floatPips = latest.signal === 'BULL' ? rawPips : -rawPips;
                            return (
                              <span className={`font-mono text-xs font-bold text-right ${
                                floatPips > 0 ? 'text-green-400' : floatPips < 0 ? 'text-red-400' : 'text-gray-500'
                              }`}>
                                {`${floatPips >= 0 ? '+' : ''}${floatPips.toFixed(1)}`}
                              </span>
                            );
                          })()}
                          <span className={`font-mono text-xs font-bold text-right ${
                            tracker.total_pips > 0 ? 'text-green-400' : 
                            tracker.total_pips < 0 ? 'text-red-400' : 'text-gray-500'
                          }`}>
                            {tracker.trade_count > 0 ? `${tracker.total_pips >= 0 ? '+' : ''}${tracker.total_pips}` : '—'}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-gray-500 text-xs text-center">No signal</span>
                          <span className="text-gray-500 text-xs text-right">—</span>
                          <span className="text-gray-500 text-xs text-right">0</span>
                          <span className="text-gray-500 text-xs text-right">0</span>
                          <span className="text-gray-500 text-xs text-right">—</span>
                          <span className="text-gray-500 text-xs text-right">—</span>
                          <span className="text-gray-500 text-xs text-right">—</span>
                        </>
                      )}
                    </summary>
                    {recentTrades.length > 0 && (
                      <div className="bg-gray-850 border-l-2 border-gray-600 ml-2 mt-1 mb-1 rounded-r-lg p-2 space-y-1">
                        <p className="text-gray-400 text-xs font-semibold mb-1">Last {recentTrades.length} signal switches</p>
                        {recentTrades.map((trade, idx) => (
                          <div key={idx} className="flex items-center justify-between px-2 py-1 rounded bg-gray-800">
                            <span className="text-gray-400 text-xs">#{recentTrades.length - idx}</span>
                            <span className={`font-mono text-xs font-bold ${trade.pips >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {trade.pips >= 0 ? '+' : ''}{trade.pips} pips
                            </span>
                            {trade.at_target && <span className="text-yellow-400 text-xs ml-1">TP</span>}
                            {trade.at_stop && <span className="text-orange-400 text-xs ml-1">SL</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </details>
                );
              })}
            </div>

            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={() => setShowMatrixSignals(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm"
              >
                Close
              </button>
              {adminMode && (
                <>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/matrix20-tracking?session_id=admin_test_123`);
                      setMatrixTracking(res.data.tracking);
                      const cfgRes = await axios.get(`${API}/admin/matrix20-config?session_id=admin_test_123`);
                      setInvertSignals(cfgRes.data.invert_signals || false);
                      setShowMatrixTracking(true);
                    } catch (e) { console.error(e); }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg text-sm"
                  data-testid="matrix-tracking-btn"
                >
                  Tracking
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Reset all trade history to zero? This clears all signal switch results.')) return;
                    try {
                      await axios.post(`${API}/admin/matrix20-tracking-reset?session_id=admin_test_123`);
                      // Refresh signals to show cleared history
                      const res = await axios.get(`${API}/forex/matrix20-signals`);
                      setMatrixSignals(res.data.signals);
                      setSignalsLastRefresh(new Date());
                      toast.success('Trade history reset to zero');
                    } catch (e) { toast.error('Failed to reset'); }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm"
                  data-testid="matrix-reset-btn"
                >
                  Reset
                </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Matrix Tracking Modal */}
      {showMatrixTracking && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4" onClick={() => setShowMatrixTracking(false)}>
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-sm w-full border border-gray-700 p-6" onClick={e => e.stopPropagation()} data-testid="matrix-tracking-modal">
            <h3 className="text-lg font-bold text-white mb-4 text-center">Signal Switch Tracking</h3>
            <div className="flex justify-center mb-4">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/matrix20-invert?session_id=admin_test_123`);
                    setInvertSignals(res.data.invert_signals);
                    toast.success(`Signals ${res.data.invert_signals ? 'inverted' : 'normal'}`);
                  } catch (e) { toast.error('Failed to toggle'); }
                }}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  invertSignals 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
                data-testid="invert-signals-btn"
              >
                {invertSignals ? 'Inverted Signals: ON' : 'Invert Signals: OFF'}
              </button>
            </div>
            <div className="grid grid-cols-3 px-3 mb-2">
              <span className="text-gray-400 text-xs font-semibold">Pair</span>
              <span className="text-gray-400 text-xs font-semibold text-center">Switches</span>
              <span className="text-gray-400 text-xs font-semibold text-right">Running Total</span>
            </div>
            <div className="space-y-2">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => {
                const data = matrixTracking?.[pair];
                const total = data?.running_total_pips ?? 0;
                const switches = data?.switch_count ?? 0;
                return (
                  <div key={pair} className="bg-gray-800 rounded-lg p-3 grid grid-cols-3 items-center">
                    <span className="text-white font-mono font-bold text-sm">{pair}</span>
                    <span className="text-gray-300 text-sm text-center">{switches}/20</span>
                    <span className={`font-bold text-sm text-right ${total > 0 ? 'text-green-400' : total < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {total > 0 ? '+' : ''}{total.toFixed(1)} pips
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={() => setShowMatrixTracking(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  if (window.confirm('Reset all tracking totals to zero?')) {
                    try {
                      await axios.post(`${API}/admin/matrix20-tracking-reset?session_id=admin_test_123`);
                      setMatrixTracking({});
                      toast.success('Tracking totals reset');
                    } catch (e) { toast.error('Failed to reset'); }
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm"
                data-testid="tracking-reset-btn"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMA Config Modal */}
      {showSmaConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => setShowSmaConfig(false)}>
          <div className="bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full border border-gray-700 p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4 text-center">Navigator Configuration</h3>
            
            {/* SMA Lengths (3) */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">SMA Lengths (applied to all 4 MA timeframes)</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[1,2,3].map(i => (
                <div key={i}>
                  <label className="text-gray-400 text-xs">SMA {i}</label>
                  <input
                    type="number"
                    min="1"
                    value={smaForm[`sma${i}`]}
                    onChange={e => setSmaForm({...smaForm, [`sma${i}`]: parseInt(e.target.value) || 1})}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-1.5 text-sm"
                    data-testid={`sma-input-${i}`}
                  />
                </div>
              ))}
            </div>

            {/* MA Timeframes (4 selectable) */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">MA Timeframes (4 groups)</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[0,1,2,3].map(i => (
                <div key={i}>
                  <label className="text-gray-400 text-xs">Group {i + 1}</label>
                  <select
                    value={maTfForm[i]}
                    onChange={e => {
                      const updated = [...maTfForm];
                      updated[i] = e.target.value;
                      setMaTfForm(updated);
                    }}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1.5 text-sm"
                    data-testid={`ma-tf-select-${i}`}
                  >
                    <option value="M5">5 min</option>
                    <option value="M15">15 min</option>
                    <option value="M30">30 min</option>
                    <option value="H1">1 hr</option>
                    <option value="H4">4 hr</option>
                  </select>
                </div>
              ))}
            </div>

            {/* Signal Thresholds */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">Signal Thresholds</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-green-400 text-xs">Bullish Threshold</label>
                <input
                  type="number"
                  min="5" max="20"
                  value={thresholdForm.bull}
                  onChange={e => setThresholdForm({...thresholdForm, bull: parseInt(e.target.value) || 14})}
                  className="w-full bg-gray-800 text-green-400 border border-gray-600 rounded px-3 py-1.5 text-sm"
                  data-testid="bull-threshold-input"
                />
              </div>
              <div>
                <label className="text-red-400 text-xs">Bearish Threshold</label>
                <input
                  type="number"
                  min="5" max="20"
                  value={thresholdForm.bear}
                  onChange={e => setThresholdForm({...thresholdForm, bear: parseInt(e.target.value) || 14})}
                  className="w-full bg-gray-800 text-red-400 border border-gray-600 rounded px-3 py-1.5 text-sm"
                  data-testid="bear-threshold-input"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs">Use Go Flat</label>
                <select
                  value={thresholdForm.useGoFlat ? 'Yes' : 'No'}
                  onChange={e => setThresholdForm({...thresholdForm, useGoFlat: e.target.value === 'Yes'})}
                  className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-1.5 text-sm"
                  data-testid="use-go-flat-select"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs">Go Flat Threshold</label>
                <input
                  type="number"
                  min="0" max="20"
                  value={thresholdForm.goFlatThreshold}
                  onChange={e => setThresholdForm({...thresholdForm, goFlatThreshold: parseInt(e.target.value) || 0})}
                  className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-1.5 text-sm"
                  disabled={!thresholdForm.useGoFlat}
                  data-testid="go-flat-threshold-input"
                />
              </div>
            </div>

            {/* Column 1 Buffer */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">Column 1 Buffer (pips)</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => (
                <div key={pair}>
                  <label className="text-gray-500 text-[10px]">{pair}</label>
                  <input
                    type="number"
                    min="0"
                    value={bufferForm.open[pair] || 0}
                    onChange={e => setBufferForm({...bufferForm, open: {...bufferForm.open, [pair]: parseFloat(e.target.value) || 0}})}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>

            {/* SMA Buffers */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">SMA Buffer (pips)</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => (
                <div key={pair}>
                  <label className="text-gray-500 text-[10px]">{pair}</label>
                  <input
                    type="number"
                    min="0"
                    value={bufferForm.sma[pair] || 0}
                    onChange={e => setBufferForm({...bufferForm, sma: {...bufferForm.sma, [pair]: parseFloat(e.target.value) || 0}})}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>

            {/* Pip Targets (TP) */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">Profit Target (pips) — 0 = no target</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => (
                <div key={pair}>
                  <label className="text-gray-500 text-[10px]">{pair}</label>
                  <input
                    type="number"
                    min="0"
                    value={bufferForm.targets[pair] || 0}
                    onChange={e => setBufferForm({...bufferForm, targets: {...bufferForm.targets, [pair]: parseFloat(e.target.value) || 0}})}
                    className="w-full bg-gray-800 text-green-400 border border-gray-600 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>

            {/* Pip Stops (SL) */}
            <p className="text-gray-400 text-xs mb-2 font-semibold">Stop Loss (pips) — 0 = no stop</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {['EURUSD','GBPUSD','USDJPY','USDCHF','USDCAD','AUDUSD','NZDUSD'].map(pair => (
                <div key={pair}>
                  <label className="text-gray-500 text-[10px]">{pair}</label>
                  <input
                    type="number"
                    min="0"
                    value={bufferForm.stops[pair] || 0}
                    onChange={e => setBufferForm({...bufferForm, stops: {...bufferForm.stops, [pair]: parseFloat(e.target.value) || 0}})}
                    className="w-full bg-gray-800 text-red-400 border border-gray-600 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSmaConfig(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/matrix20-config?session_id=admin_test_123`, {
                      sma_lengths: [smaForm.sma1, smaForm.sma2, smaForm.sma3],
                      ma_timeframes: maTfForm,
                      bull_threshold: thresholdForm.bull,
                      bear_threshold: thresholdForm.bear,
                      use_go_flat: thresholdForm.useGoFlat,
                      go_flat_threshold: thresholdForm.goFlatThreshold,
                      open_buffers: bufferForm.open,
                      sma_buffers: bufferForm.sma,
                      pip_targets: bufferForm.targets,
                      pip_stops: bufferForm.stops,
                    });
                    toast.success('Configuration updated. Matrix reinitializing...');
                    setShowSmaConfig(false);
                  } catch (e) {
                    toast.error('Failed to update config');
                  }
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                data-testid="sma-save-btn"
              >
                Save & Reinitialize
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Legacy UnderValue Valleys Modal */}
      {showUndervaluedValleys && <UndervaluedValleysModal undervaluedValleysData={undervaluedValleysData} setShowUndervaluedValleys={setShowUndervaluedValleys} currencyPair={currencyPair} />}

      {/* Peaks and Valleys Admin Modal */}
      {showPeaksValleysAdmin && <PeaksValleysAdminModal peaksValleys={peaksValleys} setShowPeaksValleysAdmin={setShowPeaksValleysAdmin} setPeaksValleys={setPeaksValleys} />}

      {/* 2 Day Forecast Modal */}
      {showDayForecast && <DayForecastModal dayForecastData={dayForecastData} setShowDayForecast={setShowDayForecast} currencyPair={currencyPair} adminMode={adminMode} />}
      
      {/* HEGM Horizons Modal */}
      {showHegmHorizons && (
        <HegmHorizonsModal 
          showHegmHorizons={showHegmHorizons}
          setShowHegmHorizons={setShowHegmHorizons}
          hegmHorizonsData={hegmHorizonsData}
          hegmHorizonsLoading={hegmHorizonsLoading}
          fetchHegmHorizons={fetchHegmHorizons}
        />
      )}
      
      {/* Institutional Bias Locations Modal */}
      {showInstitutionalBias && (
        <InstitutionalBiasModal 
          showInstitutionalBias={showInstitutionalBias}
          setShowInstitutionalBias={setShowInstitutionalBias}
          institutionalBiasData={institutionalBiasData}
          institutionalBiasLoading={institutionalBiasLoading}
          fetchInstitutionalBias={fetchInstitutionalBias}
          adminMode={adminMode}
        />
      )}
      
      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

// Directional Patterns Scanner Modal Component
const CompassScannerModal = ({ scannerData, currentBlockPatterns, setShowCompassScanner, currencyPair, timeframe }) => {
  
  // Get current patterns from the simplified scanner response
  const currentPatterns = scannerData?.scanner_results?.current_patterns || [];
  if (!scannerData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">🔍 Directional Patterns Scanner</h2>
          <p className="text-gray-300 text-sm">
            {currencyPair} • {timeframe}
          </p>
        </div>

        {/* Most Recent Patterns Section - Simplified */}
        <div className="mb-8">
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-3 text-blue-400">📊 Directional Patterns Analysis</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              Each price bar is classified by its close direction — Up (U) or Down (D) — regardless of how much it moved. For example, a UUD pattern on 1-hour price bars means the most recent bar closed Down, the bar before it closed Up, and the bar before that also closed Up. The most recently completed price bar is the completion bar in an 8-bar Directional Pattern. Nested within that pattern is a 5-bar Directional Pattern and a 3-bar Directional Pattern. These patterns are identified from the most recently completed 1000 price bars. The number of occurrences of each specific pattern is given, and based on historical moves after each pattern, a probability percentage is given for the current price bar completing with either a bullish close up or a bearish close down.
            </p>
          </div>
          {currentPatterns && currentPatterns.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentPatterns.sort((a, b) => a.pattern_length - b.pattern_length).map((pattern, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4 border-l-4 border-blue-400">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold text-white">Length {pattern.pattern_length}</h5>
                    <span className={`text-xs px-2 py-1 rounded ${
                      pattern.prediction === 'BULLISH' ? 'bg-green-600' : 
                      pattern.prediction === 'BEARISH' ? 'bg-red-600' : 'bg-gray-600'
                    } text-white`}>
                      {pattern.prediction}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pattern:</span>
                      <span className="font-mono text-blue-400">{pattern.pattern}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Accuracy:</span>
                      <span className="font-semibold text-green-400">{pattern.success_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Occurrences:</span>
                      <span className="text-gray-300">{pattern.occurrences}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4">📭</div>
              <h3 className="text-white font-semibold mb-2">No Current Octet Block Patterns</h3>
              <p className="text-gray-300 text-sm">
                No patterns found in the current {timeframe} Octet Block for analysis.
              </p>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowCompassScanner(false)}
          className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full"
        >
          Close Scanner
        </button>
      </div>
    </div>
  );
};

// Price Horizons Modal Component
const PriceHorizonsModal = ({ priceHorizonsData, setShowPriceHorizons, currencyPair }) => {
  if (!priceHorizonsData) return null;

  const formatPrice = (price) => {
    if (typeof price !== 'number') return price;
    return currencyPair.includes('JPY') ? price.toFixed(3) : price.toFixed(5);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">Quintile Valleys</h2>
          <p className="text-gray-300 text-lg font-semibold">
            {currencyPair}
          </p>
        </div>

        {/* NEW INVERTED HORIZON SYSTEM */}
        <div className="space-y-4">
          {/* Distant Bear Peak */}
          <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-4">
            <div className="text-red-300 font-semibold text-lg">
              Distant Bear Peak = {formatPrice(priceHorizonsData.distant_bear_peak)}
            </div>
          </div>

          {/* Near Bear Peak */}
          <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-4">
            <div className="text-red-300 font-semibold text-lg">
              Near Bear Peak = {formatPrice(priceHorizonsData.near_bear_peak)}
            </div>
          </div>

          {/* Near Bull Valley */}
          <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-4">
            <div className="text-green-300 font-semibold text-lg">
              Near Bull Valley = {formatPrice(priceHorizonsData.near_bull_valley)}
            </div>
          </div>

          {/* Distant Bull Valley */}
          <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-4">
            <div className="text-green-300 font-semibold text-lg">
              Distant Bull Valley = {formatPrice(priceHorizonsData.distant_bull_valley)}
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowPriceHorizons(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>

        {/* Timestamp */}
        <div className="text-center text-xs text-gray-500 mt-4">
          Analysis generated: {priceHorizonsData.analysis_timestamp}
        </div>
      </div>
    </div>
  );
};

// Quintile Valleys Modal Component (New System)
const QuintileValleysModal = ({ quintileValleysData, setShowQuintileValleys, adminMode }) => {
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showMonthlyLevels, setShowMonthlyLevels] = useState(false);
  const [monthlyLevelsPair, setMonthlyLevelsPair] = useState('EURUSD');
  const [monthlyLevelsForm, setMonthlyLevelsForm] = useState({ Q1: '', Q2: '', Q3: '', Q4: '', Q5: '' });
  const [adminForm, setAdminForm] = useState({
    currency_pair: 'EURUSD',
    open_type: 'year',
    price: '',
    date: ''
  });

  if (!quintileValleysData) return null;

  const quintileColors = {
    Q1: { bg: 'bg-green-900', border: 'border-green-500', text: 'text-green-400', emoji: '🟢' },
    Q2: { bg: 'bg-blue-900', border: 'border-blue-500', text: 'text-blue-400', emoji: '🔵' },
    Q3: { bg: 'bg-yellow-900', border: 'border-yellow-500', text: 'text-yellow-400', emoji: '🟡' },
    Q4: { bg: 'bg-orange-900', border: 'border-orange-500', text: 'text-orange-400', emoji: '🟠' },
    Q5: { bg: 'bg-red-900', border: 'border-red-500', text: 'text-red-400', emoji: '🔴' }
  };

  const handleSetOpen = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/quintile-valleys/set-open?session_id=admin_test_123`, {
        currency_pair: adminForm.currency_pair,
        open_type: adminForm.open_type,
        price: parseFloat(adminForm.price),
        date: adminForm.date || undefined
      });
      toast.success(`Set ${adminForm.currency_pair} ${adminForm.open_type} open to ${adminForm.price}`);
      setAdminForm({ ...adminForm, price: '', date: '' });
    } catch (err) {
      console.error('Failed to set open:', err);
      toast.error('Failed to set open price');
    }
  };

  const formatPrice = (price, pair) => {
    if (!price) return 'Not Set';
    return pair === 'USDJPY' ? price.toFixed(3) : price.toFixed(5);
  };

  const formatPipsFromOpen = (pips, openType) => {
    if (pips == null) return '';
    const absPips = Math.abs(pips);
    const direction = pips > 0 ? 'below' : 'above';
    return ` (${absPips} pips ${direction} ${openType})`;
  };

  const renderValleyTable = (valleys, title, pair) => {
    if (!valleys || valleys.length === 0) {
      return (
        <div className="text-gray-400 text-sm text-center py-4">
          {title} not available - Open price not set
        </div>
      );
    }

    return (
      <div className="mb-4">
        <h4 className="text-white font-semibold mb-2 text-sm">{title}</h4>
        <div className="space-y-2">
          {valleys.map((valley) => {
            const colors = quintileColors[valley.quintile];
            return (
              <div 
                key={valley.quintile} 
                className={`${colors.bg} bg-opacity-30 border ${colors.border} rounded-lg p-3`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span>{colors.emoji}</span>
                    <span className={`${colors.text} font-semibold`}>
                      {valley.quintile} — {valley.name}
                    </span>
                  </div>
                  <span className={`text-xs ${valley.in_valley ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                    {valley.status}
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-400">
                    {formatPrice(valley.valley_price, pair)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2 text-white">Under Value Valleys</h2>
          <p className="text-gray-400 text-sm"></p>
        </div>

        {/* Two Column Layout for EURUSD and USDJPY */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* EURUSD Column */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-bold text-white mb-3 text-center">EURUSD</h3>
            
            {quintileValleysData.pairs?.EURUSD && (
              <>
                <div className="text-center mb-3 text-sm">
                  <p className="text-gray-300">
                    Current: <span className="text-white font-bold">{formatPrice(quintileValleysData.pairs.EURUSD.current_price, 'EURUSD')}</span>
                  </p>
                </div>
                
                {renderValleyTable(quintileValleysData.pairs.EURUSD.year_open_valleys, "— This Year's Valleys —", 'EURUSD')}
                {renderValleyTable(quintileValleysData.pairs.EURUSD.monthly_open_valleys, "— This Month's Valleys —", 'EURUSD')}
              </>
            )}
          </div>

          {/* USDJPY Column */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-bold text-white mb-3 text-center">USDJPY</h3>
            
            {quintileValleysData.pairs?.USDJPY && (
              <>
                <div className="text-center mb-3 text-sm">
                  <p className="text-gray-300">
                    Current: <span className="text-white font-bold">{formatPrice(quintileValleysData.pairs.USDJPY.current_price, 'USDJPY')}</span>
                  </p>
                </div>
                
                {renderValleyTable(quintileValleysData.pairs.USDJPY.year_open_valleys, "— This Year's Valleys —", 'USDJPY')}
                {renderValleyTable(quintileValleysData.pairs.USDJPY.monthly_open_valleys, "— This Month's Valleys —", 'USDJPY')}
              </>
            )}
          </div>
        </div>

        {/* Admin Controls */}
        {adminMode && (
          <div className="mt-4">
            <button
              onClick={() => setShowAdminForm(!showAdminForm)}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-sm font-medium"
            >
              {showAdminForm ? '✕ Cancel' : '⚙️ Set Open Prices'}
            </button>
            
            {showAdminForm && (
              <form onSubmit={handleSetOpen} className="mt-3 bg-gray-700 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Currency Pair</label>
                    <select
                      value={adminForm.currency_pair}
                      onChange={(e) => setAdminForm({...adminForm, currency_pair: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="EURUSD">EURUSD</option>
                      <option value="USDJPY">USDJPY</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Open Type</label>
                    <select
                      value={adminForm.open_type}
                      onChange={(e) => setAdminForm({...adminForm, open_type: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="year">Year Open</option>
                      <option value="monthly">Monthly Open</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Price</label>
                    <input
                      type="number"
                      step="0.00001"
                      value={adminForm.price}
                      onChange={(e) => setAdminForm({...adminForm, price: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      placeholder="1.10380"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Date (optional)</label>
                    <input
                      type="date"
                      value={adminForm.date}
                      onChange={(e) => setAdminForm({...adminForm, date: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium text-sm"
                >
                  Save Open Price
                </button>
              </form>
            )}

            <button
              onClick={async () => {
                if (!showMonthlyLevels) {
                  try {
                    const res = await axios.get(`${API}/admin/quintile-valleys/monthly-levels/${monthlyLevelsPair}?session_id=admin_test_123`);
                    const levels = res.data.levels || {};
                    setMonthlyLevelsForm({
                      Q1: levels.Q1?.toString() || '',
                      Q2: levels.Q2?.toString() || '',
                      Q3: levels.Q3?.toString() || '',
                      Q4: levels.Q4?.toString() || '',
                      Q5: levels.Q5?.toString() || ''
                    });
                  } catch (err) {
                    console.error('Failed to load monthly levels');
                  }
                }
                setShowMonthlyLevels(!showMonthlyLevels);
              }}
              className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium"
              data-testid="set-monthly-levels-btn"
            >
              {showMonthlyLevels ? 'Cancel' : 'Set Monthly Valley Levels'}
            </button>

            {showMonthlyLevels && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.post(`${API}/admin/quintile-valleys/set-monthly-levels?session_id=admin_test_123`, {
                    currency_pair: monthlyLevelsPair,
                    Q1: parseFloat(monthlyLevelsForm.Q1),
                    Q2: parseFloat(monthlyLevelsForm.Q2),
                    Q3: parseFloat(monthlyLevelsForm.Q3),
                    Q4: parseFloat(monthlyLevelsForm.Q4),
                    Q5: parseFloat(monthlyLevelsForm.Q5)
                  });
                  toast.success(`Monthly valley levels saved for ${monthlyLevelsPair}`);
                  setShowMonthlyLevels(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to save monthly levels');
                }
              }} className="mt-3 bg-gray-700 rounded-lg p-4 space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Currency Pair</label>
                  <select
                    value={monthlyLevelsPair}
                    onChange={async (e) => {
                      const pair = e.target.value;
                      setMonthlyLevelsPair(pair);
                      try {
                        const res = await axios.get(`${API}/admin/quintile-valleys/monthly-levels/${pair}?session_id=admin_test_123`);
                        const levels = res.data.levels || {};
                        setMonthlyLevelsForm({
                          Q1: levels.Q1?.toString() || '',
                          Q2: levels.Q2?.toString() || '',
                          Q3: levels.Q3?.toString() || '',
                          Q4: levels.Q4?.toString() || '',
                          Q5: levels.Q5?.toString() || ''
                        });
                      } catch (err) {
                        console.error('Failed to load monthly levels');
                      }
                    }}
                    className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    data-testid="monthly-levels-pair-select"
                  >
                    <option value="EURUSD">EURUSD</option>
                    <option value="USDJPY">USDJPY</option>
                  </select>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: 'Q1', label: 'Shallow', color: 'text-green-400' },
                    { key: 'Q2', label: 'Below Shallow', color: 'text-blue-400' },
                    { key: 'Q3', label: 'Middle', color: 'text-yellow-400' },
                    { key: 'Q4', label: 'Below Mid', color: 'text-orange-400' },
                    { key: 'Q5', label: 'Deep', color: 'text-red-400' }
                  ].map(({ key, label, color }) => (
                    <div key={key}>
                      <label className={`block text-xs ${color} mb-1 text-center`}>{label}</label>
                      <input
                        type="number"
                        value={monthlyLevelsForm[key]}
                        onChange={(e) => setMonthlyLevelsForm({ ...monthlyLevelsForm, [key]: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm text-center"
                        placeholder="pips"
                        required
                        data-testid={`monthly-level-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium text-sm"
                  data-testid="save-monthly-levels-btn"
                >
                  Save Monthly Levels
                </button>
              </form>
            )}
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowQuintileValleys(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
          >
            Close
          </button>
        </div>

        {/* Timestamp */}
        <div className="text-center text-xs text-gray-500 mt-3">
          Last updated: {quintileValleysData.timestamp ? new Date(quintileValleysData.timestamp).toLocaleString() : 'N/A'}
        </div>
      </div>
    </div>
  );
};

// UnderValue Valleys Modal Component (Legacy - kept for backward compatibility)
const UndervaluedValleysModal = ({ undervaluedValleysData, setShowUndervaluedValleys, currencyPair }) => {
  if (!undervaluedValleysData) return null;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return null;
    if (typeof price !== 'number') return price;
    return currencyPair.includes('JPY') ? price.toFixed(3) : price.toFixed(5);
  };

  const formatRange = (high, low) => {
    const highFormatted = formatPrice(high);
    const lowFormatted = formatPrice(low);
    
    if (!highFormatted && !lowFormatted) return 'Not Set';
    if (highFormatted && lowFormatted) return `${highFormatted} - ${lowFormatted}`;
    if (highFormatted) return highFormatted;
    return lowFormatted;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">📉 Quintile Valleys</h2>
          <p className="text-gray-300 text-lg font-semibold">
            {currencyPair}
          </p>
        </div>

        <div className="space-y-4">
          {/* Shallow Valley */}
          <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-4">
            <div className="text-green-300 font-semibold text-lg">
              Shallow Valley = {formatRange(undervaluedValleysData.shallow_valley_high, undervaluedValleysData.shallow_valley_low)}
            </div>
          </div>

          {/* Mid Valley */}
          <div className="bg-green-900 bg-opacity-40 border border-green-400 rounded-lg p-4">
            <div className="text-green-200 font-semibold text-lg">
              Mid Valley = {formatRange(undervaluedValleysData.mid_valley_high, undervaluedValleysData.mid_valley_low)}
            </div>
          </div>

          {/* Deep Valley */}
          <div className="bg-green-900 bg-opacity-50 border border-green-300 rounded-lg p-4">
            <div className="text-green-100 font-semibold text-lg">
              Deep Valley = {formatRange(undervaluedValleysData.deep_valley_high, undervaluedValleysData.deep_valley_low)}
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setShowUndervaluedValleys(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>

        {/* Timestamp */}
        {undervaluedValleysData.timestamp && (
          <div className="text-center text-xs text-gray-500 mt-4">
            Last updated: {undervaluedValleysData.timestamp}
          </div>
        )}
      </div>
    </div>
  );
};




// Logo Zoom Modal Component
const LogoZoomModal = ({ setShowLogoZoom }) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={() => setShowLogoZoom(false)}
    >
      <div 
        className="relative max-w-4xl max-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src="/random-forest-diagram.png" 
          alt="Random Forest Algorithm - Enlarged View" 
          className="max-w-full max-h-full object-contain rounded-lg"
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowLogoZoom(false);
          }}
          className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold transition-all duration-200"
          title="Close"
        >
          ×
        </button>
        <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-3 py-2 rounded-lg text-sm">
          Random Forest Algorithm Visualization
        </div>
      </div>
    </div>
  );
};

// Free Access Notification Signup Modal Component
const FreeAccessSignupModal = ({ setShowFreeAccessSignup }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubmitting(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/signup/free-access-notification`, null, {
        params: { email: email }
      });
      setResult({ success: true, message: response.data.message });
      setEmail('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to sign up. Please try again.';
      setResult({ success: false, message: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-4xl mb-3"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Free Access Notification</h2>
          <p className="text-gray-300 text-sm">
            Sign up to be notified when our random Full Free Access day is announced each month!
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          {result && (
            <div className={`mb-4 p-3 rounded-lg text-center ${
              result.success ? 'bg-green-900 bg-opacity-50 text-green-300' : 'bg-red-900 bg-opacity-50 text-red-300'
            }`}>
              {result.message}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={submitting || !email}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              {submitting ? 'Signing up...' : 'Sign Up'}
            </button>
            <button
              type="button"
              onClick={() => setShowFreeAccessSignup(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          We'll only email you about Free Access days. No spam, ever.
        </p>
      </div>
    </div>
  );
};

// Correlation Tables Modal Component (Public View)
const CorrelationTablesModal = ({ setShowCorrelationTables, correlationData, adminMode, setShowCorrelationAdmin }) => {
  const [data, setData] = useState(correlationData);
  const [loading, setLoading] = useState(!correlationData);

  useEffect(() => {
    if (!correlationData) {
      fetchCorrelationData();
    }
  }, []);

  const fetchCorrelationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/forex/correlation-tables`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching correlation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const oneWeekPairs = data?.one_week || [];
  const oneMonthPairs = data?.one_month || [];

  const getCorrelationColor = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 90) return 'text-red-400';
    if (absValue >= 80) return 'text-orange-400';
    if (absValue >= 70) return 'text-yellow-400';
    return 'text-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Correlation Tables</h2>
          <p className="text-gray-400 text-sm">Highly Negatively Correlated Pairs (-80% or greater)</p>
          <p className="text-gray-500 text-xs mt-1">Source: myfxbook.com</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 1 Week Correlations */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-cyan-400 mb-4 text-center border-b border-gray-600 pb-2">
                1 Week Correlations
              </h3>
              {oneWeekPairs.length > 0 ? (
                <div className="space-y-3">
                  {oneWeekPairs.map((pair, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-medium">{pair.pair1}</span>
                        <span className="text-gray-500">↔</span>
                        <span className="text-white font-medium">{pair.pair2}</span>
                      </div>
                      <span className={`font-bold ${getCorrelationColor(pair.correlation)}`}>
                        {pair.correlation}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>

            {/* 1 Month Correlations */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-400 mb-4 text-center border-b border-gray-600 pb-2">
                1 Month Correlations
              </h3>
              {oneMonthPairs.length > 0 ? (
                <div className="space-y-3">
                  {oneMonthPairs.map((pair, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-medium">{pair.pair1}</span>
                        <span className="text-gray-500">↔</span>
                        <span className="text-white font-medium">{pair.pair2}</span>
                      </div>
                      <span className={`font-bold ${getCorrelationColor(pair.correlation)}`}>
                        {pair.correlation}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-2">Correlation Strength:</h4>
          <div className="flex flex-wrap gap-4 text-xs">
            <span className="text-red-400">● -90% or stronger (Very High)</span>
            <span className="text-orange-400">● -80% to -89% (High)</span>
            <span className="text-yellow-400">● -70% to -79% (Moderate)</span>
          </div>
        </div>

        {/* Last Updated */}
        {data?.updated_at && (
          <p className="text-xs text-gray-500 text-center mt-4">
            Last updated: {new Date(data.updated_at).toLocaleString()}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-6">
          {adminMode && (
            <button
              onClick={() => {
                setShowCorrelationTables(false);
                setShowCorrelationAdmin(true);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Edit Correlations
            </button>
          )}
          <button
            onClick={() => setShowCorrelationTables(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Correlation Tables Admin Modal Component
const CorrelationAdminModal = ({ setShowCorrelationAdmin }) => {
  const [oneWeekPairs, setOneWeekPairs] = useState([
    { pair1: 'EURUSD', pair2: 'USDCHF', correlation: -92 }
  ]);
  const [oneMonthPairs, setOneMonthPairs] = useState([
    { pair1: 'EURUSD', pair2: 'USDCHF', correlation: -89 }
  ]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const validPairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'];

  useEffect(() => {
    fetchCurrentData();
  }, []);

  const fetchCurrentData = async () => {
    try {
      const response = await axios.get(`${API}/forex/correlation-tables`);
      if (response.data?.one_week?.length > 0) {
        setOneWeekPairs(response.data.one_week);
      }
      if (response.data?.one_month?.length > 0) {
        setOneMonthPairs(response.data.one_month);
      }
    } catch (error) {
      console.error('Error fetching correlation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPair = (weekOrMonth) => {
    const newPair = { pair1: 'EURUSD', pair2: 'USDCHF', correlation: -80 };
    if (weekOrMonth === 'week') {
      setOneWeekPairs([...oneWeekPairs, newPair]);
    } else {
      setOneMonthPairs([...oneMonthPairs, newPair]);
    }
  };

  const removePair = (weekOrMonth, index) => {
    if (weekOrMonth === 'week') {
      setOneWeekPairs(oneWeekPairs.filter((_, i) => i !== index));
    } else {
      setOneMonthPairs(oneMonthPairs.filter((_, i) => i !== index));
    }
  };

  const updatePair = (weekOrMonth, index, field, value) => {
    if (weekOrMonth === 'week') {
      const updated = [...oneWeekPairs];
      updated[index][field] = field === 'correlation' ? parseInt(value) : value;
      setOneWeekPairs(updated);
    } else {
      const updated = [...oneMonthPairs];
      updated[index][field] = field === 'correlation' ? parseInt(value) : value;
      setOneMonthPairs(updated);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
      await axios.post(`${API}/admin/correlation-tables`, {
        one_week: oneWeekPairs,
        one_month: oneMonthPairs
      }, {
        params: { session_id: sessionId }
      });
      alert('Correlation tables saved successfully!');
      setShowCorrelationAdmin(false);
    } catch (error) {
      console.error('Error saving correlation data:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderPairInputs = (pairs, weekOrMonth) => (
    <div className="space-y-3">
      {pairs.map((pair, idx) => (
        <div key={idx} className="flex items-center gap-2 bg-gray-800 p-3 rounded-lg">
          <select
            value={pair.pair1}
            onChange={(e) => updatePair(weekOrMonth, idx, 'pair1', e.target.value)}
            className="bg-gray-700 text-white rounded px-2 py-1 text-sm"
          >
            {validPairs.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span className="text-gray-500">↔</span>
          <select
            value={pair.pair2}
            onChange={(e) => updatePair(weekOrMonth, idx, 'pair2', e.target.value)}
            className="bg-gray-700 text-white rounded px-2 py-1 text-sm"
          >
            {validPairs.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input
            type="number"
            value={pair.correlation}
            onChange={(e) => updatePair(weekOrMonth, idx, 'correlation', e.target.value)}
            className="bg-gray-700 text-white rounded px-2 py-1 w-20 text-sm"
            min="-100"
            max="0"
          />
          <span className="text-gray-400 text-sm">%</span>
          <button
            onClick={() => removePair(weekOrMonth, idx)}
            className="text-red-400 hover:text-red-300 ml-2"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={() => addPair(weekOrMonth)}
        className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
      >
        + Add Pair
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Correlation Tables Admin</h2>
          <p className="text-gray-400 text-sm">Enter highly negatively correlated pairs (-80% or greater)</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 1 Week */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-cyan-400 mb-4 text-center border-b border-gray-600 pb-2">
                1 Week Correlations
              </h3>
              {renderPairInputs(oneWeekPairs, 'week')}
            </div>

            {/* 1 Month */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-400 mb-4 text-center border-b border-gray-600 pb-2">
                1 Month Correlations
              </h3>
              {renderPairInputs(oneMonthPairs, 'month')}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            {saving ? 'Saving...' : 'Save Correlations'}
          </button>
          <button
            onClick={() => setShowCorrelationAdmin(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Peaks and Valleys Admin Modal Component
const PeaksValleysAdminModal = ({ peaksValleys, setShowPeaksValleysAdmin, setPeaksValleys }) => {
  const inputRefs = useRef({});
  
  const currencyPairs = ['EURUSD', 'GBPUSD', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD', 'USDJPY'];

  const handleUpdatePeaksValleys = async () => {
    const sessionId = localStorage.getItem('sessionId') || 'admin_test_123';
    const updates = {};
    
    currencyPairs.forEach(pair => {
      const distantBearPeak = inputRefs.current[`${pair}_distant_bear`]?.value;
      const nearBearPeak = inputRefs.current[`${pair}_near_bear`]?.value;
      const nearBullValley = inputRefs.current[`${pair}_near_bull`]?.value;
      const distantBullValley = inputRefs.current[`${pair}_distant_bull`]?.value;
      
      if (distantBearPeak || nearBearPeak || nearBullValley || distantBullValley) {
        updates[pair] = {
          distant_bear_peak: distantBearPeak ? parseFloat(distantBearPeak) : null,
          near_bear_peak: nearBearPeak ? parseFloat(nearBearPeak) : null,
          near_bull_valley: nearBullValley ? parseFloat(nearBullValley) : null,
          distant_bull_valley: distantBullValley ? parseFloat(distantBullValley) : null
        };
      }
    });

    try {
      const response = await axios.post(`${API}/admin/peaks-valleys?session_id=${sessionId}`, {
        peaks_valleys: updates
      });
      
      console.log('Peaks and valleys updated successfully:', response.data);
      setPeaksValleys(response.data.peaks_valleys || {});
      setShowPeaksValleysAdmin(false);
    } catch (error) {
      console.error('Failed to update peaks and valleys:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Updates being sent:', updates);
      alert(`Failed to update peaks and valleys. Error: ${error.response?.data?.detail || error.message}. Please check console for details.`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">🏔️ Peaks & Valleys Admin</h2>
          <p className="text-gray-300 text-sm">
            Manually input the peaks and valleys prices
          </p>
        </div>

        {/* Currency Pair Input Grid */}
        <div className="space-y-6 mb-6">
          {currencyPairs.map(pair => (
            <div key={pair} className="bg-gray-700 p-4 rounded-lg">
              <h3 className="text-white text-lg font-semibold mb-4">{pair}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Distant Bear Peak */}
                <div>
                  <label className="block text-red-300 text-sm font-medium mb-2">
                    Distant Bear Peak
                  </label>
                  <input
                    ref={(el) => (inputRefs.current[`${pair}_distant_bear`] = el)}
                    type="number"
                    step="any"
                    defaultValue={peaksValleys[pair]?.distant_bear_peak || ''}
                    placeholder="Enter price"
                    className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-red-500 focus:outline-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>

                {/* Near Bear Peak */}
                <div>
                  <label className="block text-red-300 text-sm font-medium mb-2">
                    Near Bear Peak
                  </label>
                  <input
                    ref={(el) => (inputRefs.current[`${pair}_near_bear`] = el)}
                    type="number"
                    step="any"
                    defaultValue={peaksValleys[pair]?.near_bear_peak || ''}
                    placeholder="Enter price"
                    className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-red-500 focus:outline-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>

                {/* Near Bull Valley */}
                <div>
                  <label className="block text-green-300 text-sm font-medium mb-2">
                    Near Bull Valley
                  </label>
                  <input
                    ref={(el) => (inputRefs.current[`${pair}_near_bull`] = el)}
                    type="number"
                    step="any"
                    defaultValue={peaksValleys[pair]?.near_bull_valley || ''}
                    placeholder="Enter price"
                    className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>

                {/* Distant Bull Valley */}
                <div>
                  <label className="block text-green-300 text-sm font-medium mb-2">
                    Distant Bull Valley
                  </label>
                  <input
                    ref={(el) => (inputRefs.current[`${pair}_distant_bull`] = el)}
                    type="number"
                    step="any"
                    defaultValue={peaksValleys[pair]?.distant_bull_valley || ''}
                    placeholder="Enter price"
                    className="w-full px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>

              {/* Current Values Display */}
              {peaksValleys[pair] && (
                <div className="mt-3 text-xs text-gray-400 grid grid-cols-2 gap-2">
                  <div>Current Values:</div>
                  <div>Updated: {peaksValleys[pair].timestamp}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={handleUpdatePeaksValleys}
            className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white text-sm font-medium transition-colors duration-200"
          >
            Update Peaks & Valleys
          </button>
        </div>

        {/* Usage Notes */}
        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4 mb-4">
          <h4 className="text-gray-200 font-semibold mb-2">Usage Notes:</h4>
          <div className="text-xs text-gray-300 space-y-1">
            <p>• Enter exact prices for each peak and valley level</p>
            <p>• These prices will be displayed directly in the UnderValue Valleys modal</p>
            <p>• JPY pairs use 3 decimal places, standard pairs use 5 decimal places</p>
            <p>• Changes take effect immediately for the UnderValue Valleys display</p>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowPeaksValleysAdmin(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// EURUSD 48 Hour Forecast Modal Component
// EURUSD 2-Day Cycle Forecast Modal Component
// History Modal for viewing/managing forecast history
const HistoryModal = ({ currencyPair, onClose, onRefresh }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    direction: 'DOWN',
    endPips: ''
  });

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/compass-history/${currencyPair}?session_id=admin_test_123`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [currencyPair]);

  const handleAddHistory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/compass-history/${currencyPair}?session_id=admin_test_123`, {
        direction: formData.direction,
        end_pips: parseFloat(formData.endPips)
      });
      toast.success('Historical forecast added!');
      setShowAddForm(false);
      setFormData({
        direction: 'DOWN',
        endPips: ''
      });
      fetchHistory();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to add history:', err);
      toast.error('Failed to add forecast');
    }
  };

  const handleRecalculateStats = async () => {
    try {
      await axios.post(`${API}/admin/compass-recalculate-stats/${currencyPair}?session_id=admin_test_123`);
      toast.success('Stats recalculated from history!');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to recalculate stats');
    }
  };

  const handleDeleteHistory = async (entryId) => {
    if (!window.confirm('Delete this historical forecast?')) return;
    try {
      await axios.delete(`${API}/admin/compass-history/${currencyPair}/${entryId}?session_id=admin_test_123`);
      toast.success('History entry deleted');
      fetchHistory();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete');
    }
  };

  const handleResetHistory = async () => {
    if (!window.confirm(`Are you sure you want to delete ALL ${currencyPair} forecast history? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/admin/2day-cycle-reset/${currencyPair}?session_id=admin_test_123&clear_history=true`);
      toast.success(`${currencyPair} history cleared!`);
      fetchHistory();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to reset history:', err);
      toast.error('Failed to clear history');
    }
  };

  const getStatusFromEntry = (h) => {
    return h.end_pips >= 0 ? 'positive' : 'negative';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
        <h2 className="text-xl font-bold text-white mb-4 text-center" data-testid="history-modal-title">
          📜 {currencyPair} Compass Forecast History
        </h2>

        {/* Action Buttons */}
        <div className="flex justify-center gap-3 mb-4 flex-wrap">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
            data-testid="add-history-btn"
          >
            {showAddForm ? '✕ Cancel' : '➕ Add History'}
          </button>
          <button
            onClick={handleRecalculateStats}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
            data-testid="recalculate-stats-btn"
          >
            🔄 Recalculate Stats
          </button>
          <button
            onClick={handleResetHistory}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm"
            data-testid="reset-history-btn"
          >
            🗑️ Reset History
          </button>
          <button
            onClick={fetchHistory}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
            data-testid="refresh-history-btn"
          >
            🔃 Refresh
          </button>
        </div>

        {/* Add History Form - Simplified to just Direction and Pip Result */}
        {showAddForm && (
          <form onSubmit={handleAddHistory} className="bg-gray-700 rounded-lg p-4 mb-4 space-y-3" data-testid="add-history-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Direction</label>
                <select
                  value={formData.direction}
                  onChange={(e) => setFormData({...formData, direction: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                  data-testid="direction-select"
                >
                  <option value="UP">UP</option>
                  <option value="DOWN">DOWN</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Pip Result (+/-)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.endPips}
                  onChange={(e) => setFormData({...formData, endPips: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                  placeholder="120 or -40"
                  required
                  data-testid="end-pips"
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
              data-testid="submit-history-btn"
            >
              Add to History
            </button>
          </form>
        )}

        {/* History List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-400" data-testid="no-history-message">
            No historical forecasts found
          </div>
        ) : (
          <div className="space-y-2" data-testid="history-list">
            {history.map((h, idx) => {
              const status = getStatusFromEntry(h);
              return (
                <div 
                  key={h._id || idx} 
                  className={`p-3 rounded-lg border ${status === 'positive' ? 'border-green-600 bg-green-900 bg-opacity-20' : 'border-red-600 bg-red-900 bg-opacity-20'}`}
                  data-testid={`history-entry-${idx}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className={`font-bold ${h.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                        {h.direction}
                      </span>
                      <span className="text-gray-400 text-sm ml-2">
                        {h.added_time ? new Date(h.added_time).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${status === 'positive' ? 'text-green-400' : 'text-red-400'}`}>
                        {h.end_pips > 0 ? '+' : ''}{h.end_pips?.toFixed(1) || 0} pips
                      </span>
                      <button
                        onClick={() => handleDeleteHistory(h._id)}
                        className="text-red-400 hover:text-red-300 text-xs"
                        title="Delete entry"
                        data-testid={`delete-entry-${idx}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-center mt-4">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
            data-testid="close-history-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const DayForecastModal = ({ dayForecastData, setShowDayForecast, currencyPair, adminMode }) => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showSetDailyOpen, setShowSetDailyOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEtaSetup, setShowEtaSetup] = useState(false);
  const [showZetaSetup, setShowZetaSetup] = useState(false);
  const [etaForm, setEtaForm] = useState({ open_price: '', up_trigger_pips: '', down_trigger_pips: '', target_pips: '', failure_pips: '', signal_direction: 'BOTH' });
  const [zetaForm, setZetaForm] = useState({ up_trigger_price: '', up_trigger_pips: '', down_trigger_price: '', down_trigger_pips: '', target_pips: '', failure_pips: '', signal_direction: 'BOTH' });
  const [showStatsDateEdit, setShowStatsDateEdit] = useState(false);
  const [statsDateInput, setStatsDateInput] = useState('');
  const [showZetaMarket, setShowZetaMarket] = useState(false);
  const [zetaMarketForm, setZetaMarketForm] = useState({ direction: 'UP', target_pips: '', stop_pips: '' });
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [autoConfig, setAutoConfig] = useState(null);

  const fetchAutoConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/auto-config/EURUSD?session_id=admin_test_123`);
      setAutoConfig(res.data.config);
    } catch (err) { console.error('Failed to load auto config'); }
  };

  const saveAutoConfig = async (cfg) => {
    try {
      await axios.post(`${API}/admin/auto-config/EURUSD?session_id=admin_test_123`, cfg);
      setAutoConfig(cfg);
      toast.success(`Auto mode ${cfg.enabled ? 'enabled' : 'disabled'} for EURUSD`);
      fetchForecast(false);
    } catch (err) { toast.error('Failed to save auto config'); }
  };

  const fetchForecast = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`${API}/forex/2day-cycle-forecast/EURUSD`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() }
      });
      setForecast(response.data?.forecast);
      if (response.data?.forecast?.stats_start_date) {
        setStatsDateInput(response.data.forecast.stats_start_date);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch EURUSD forecast:', err);
      setError('Failed to load forecast data');
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchForecast();
    const intervalId = setInterval(() => fetchForecast(false), 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchForecast(false);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)} at ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white">EURUSD Random Forest Cycles</h2>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="mt-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors"
          >
            {refreshing ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900 bg-opacity-30 rounded-lg p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : forecast ? (
          <div className="space-y-4">
            {/* Direction Only Mode - EURUSD */}
            {forecast.direction_only ? (
              <div className="space-y-4">
                {(forecast.cycle_direction === 'UP' || forecast.cycle_direction === 'DOWN') ? (
                <div className={`rounded-lg p-6 border-2 text-center ${
                  forecast.cycle_direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-30' :
                  'border-red-500 bg-red-900 bg-opacity-30'
                }`}>
                  <p className={`text-2xl font-bold ${
                    forecast.cycle_direction === 'UP' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {forecast.cycle_direction === 'UP' ? '<1 Day UP Cycle' : '<1 Day Down Cycle'}
                  </p>
                  {forecast.cycle_since && (
                    <p className="text-gray-400 text-sm mt-2">
                      since {formatDateTime(forecast.cycle_since)}
                    </p>
                  )}
                  {forecast.cycle_entry_price && forecast.current_price && (() => {
                    const pipSize = 'EURUSD'.includes('JPY') ? 0.01 : 0.0001;
                    const rawPips = (forecast.current_price - forecast.cycle_entry_price) / pipSize;
                    const isDown = rawPips < 0;
                    const absPips = Math.abs(rawPips).toFixed(1);
                    const dirLabel = forecast.cycle_direction === 'DOWN' ? 'Down Cycle' : 'Up Cycle';
                    return (
                      <p className={`text-sm mt-2 font-semibold ${isDown ? 'text-red-400' : 'text-green-400'}`}>
                        {isDown ? 'Down' : 'Up'} {absPips} pips since start of {dirLabel} Forecast
                      </p>
                    );
                  })()}
                </div>
                ) : (
                <div className="rounded-lg p-6 border-2 border-gray-600 bg-gray-800 bg-opacity-50 text-center">
                  <p className="text-gray-400 text-lg">No Active Cycle</p>
                </div>
                )}
              </div>
            ) : (
            <>
            {/* Full Forecast Mode */}
            {/* AUTOMATIC Forecast */}
            <p className="text-white text-sm tracking-wide font-bold">Scylla Cycle</p>
            {forecast.active_forecasts && forecast.active_forecasts.length > 0 ? (
              <div className={`rounded-lg p-4 border-2 ${forecast.active_forecasts[0].direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-30' : 'border-red-500 bg-red-900 bg-opacity-30'}`}>
                <p className="text-white text-sm leading-relaxed">
                  <span className="text-gray-400">{formatDateTime(forecast.active_forecasts[0].entry_time)}</span>
                  {' '}Cycle Forecasted to start{' '}
                  <span className={forecast.active_forecasts[0].direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                    {forecast.active_forecasts[0].direction}
                  </span>
                  {' '}from{' '}
                  <span className="text-white font-mono font-bold">{forecast.active_forecasts[0].entry_price?.toFixed(5)}</span>
                  {' '}towards{' '}
                  <span className="text-white font-mono font-bold">{forecast.active_forecasts[0].target_price?.toFixed(5)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  This will be a Forecast Failure if instead the price {forecast.active_forecasts[0].direction === 'UP' ? 'falls' : 'rises'} to{' '}
                  <span className="text-red-400 font-mono">{forecast.active_forecasts[0].failure_price?.toFixed(5)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  Since the start of this forecast the price has moved{' '}
                  <span className="font-bold">{Math.abs(forecast.active_forecasts[0].progress_pips || 0).toFixed(1)}</span> pips{' '}
                  {(forecast.active_forecasts[0].progress_pips || 0) >= 0 ? (
                    <span className="text-green-400">in the direction of the Forecast</span>
                  ) : (
                    <span className="text-red-400">in the opposite direction of the Forecast</span>
                  )}
                </p>
              </div>
            ) : forecast.pending_forecasts && forecast.pending_forecasts.length > 0 ? (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-sky-400 text-sm mb-2">Scylla Pending — Waiting for price to trigger:</p>
                {forecast.pending_forecasts.map((pf, idx) => (
                  <p key={idx} className={`text-sm ${pf.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                    {pf.direction} @ <span className="font-mono">{pf.entry_price?.toFixed(5)}</span>
                    {' → '}Target: <span className="font-mono">{pf.target_price?.toFixed(5)}</span>, Failure: <span className="font-mono">{pf.failure_price?.toFixed(5)}</span>
                  </p>
                ))}
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sky-400">Waiting on next Scylla Cycle</p>
              </div>
            )}

            {/* MANUAL Forecast */}
            <p className="text-white text-sm tracking-wide mt-4 font-bold">Charybdis Cycle</p>
            {forecast.manual_forecast ? (
              <div className={`rounded-lg p-4 border-2 ${forecast.manual_forecast.direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-20' : 'border-red-500 bg-red-900 bg-opacity-20'}`}>
                <p className="text-white text-sm leading-relaxed">
                  <span className="text-gray-400">{formatDateTime(forecast.manual_forecast.entry_time)}</span>
                  {' '}Cycle Forecasted to start{' '}
                  <span className={forecast.manual_forecast.direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                    {forecast.manual_forecast.direction}
                  </span>
                  {' '}from{' '}
                  <span className="text-white font-mono font-bold">{forecast.manual_forecast.entry_price?.toFixed(5)}</span>
                  {' '}towards{' '}
                  <span className="text-white font-mono font-bold">{forecast.manual_forecast.target_price?.toFixed(5)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  This will be a Forecast Failure if instead the price {forecast.manual_forecast.direction === 'UP' ? 'falls' : 'rises'} to{' '}
                  <span className="text-red-400 font-mono">{forecast.manual_forecast.failure_price?.toFixed(5)}</span>
                  <span className="text-gray-500 text-xs ml-2">(fixed)</span>
                </p>
                <p className="text-white text-sm mt-2">
                  Since the start of this forecast the price has moved{' '}
                  <span className="font-bold">{Math.abs(forecast.manual_forecast.progress_pips || 0).toFixed(1)}</span> pips{' '}
                  {(forecast.manual_forecast.progress_pips || 0) >= 0 ? (
                    <span className="text-green-400">in the direction of the Forecast</span>
                  ) : (
                    <span className="text-red-400">in the opposite direction of the Forecast</span>
                  )}
                </p>
              </div>
            ) : forecast.zeta_pending_up || forecast.zeta_pending_down ? (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-sky-400 text-sm mb-2">Charybdis Pending — Waiting for price to trigger:</p>
                {forecast.zeta_pending_up && (
                  <p className="text-sm text-green-400">
                    UP @ <span className="font-mono">{forecast.zeta_pending_up.entry_price?.toFixed(5)}</span>
                    {' → '}Target: <span className="font-mono">{forecast.zeta_pending_up.target_price?.toFixed(5)}</span>, Failure: <span className="font-mono">{forecast.zeta_pending_up.failure_price?.toFixed(5)}</span>
                  </p>
                )}
                {forecast.zeta_pending_down && (
                  <p className="text-sm text-red-400">
                    DOWN @ <span className="font-mono">{forecast.zeta_pending_down.entry_price?.toFixed(5)}</span>
                    {' → '}Target: <span className="font-mono">{forecast.zeta_pending_down.target_price?.toFixed(5)}</span>, Failure: <span className="font-mono">{forecast.zeta_pending_down.failure_price?.toFixed(5)}</span>
                  </p>
                )}
                <button
                  data-testid="clear-zeta-pending-eurusd"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/clear-zeta-pending/EURUSD?session_id=admin_test_123`);
                      toast.success('Charybdis pending orders cleared');
                      fetchForecast(false);
                    } catch (err) {
                      toast.error('Failed to clear Charybdis pending');
                    }
                  }}
                  className="mt-3 px-3 py-1 bg-orange-600 hover:bg-orange-500 text-white text-xs rounded"
                >
                  Clear Charybdis Pending
                </button>
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sky-400">Waiting on next Charybdis Cycle</p>
              </div>
            )}

            {/* All Previous Active Forecasts */}
            {forecast.active_forecasts && forecast.active_forecasts.length > 1 && (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-3 font-semibold">All previous Forecasts that are still Active:</p>
                <div className="space-y-3">
                  {forecast.active_forecasts.slice(1).map((af, idx) => (
                    <div key={af.id || idx} className={`rounded-lg p-3 border ${af.direction === 'UP' ? 'border-green-600 bg-green-900 bg-opacity-20' : 'border-red-600 bg-red-900 bg-opacity-20'}`}>
                      <p className="text-white text-sm">
                        <span className="text-gray-400">{formatDateTime(af.entry_time)}</span>
                        {' '}Cycle Forecasted to start{' '}
                        <span className={af.direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>{af.direction}</span>
                        {' '}from{' '}
                        <span className="font-mono">{af.entry_price?.toFixed(5)}</span>
                        {' '}towards{' '}
                        <span className="font-mono">{af.target_price?.toFixed(5)}</span>
                      </p>
                      <p className="text-white text-sm mt-1">
                        Since the start of the forecast the price has moved{' '}
                        <span className="font-bold">{Math.abs(af.progress_pips || 0).toFixed(1)}</span> pips{' '}
                        {(af.progress_pips || 0) >= 0 ? (
                          <span className="text-green-400">in the direction of the Forecast</span>
                        ) : (
                          <span className="text-red-400">in the opposite direction of the Forecast</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            
            {/* Statistics */}
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-sm font-semibold">Statistics Start Date: {forecast.stats_start_date || 'Not Set'}</p>
                {adminMode && (
                  <button
                    onClick={() => setShowStatsDateEdit(true)}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                    data-testid="eurusd-stats-date-edit-btn"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-white">Total Cycles Ending with Positive Result: <span className="text-green-400 font-bold">{forecast.stats?.positive_count || 0}</span></p>
                <p className="text-white">Total Cycles Ending with Negative Result: <span className="text-red-400 font-bold">{forecast.stats?.negative_count || 0}</span></p>
                <p className="text-white">Total Pips in Positive Result Cycles: <span className="text-green-400 font-bold">+{(forecast.stats?.positive_pips_total || 0).toFixed(1)}</span></p>
                <p className="text-white">Total Pips in Negative Result Cycles: <span className="text-red-400 font-bold">-{(forecast.stats?.negative_pips_total || 0).toFixed(1)}</span></p>
                <p className="text-white text-xs mt-3">Results shown above represent simulated or hypothetical performance. Please read our full CFTC 4.41 disclosure in the footer.</p>
              </div>
            </div>
          </>
          )}
          </div>
        ) : null}

        {/* Admin & Close Buttons */}
        <div className="flex justify-center gap-3 mt-4 flex-wrap">
          {adminMode && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
              >
                📜 History
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/eta-config/EURUSD?session_id=admin_test_123`);
                    if (res.data.eta_config) {
                      const c = res.data.eta_config;
                      setEtaForm({
                        open_price: c.open_price || '',
                        up_trigger_pips: c.up_trigger_pips || '',
                        down_trigger_pips: c.down_trigger_pips || '',
                        target_pips: c.target_pips || '',
                        failure_pips: c.failure_pips || '',
                        signal_direction: c.signal_direction || 'BOTH'
                      });
                    }
                  } catch (err) { console.log('No existing eta config'); }
                  setShowEtaSetup(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-eta-setup-btn"
              >
                EURUSD Scylla Forecast
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/eta-config/EURUSD?session_id=admin_test_123`);
                    if (res.data.zeta_config) {
                      const c = res.data.zeta_config;
                      setZetaForm({
                        up_trigger_price: c.up_trigger_price || '',
                        up_trigger_pips: c.up_trigger_pips || '',
                        down_trigger_price: c.down_trigger_price || '',
                        down_trigger_pips: c.down_trigger_pips || '',
                        target_pips: c.target_pips || '',
                        failure_pips: c.failure_pips || '',
                        signal_direction: c.signal_direction || 'BOTH'
                      });
                    }
                  } catch (err) { console.log('No existing zeta config'); }
                  setShowZetaSetup(true);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-zeta-setup-btn"
              >
                EURUSD Charybdis Forecast
              </button>
              <button
                onClick={() => setShowZetaMarket(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-zeta-market-btn"
              >
                Charybdis at Market
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/end-eta/EURUSD?session_id=admin_test_123`);
                    toast.success('EURUSD Scylla ended.');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to end Eta');
                  }
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-end-eta-btn"
              >
                End Scylla
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/end-zeta/EURUSD?session_id=admin_test_123`);
                    toast.success('EURUSD Charybdis ended.');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to end Zeta');
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-end-zeta-btn"
              >
                End Charybdis
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('Reset all EURUSD statistics to zero?')) return;
                  try {
                    await axios.post(`${API}/admin/reset-stats/EURUSD?session_id=admin_test_123`);
                    toast.success('EURUSD stats reset to zero');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to reset stats');
                  }
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-reset-stats-btn"
              >
                Reset Stats
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/2day-cycle-reset/EURUSD?session_id=admin_test_123&clear_history=false`);
                    toast.success('EURUSD Compass Price reset');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Reset failed');
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-reset-btn"
              >
                🔄 Reset
              </button>
              <button
                onClick={() => { fetchAutoConfig(); setShowAutoConfig(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="eurusd-auto-config-btn"
              >
                Auto Mode
              </button>
              <button
                onClick={async () => {
                  try {
                    const current = forecast?.direction_only || false;
                    await axios.post(`${API}/admin/direction-only/EURUSD?session_id=admin_test_123`, { enabled: !current });
                    toast.success(`Direction Only mode ${!current ? 'enabled' : 'disabled'}`);
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to toggle Direction Only');
                  }
                }}
                className={`${forecast?.direction_only ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-600 hover:bg-gray-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm`}
                data-testid="eurusd-direction-only-btn"
              >
                {forecast?.direction_only ? 'Direction Only: ON' : 'Direction Only: OFF'}
              </button>
            </>
          )}
          <button
            onClick={() => setShowDayForecast(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>

        {/* Auto Config Modal - EURUSD */}
        {showAutoConfig && autoConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-indigo-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                EURUSD Automated Mode
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-gray-300 text-sm">Automated Forecasts</label>
                  <button
                    onClick={() => setAutoConfig({ ...autoConfig, enabled: !autoConfig.enabled })}
                    className={`px-4 py-1 rounded-full text-sm font-bold ${autoConfig.enabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                    data-testid="eurusd-auto-toggle"
                  >
                    {autoConfig.enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div>
                  <label className="text-gray-300 text-sm">Cycle Direction</label>
                  <select
                    value={autoConfig.cycle || 'BULLISH'}
                    onChange={(e) => setAutoConfig({ ...autoConfig, cycle: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                    data-testid="eurusd-auto-cycle"
                  >
                    <option value="BULLISH">BULLISH (Buy only)</option>
                    <option value="BEARISH">BEARISH (Sell only)</option>
                    <option value="BOTH">BOTH (Buys and Sells)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-gray-300 text-xs">Daily Open Price (manual)</label>
                    <input type="number" step="0.00001" value={autoConfig.daily_open || ''} onChange={(e) => setAutoConfig({ ...autoConfig, daily_open: parseFloat(e.target.value) || 0 })} className="w-full bg-gray-700 border border-yellow-600 rounded px-3 py-2 text-yellow-300 mt-1 text-sm font-mono" placeholder="e.g. 1.15960" data-testid="eurusd-auto-daily-open" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Scylla Trigger (pips)</label>
                    <input type="number" value={autoConfig.eta_trigger_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, eta_trigger_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-eta-trigger" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Charybdis Dip (pips)</label>
                    <input type="number" value={autoConfig.zeta_dip_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, zeta_dip_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-zeta-dip" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Charybdis Trigger (pips)</label>
                    <input type="number" value={autoConfig.zeta_trigger_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, zeta_trigger_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-zeta-trigger" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Stop (pips)</label>
                    <input type="number" value={autoConfig.stop_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, stop_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-stop" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Target (pips)</label>
                    <input type="number" value={autoConfig.target_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, target_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-target" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Rollover Hour (UTC)</label>
                    <input type="number" min="0" max="23" value={autoConfig.rollover_hour ?? 22} onChange={(e) => setAutoConfig({ ...autoConfig, rollover_hour: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="eurusd-auto-rollover" />
                  </div>
                </div>
                {autoConfig.enabled && (
                  <div className="bg-gray-700 rounded p-3 text-xs text-gray-300">
                    <p className="text-indigo-400 font-bold mb-1">Auto Mode Active</p>
                    <p>Daily open captured at {autoConfig.rollover_hour || 22}:00 UTC. Scylla triggers {autoConfig.eta_trigger_pips}p from open. Charybdis requires {autoConfig.zeta_dip_pips}p dip then {autoConfig.zeta_trigger_pips}p recovery. Stop: {autoConfig.stop_pips}p, Target: {autoConfig.target_pips}p.</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-4 justify-center">
                <button onClick={() => saveAutoConfig(autoConfig)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium" data-testid="eurusd-auto-save">
                  Save Settings
                </button>
                <button onClick={() => setShowAutoConfig(false)} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <HistoryModal
            currencyPair="EURUSD"
            onClose={() => setShowHistory(false)}
            onRefresh={() => fetchForecast(false)}
          />
        )}

        {/* Eta Setup Modal */}
        {showEtaSetup && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
              <h2 className="text-xl font-bold text-white mb-4 text-center">
                EURUSD Scylla Forecast Setup
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Signal Direction</label>
                  <select
                    value={etaForm.signal_direction}
                    onChange={(e) => setEtaForm({...etaForm, signal_direction: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-signal-direction"
                  >
                    <option value="BOTH">Buy & Sell Signals</option>
                    <option value="BUY">Buy Signals Only</option>
                    <option value="SELL">Sell Signals Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Open Price</label>
                  <input
                    type="number"
                    step="0.00001"
                    value={etaForm.open_price}
                    onChange={(e) => setEtaForm({...etaForm, open_price: e.target.value})}
                    placeholder="e.g. 1.08500"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-open-price"
                  />
                </div>
                {(etaForm.signal_direction === 'BOTH' || etaForm.signal_direction === 'BUY') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Scylla UP Forecast Trigger Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.up_trigger_pips}
                    onChange={(e) => setEtaForm({...etaForm, up_trigger_pips: e.target.value})}
                    placeholder="e.g. 20"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-up-trigger-pips"
                  />
                </div>
                )}
                {(etaForm.signal_direction === 'BOTH' || etaForm.signal_direction === 'SELL') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Scylla DOWN Forecast Trigger Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.down_trigger_pips}
                    onChange={(e) => setEtaForm({...etaForm, down_trigger_pips: e.target.value})}
                    placeholder="e.g. 20"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-down-trigger-pips"
                  />
                </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Target Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.target_pips}
                    onChange={(e) => setEtaForm({...etaForm, target_pips: e.target.value})}
                    placeholder="e.g. 180"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-target-pips"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Failure Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.failure_pips}
                    onChange={(e) => setEtaForm({...etaForm, failure_pips: e.target.value})}
                    placeholder="e.g. 40"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-eta-failure-pips"
                  />
                </div>
                <div className="flex justify-center gap-3 mt-4">
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/eta-setup/EURUSD?session_id=admin_test_123`, {
                          open_price: parseFloat(etaForm.open_price),
                          up_trigger_pips: etaForm.signal_direction !== 'SELL' ? parseFloat(etaForm.up_trigger_pips) : 0,
                          down_trigger_pips: etaForm.signal_direction !== 'BUY' ? parseFloat(etaForm.down_trigger_pips) : 0,
                          target_pips: parseFloat(etaForm.target_pips),
                          failure_pips: parseFloat(etaForm.failure_pips),
                          signal_direction: etaForm.signal_direction
                        });
                        toast.success('EURUSD Scylla Forecast configured!');
                        setShowEtaSetup(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error('Failed to configure Scylla Forecast');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eurusd-eta-submit-btn"
                  >
                    Set Scylla Forecast
                  </button>
                  <button
                    onClick={() => setShowEtaSetup(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eurusd-eta-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Zeta Setup Modal */}
        {showZetaSetup && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
              <h2 className="text-xl font-bold text-white mb-4 text-center">
                EURUSD Charybdis Forecast Setup
              </h2>
              <p className="text-gray-400 text-xs text-center mb-3">Enter either UP or DOWN reference price (one at a time)</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Signal Direction</label>
                  <select
                    value={zetaForm.signal_direction}
                    onChange={(e) => setZetaForm({...zetaForm, signal_direction: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-zeta-signal-direction"
                  >
                    <option value="BOTH">Buy & Sell Signals</option>
                    <option value="BUY">Buy Signals Only</option>
                    <option value="SELL">Sell Signals Only</option>
                  </select>
                </div>
                {(zetaForm.signal_direction === 'BOTH' || zetaForm.signal_direction === 'BUY') && (
                <div className="border border-gray-600 rounded p-3">
                  <p className="text-green-400 text-sm font-semibold mb-2">UP Forecast</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Up Forecast Reference Price</label>
                      <input
                        type="number"
                        step="0.00001"
                        value={zetaForm.up_trigger_price}
                        onChange={(e) => setZetaForm({...zetaForm, up_trigger_price: e.target.value, down_trigger_price: ''})}
                        placeholder="e.g. 1.08000"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="eurusd-zeta-up-trigger-price"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Pips Above Reference Price</label>
                      <input
                        type="number"
                        step="1"
                        value={zetaForm.up_trigger_pips}
                        onChange={(e) => setZetaForm({...zetaForm, up_trigger_pips: e.target.value})}
                        placeholder="e.g. 15"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="eurusd-zeta-up-trigger-pips"
                      />
                    </div>
                  </div>
                </div>
                )}
                {(zetaForm.signal_direction === 'BOTH' || zetaForm.signal_direction === 'SELL') && (
                <div className="border border-gray-600 rounded p-3">
                  <p className="text-red-400 text-sm font-semibold mb-2">DOWN Forecast</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Down Forecast Reference Price</label>
                      <input
                        type="number"
                        step="0.00001"
                        value={zetaForm.down_trigger_price}
                        onChange={(e) => setZetaForm({...zetaForm, down_trigger_price: e.target.value, up_trigger_price: ''})}
                        placeholder="e.g. 1.09000"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="eurusd-zeta-down-trigger-price"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Pips Below Reference Price</label>
                      <input
                        type="number"
                        step="1"
                        value={zetaForm.down_trigger_pips}
                        onChange={(e) => setZetaForm({...zetaForm, down_trigger_pips: e.target.value})}
                        placeholder="e.g. 15"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="eurusd-zeta-down-trigger-pips"
                      />
                    </div>
                  </div>
                </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Target Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={zetaForm.target_pips}
                    onChange={(e) => setZetaForm({...zetaForm, target_pips: e.target.value})}
                    placeholder="e.g. 150"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-zeta-target-pips"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Failure Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={zetaForm.failure_pips}
                    onChange={(e) => setZetaForm({...zetaForm, failure_pips: e.target.value})}
                    placeholder="e.g. 30"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eurusd-zeta-failure-pips"
                  />
                </div>
                <div className="flex justify-center gap-3 mt-4">
                  <button
                    onClick={async () => {
                      try {
                        const payload = {
                          target_pips: parseFloat(zetaForm.target_pips),
                          failure_pips: parseFloat(zetaForm.failure_pips),
                          signal_direction: zetaForm.signal_direction
                        };
                        if (zetaForm.up_trigger_price) {
                          payload.up_trigger_price = parseFloat(zetaForm.up_trigger_price);
                          payload.up_trigger_pips = parseFloat(zetaForm.up_trigger_pips || 0);
                        }
                        if (zetaForm.down_trigger_price) {
                          payload.down_trigger_price = parseFloat(zetaForm.down_trigger_price);
                          payload.down_trigger_pips = parseFloat(zetaForm.down_trigger_pips || 0);
                        }
                        await axios.post(`${API}/admin/zeta-setup/EURUSD?session_id=admin_test_123`, payload);
                        toast.success('EURUSD Charybdis Forecast configured!');
                        setShowZetaSetup(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to configure Charybdis Forecast');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eurusd-zeta-submit-btn"
                  >
                    Set Charybdis Forecast
                  </button>
                  <button
                    onClick={() => setShowZetaSetup(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eurusd-zeta-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charybdis at Market Modal - EURUSD */}
        {showZetaMarket && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-cyan-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                EURUSD Charybdis at Market
              </h2>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.post(`${API}/admin/zeta-at-market/EURUSD?session_id=admin_test_123`, {
                    direction: zetaMarketForm.direction,
                    target_pips: parseFloat(zetaMarketForm.target_pips),
                    stop_pips: parseFloat(zetaMarketForm.stop_pips)
                  });
                  toast.success(`EURUSD Charybdis ${zetaMarketForm.direction} triggered at market!`);
                  setShowZetaMarket(false);
                  setZetaMarketForm({ direction: 'UP', target_pips: '', stop_pips: '' });
                  fetchForecast(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to trigger Charybdis at Market');
                }
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-300 text-sm">Direction</label>
                    <select
                      value={zetaMarketForm.direction}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, direction: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      data-testid="eurusd-zeta-market-direction"
                    >
                      <option value="UP">UP</option>
                      <option value="DOWN">DOWN</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm">Target (pips)</label>
                    <input
                      type="number"
                      value={zetaMarketForm.target_pips}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, target_pips: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      placeholder="e.g. 120"
                      required
                      data-testid="eurusd-zeta-market-target"
                    />
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm">Stop (pips)</label>
                    <input
                      type="number"
                      value={zetaMarketForm.stop_pips}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, stop_pips: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      placeholder="e.g. 30"
                      required
                      data-testid="eurusd-zeta-market-stop"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4 justify-center">
                  <button
                    type="submit"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eurusd-zeta-market-submit"
                  >
                    Trigger Charybdis at Market
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowZetaMarket(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Stats Date Edit Modal */}
        {showStatsDateEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                EURUSD Statistics Start Date
              </h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={statsDateInput}
                  onChange={(e) => setStatsDateInput(e.target.value)}
                  placeholder="e.g. March 7th 2026"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  data-testid="eurusd-stats-date-input"
                />
                <div className="flex justify-center gap-3">
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/stats-start-date/EURUSD?session_id=admin_test_123`, { stats_start_date: statsDateInput });
                        toast.success('Statistics Start Date updated!');
                        setShowStatsDateEdit(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error('Failed to update date');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-medium text-sm"
                    data-testid="eurusd-stats-date-save-btn"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setShowStatsDateEdit(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// USDJPY 2-Day Cycle Forecast Modal Component
const UsdjpyForecastModal = ({ setShowUsdjpyForecast, adminMode }) => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showSetDailyOpen, setShowSetDailyOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEtaSetup, setShowEtaSetup] = useState(false);
  const [showZetaSetup, setShowZetaSetup] = useState(false);
  const [etaForm, setEtaForm] = useState({ open_price: '', up_trigger_pips: '', down_trigger_pips: '', target_pips: '', failure_pips: '', signal_direction: 'BOTH' });
  const [zetaForm, setZetaForm] = useState({ up_trigger_price: '', up_trigger_pips: '', down_trigger_price: '', down_trigger_pips: '', target_pips: '', failure_pips: '', signal_direction: 'BOTH' });
  const [showStatsDateEdit, setShowStatsDateEdit] = useState(false);
  const [statsDateInput, setStatsDateInput] = useState('');
  const [showZetaMarket, setShowZetaMarket] = useState(false);
  const [zetaMarketForm, setZetaMarketForm] = useState({ direction: 'UP', target_pips: '', stop_pips: '' });
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [autoConfig, setAutoConfig] = useState(null);

  const fetchAutoConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/auto-config/USDJPY?session_id=admin_test_123`);
      setAutoConfig(res.data.config);
    } catch (err) { console.error('Failed to load auto config'); }
  };

  const saveAutoConfig = async (cfg) => {
    try {
      await axios.post(`${API}/admin/auto-config/USDJPY?session_id=admin_test_123`, cfg);
      setAutoConfig(cfg);
      toast.success(`Auto mode ${cfg.enabled ? 'enabled' : 'disabled'} for USDJPY`);
      fetchForecast(false);
    } catch (err) { toast.error('Failed to save auto config'); }
  };

  const fetchForecast = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`${API}/forex/2day-cycle-forecast/USDJPY`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() }
      });
      setForecast(response.data.forecast);
      if (response.data.forecast?.stats_start_date) {
        setStatsDateInput(response.data.forecast.stats_start_date);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch USDJPY forecast:', err);
      setError('Failed to load forecast data');
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchForecast();
    const intervalId = setInterval(() => fetchForecast(false), 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchForecast(false);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)} at ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white">USDJPY Random Forest Cycles</h2>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="mt-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors"
          >
            {refreshing ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900 bg-opacity-30 rounded-lg p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : forecast ? (
          <div className="space-y-4">
            {/* Direction Only Mode - USDJPY */}
            {forecast.direction_only ? (
              <div className="space-y-4">
                {(forecast.cycle_direction === 'UP' || forecast.cycle_direction === 'DOWN') ? (
                <div className={`rounded-lg p-6 border-2 text-center ${
                  forecast.cycle_direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-30' :
                  'border-red-500 bg-red-900 bg-opacity-30'
                }`}>
                  <p className={`text-2xl font-bold ${
                    forecast.cycle_direction === 'UP' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {forecast.cycle_direction === 'UP' ? '<1 Day UP Cycle' : '<1 Day Down Cycle'}
                  </p>
                  {forecast.cycle_since && (
                    <p className="text-gray-400 text-sm mt-2">
                      since {formatDateTime(forecast.cycle_since)}
                    </p>
                  )}
                  {forecast.cycle_entry_price && forecast.current_price && (() => {
                    const pipSize = 'USDJPY'.includes('JPY') ? 0.01 : 0.0001;
                    const rawPips = (forecast.current_price - forecast.cycle_entry_price) / pipSize;
                    const isDown = rawPips < 0;
                    const absPips = Math.abs(rawPips).toFixed(1);
                    const dirLabel = forecast.cycle_direction === 'DOWN' ? 'Down Cycle' : 'Up Cycle';
                    return (
                      <p className={`text-sm mt-2 font-semibold ${isDown ? 'text-red-400' : 'text-green-400'}`}>
                        {isDown ? 'Down' : 'Up'} {absPips} pips since start of {dirLabel} Forecast
                      </p>
                    );
                  })()}
                </div>
                ) : (
                <div className="rounded-lg p-6 border-2 border-gray-600 bg-gray-800 bg-opacity-50 text-center">
                  <p className="text-gray-400 text-lg">No Active Cycle</p>
                </div>
                )}
              </div>
            ) : (
            <>
            {/* AUTOMATIC Forecast */}
            <p className="text-white text-sm tracking-wide font-bold">Scylla Cycle</p>
            {forecast.active_forecasts && forecast.active_forecasts.length > 0 ? (
              <div className={`rounded-lg p-4 border-2 ${forecast.active_forecasts[0].direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-30' : 'border-red-500 bg-red-900 bg-opacity-30'}`}>
                <p className="text-white text-sm leading-relaxed">
                  <span className="text-gray-400">{formatDateTime(forecast.active_forecasts[0].entry_time)}</span>
                  {' '}Cycle Forecasted to start{' '}
                  <span className={forecast.active_forecasts[0].direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                    {forecast.active_forecasts[0].direction}
                  </span>
                  {' '}from{' '}
                  <span className="text-white font-mono font-bold">{forecast.active_forecasts[0].entry_price?.toFixed(3)}</span>
                  {' '}towards{' '}
                  <span className="text-white font-mono font-bold">{forecast.active_forecasts[0].target_price?.toFixed(3)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  This will be a Forecast Failure if instead the price {forecast.active_forecasts[0].direction === 'UP' ? 'falls' : 'rises'} to{' '}
                  <span className="text-red-400 font-mono">{forecast.active_forecasts[0].failure_price?.toFixed(3)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  Since the start of this forecast the price has moved{' '}
                  <span className="font-bold">{Math.abs(forecast.active_forecasts[0].progress_pips || 0).toFixed(1)}</span> pips{' '}
                  {(forecast.active_forecasts[0].progress_pips || 0) >= 0 ? (
                    <span className="text-green-400">in the direction of the Forecast</span>
                  ) : (
                    <span className="text-red-400">in the opposite direction of the Forecast</span>
                  )}
                </p>
              </div>
            ) : forecast.pending_forecasts && forecast.pending_forecasts.length > 0 ? (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-sky-400 text-sm mb-2">Scylla Pending — Waiting for price to trigger:</p>
                {forecast.pending_forecasts.map((pf, idx) => (
                  <p key={idx} className={`text-sm ${pf.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                    {pf.direction} @ <span className="font-mono">{pf.entry_price?.toFixed(3)}</span>
                    {' → '}Target: <span className="font-mono">{pf.target_price?.toFixed(3)}</span>, Failure: <span className="font-mono">{pf.failure_price?.toFixed(3)}</span>
                  </p>
                ))}
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sky-400">Waiting on next Scylla Cycle</p>
              </div>
            )}

            {/* MANUAL Forecast */}
            <p className="text-white text-sm tracking-wide mt-4 font-bold">Charybdis Cycle</p>
            {forecast.manual_forecast ? (
              <div className={`rounded-lg p-4 border-2 ${forecast.manual_forecast.direction === 'UP' ? 'border-green-500 bg-green-900 bg-opacity-20' : 'border-red-500 bg-red-900 bg-opacity-20'}`}>
                <p className="text-white text-sm leading-relaxed">
                  <span className="text-gray-400">{formatDateTime(forecast.manual_forecast.entry_time)}</span>
                  {' '}Cycle Forecasted to start{' '}
                  <span className={forecast.manual_forecast.direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                    {forecast.manual_forecast.direction}
                  </span>
                  {' '}from{' '}
                  <span className="text-white font-mono font-bold">{forecast.manual_forecast.entry_price?.toFixed(3)}</span>
                  {' '}towards{' '}
                  <span className="text-white font-mono font-bold">{forecast.manual_forecast.target_price?.toFixed(3)}</span>
                </p>
                <p className="text-white text-sm mt-2">
                  This will be a Forecast Failure if instead the price {forecast.manual_forecast.direction === 'UP' ? 'falls' : 'rises'} to{' '}
                  <span className="text-red-400 font-mono">{forecast.manual_forecast.failure_price?.toFixed(3)}</span>
                  <span className="text-gray-500 text-xs ml-2">(fixed)</span>
                </p>
                <p className="text-white text-sm mt-2">
                  Since the start of this forecast the price has moved{' '}
                  <span className="font-bold">{Math.abs(forecast.manual_forecast.progress_pips || 0).toFixed(1)}</span> pips{' '}
                  {(forecast.manual_forecast.progress_pips || 0) >= 0 ? (
                    <span className="text-green-400">in the direction of the Forecast</span>
                  ) : (
                    <span className="text-red-400">in the opposite direction of the Forecast</span>
                  )}
                </p>
              </div>
            ) : forecast.zeta_pending_up || forecast.zeta_pending_down ? (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-sky-400 text-sm mb-2">Charybdis Pending — Waiting for price to trigger:</p>
                {forecast.zeta_pending_up && (
                  <p className="text-sm text-green-400">
                    UP @ <span className="font-mono">{forecast.zeta_pending_up.entry_price?.toFixed(3)}</span>
                    {' → '}Target: <span className="font-mono">{forecast.zeta_pending_up.target_price?.toFixed(3)}</span>, Failure: <span className="font-mono">{forecast.zeta_pending_up.failure_price?.toFixed(3)}</span>
                  </p>
                )}
                {forecast.zeta_pending_down && (
                  <p className="text-sm text-red-400">
                    DOWN @ <span className="font-mono">{forecast.zeta_pending_down.entry_price?.toFixed(3)}</span>
                    {' → '}Target: <span className="font-mono">{forecast.zeta_pending_down.target_price?.toFixed(3)}</span>, Failure: <span className="font-mono">{forecast.zeta_pending_down.failure_price?.toFixed(3)}</span>
                  </p>
                )}
                <button
                  data-testid="clear-zeta-pending-usdjpy"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/clear-zeta-pending/USDJPY?session_id=admin_test_123`);
                      toast.success('Charybdis pending orders cleared');
                      fetchForecast(false);
                    } catch (err) {
                      toast.error('Failed to clear Charybdis pending');
                    }
                  }}
                  className="mt-3 px-3 py-1 bg-orange-600 hover:bg-orange-500 text-white text-xs rounded"
                >
                  Clear Charybdis Pending
                </button>
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sky-400">Waiting on next Charybdis Cycle</p>
              </div>
            )}

            {/* All Previous Active Forecasts */}
            {forecast.active_forecasts && forecast.active_forecasts.length > 1 && (
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-3 font-semibold">All previous Forecasts that are still Active:</p>
                <div className="space-y-3">
                  {forecast.active_forecasts.slice(1).map((af, idx) => (
                    <div key={af.id || idx} className={`rounded-lg p-3 border ${af.direction === 'UP' ? 'border-green-600 bg-green-900 bg-opacity-20' : 'border-red-600 bg-red-900 bg-opacity-20'}`}>
                      <p className="text-white text-sm">
                        <span className="text-gray-400">{formatDateTime(af.entry_time)}</span>
                        {' '}Cycle Forecasted to start{' '}
                        <span className={af.direction === 'UP' ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>{af.direction}</span>
                        {' '}from{' '}
                        <span className="font-mono">{af.entry_price?.toFixed(3)}</span>
                        {' '}towards{' '}
                        <span className="font-mono">{af.target_price?.toFixed(3)}</span>
                      </p>
                      <p className="text-white text-sm mt-1">
                        Since the start of the forecast the price has moved{' '}
                        <span className="font-bold">{Math.abs(af.progress_pips || 0).toFixed(1)}</span> pips{' '}
                        {(af.progress_pips || 0) >= 0 ? (
                          <span className="text-green-400">in the direction of the Forecast</span>
                        ) : (
                          <span className="text-red-400">in the opposite direction of the Forecast</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            
            {/* Statistics */}
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-sm font-semibold">Statistics Start Date: {forecast.stats_start_date || 'Not Set'}</p>
                {adminMode && (
                  <button
                    onClick={() => setShowStatsDateEdit(true)}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                    data-testid="usdjpy-stats-date-edit-btn"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-white">Total Cycles Ending with Positive Result: <span className="text-green-400 font-bold">{forecast.stats?.positive_count || 0}</span></p>
                <p className="text-white">Total Cycles Ending with Negative Result: <span className="text-red-400 font-bold">{forecast.stats?.negative_count || 0}</span></p>
                <p className="text-white">Total Pips in Positive Result Cycles: <span className="text-green-400 font-bold">+{(forecast.stats?.positive_pips_total || 0).toFixed(1)}</span></p>
                <p className="text-white">Total Pips in Negative Result Cycles: <span className="text-red-400 font-bold">-{(forecast.stats?.negative_pips_total || 0).toFixed(1)}</span></p>
                <p className="text-white text-xs mt-3">Results shown above represent simulated or hypothetical performance. Please read our full CFTC 4.41 disclosure in the footer.</p>
              </div>
            </div>
            </>
            )}
          </div>
        ) : null}

        {/* Admin & Close Buttons */}
        <div className="flex justify-center gap-3 mt-4 flex-wrap">
          {adminMode && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
              >
                📜 History
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/eta-config/USDJPY?session_id=admin_test_123`);
                    if (res.data.eta_config) {
                      const c = res.data.eta_config;
                      setEtaForm({
                        open_price: c.open_price || '',
                        up_trigger_pips: c.up_trigger_pips || '',
                        down_trigger_pips: c.down_trigger_pips || '',
                        target_pips: c.target_pips || '',
                        failure_pips: c.failure_pips || '',
                        signal_direction: c.signal_direction || 'BOTH'
                      });
                    }
                  } catch (err) { console.log('No existing eta config'); }
                  setShowEtaSetup(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-eta-setup-btn"
              >
                USDJPY Scylla Forecast
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/eta-config/USDJPY?session_id=admin_test_123`);
                    if (res.data.zeta_config) {
                      const c = res.data.zeta_config;
                      setZetaForm({
                        up_trigger_price: c.up_trigger_price || '',
                        up_trigger_pips: c.up_trigger_pips || '',
                        down_trigger_price: c.down_trigger_price || '',
                        down_trigger_pips: c.down_trigger_pips || '',
                        target_pips: c.target_pips || '',
                        failure_pips: c.failure_pips || '',
                        signal_direction: c.signal_direction || 'BOTH'
                      });
                    }
                  } catch (err) { console.log('No existing zeta config'); }
                  setShowZetaSetup(true);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-zeta-setup-btn"
              >
                USDJPY Charybdis Forecast
              </button>
              <button
                onClick={() => setShowZetaMarket(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-zeta-market-btn"
              >
                Charybdis at Market
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/end-eta/USDJPY?session_id=admin_test_123`);
                    toast.success('USDJPY Scylla ended.');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to end Eta');
                  }
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-end-eta-btn"
              >
                End Scylla
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/end-zeta/USDJPY?session_id=admin_test_123`);
                    toast.success('USDJPY Charybdis ended.');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to end Zeta');
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-end-zeta-btn"
              >
                End Charybdis
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('Reset all USDJPY statistics to zero?')) return;
                  try {
                    await axios.post(`${API}/admin/reset-stats/USDJPY?session_id=admin_test_123`);
                    toast.success('USDJPY stats reset to zero');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to reset stats');
                  }
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-reset-stats-btn"
              >
                Reset Stats
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/2day-cycle-reset/USDJPY?session_id=admin_test_123&clear_history=false`);
                    toast.success('USDJPY Compass Price reset');
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Reset failed');
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-reset-btn"
              >
                🔄 Reset
              </button>
              <button
                onClick={() => { fetchAutoConfig(); setShowAutoConfig(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm"
                data-testid="usdjpy-auto-config-btn"
              >
                Auto Mode
              </button>
              <button
                onClick={async () => {
                  try {
                    const current = forecast?.direction_only || false;
                    await axios.post(`${API}/admin/direction-only/USDJPY?session_id=admin_test_123`, { enabled: !current });
                    toast.success(`Direction Only mode ${!current ? 'enabled' : 'disabled'}`);
                    fetchForecast(false);
                  } catch (err) {
                    toast.error('Failed to toggle Direction Only');
                  }
                }}
                className={`${forecast?.direction_only ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-600 hover:bg-gray-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm`}
                data-testid="usdjpy-direction-only-btn"
              >
                {forecast?.direction_only ? 'Direction Only: ON' : 'Direction Only: OFF'}
              </button>
            </>
          )}
          <button
            onClick={() => setShowUsdjpyForecast(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>

        {/* Auto Config Modal - USDJPY */}
        {showAutoConfig && autoConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-indigo-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                USDJPY Automated Mode
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-gray-300 text-sm">Automated Forecasts</label>
                  <button
                    onClick={() => setAutoConfig({ ...autoConfig, enabled: !autoConfig.enabled })}
                    className={`px-4 py-1 rounded-full text-sm font-bold ${autoConfig.enabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                    data-testid="usdjpy-auto-toggle"
                  >
                    {autoConfig.enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div>
                  <label className="text-gray-300 text-sm">Cycle Direction</label>
                  <select
                    value={autoConfig.cycle || 'BULLISH'}
                    onChange={(e) => setAutoConfig({ ...autoConfig, cycle: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                    data-testid="usdjpy-auto-cycle"
                  >
                    <option value="BULLISH">BULLISH (Buy only)</option>
                    <option value="BEARISH">BEARISH (Sell only)</option>
                    <option value="BOTH">BOTH (Buys and Sells)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-gray-300 text-xs">Daily Open Price (manual)</label>
                    <input type="number" step="0.001" value={autoConfig.daily_open || ''} onChange={(e) => setAutoConfig({ ...autoConfig, daily_open: parseFloat(e.target.value) || 0 })} className="w-full bg-gray-700 border border-yellow-600 rounded px-3 py-2 text-yellow-300 mt-1 text-sm font-mono" placeholder="e.g. 153.520" data-testid="usdjpy-auto-daily-open" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Scylla Trigger (pips)</label>
                    <input type="number" value={autoConfig.eta_trigger_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, eta_trigger_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-eta-trigger" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Charybdis Dip (pips)</label>
                    <input type="number" value={autoConfig.zeta_dip_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, zeta_dip_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-zeta-dip" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Charybdis Trigger (pips)</label>
                    <input type="number" value={autoConfig.zeta_trigger_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, zeta_trigger_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-zeta-trigger" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Stop (pips)</label>
                    <input type="number" value={autoConfig.stop_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, stop_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-stop" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Target (pips)</label>
                    <input type="number" value={autoConfig.target_pips || ''} onChange={(e) => setAutoConfig({ ...autoConfig, target_pips: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-target" />
                  </div>
                  <div>
                    <label className="text-gray-300 text-xs">Rollover Hour (UTC)</label>
                    <input type="number" min="0" max="23" value={autoConfig.rollover_hour ?? 22} onChange={(e) => setAutoConfig({ ...autoConfig, rollover_hour: parseInt(e.target.value) || 0 })} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1 text-sm" data-testid="usdjpy-auto-rollover" />
                  </div>
                </div>
                {autoConfig.enabled && (
                  <div className="bg-gray-700 rounded p-3 text-xs text-gray-300">
                    <p className="text-indigo-400 font-bold mb-1">Auto Mode Active</p>
                    <p>Daily open captured at {autoConfig.rollover_hour || 22}:00 UTC. Scylla triggers {autoConfig.eta_trigger_pips}p from open. Charybdis requires {autoConfig.zeta_dip_pips}p dip then {autoConfig.zeta_trigger_pips}p recovery. Stop: {autoConfig.stop_pips}p, Target: {autoConfig.target_pips}p.</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-4 justify-center">
                <button onClick={() => saveAutoConfig(autoConfig)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium" data-testid="usdjpy-auto-save">
                  Save Settings
                </button>
                <button onClick={() => setShowAutoConfig(false)} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <HistoryModal
            currencyPair="USDJPY"
            onClose={() => setShowHistory(false)}
            onRefresh={() => fetchForecast(false)}
          />
        )}

        {/* Set Daily Open Modal */}
        {showSetDailyOpen && forecast && (
          <SetDailyOpenModal
            currencyPair="USDJPY"
            currentOpen={forecast.daily_open}
            capturedAt={forecast.open_captured_at}
            onClose={() => setShowSetDailyOpen(false)}
            onSuccess={() => fetchForecast(false)}
          />
        )}

        {/* Eta Setup Modal */}
        {showEtaSetup && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
              <h2 className="text-xl font-bold text-white mb-4 text-center">
                USDJPY Scylla Forecast Setup
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Signal Direction</label>
                  <select
                    value={etaForm.signal_direction}
                    onChange={(e) => setEtaForm({...etaForm, signal_direction: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="usdjpy-eta-signal-direction"
                  >
                    <option value="BOTH">Buy & Sell Signals</option>
                    <option value="BUY">Buy Signals Only</option>
                    <option value="SELL">Sell Signals Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Open Price</label>
                  <input
                    type="number"
                    step="0.001"
                    value={etaForm.open_price}
                    onChange={(e) => setEtaForm({...etaForm, open_price: e.target.value})}
                    placeholder="e.g. 159.000"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eta-open-price"
                  />
                </div>
                {(etaForm.signal_direction === 'BOTH' || etaForm.signal_direction === 'BUY') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Scylla UP Forecast Trigger Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.up_trigger_pips}
                    onChange={(e) => setEtaForm({...etaForm, up_trigger_pips: e.target.value})}
                    placeholder="e.g. 20"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eta-up-trigger-pips"
                  />
                </div>
                )}
                {(etaForm.signal_direction === 'BOTH' || etaForm.signal_direction === 'SELL') && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Scylla DOWN Forecast Trigger Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.down_trigger_pips}
                    onChange={(e) => setEtaForm({...etaForm, down_trigger_pips: e.target.value})}
                    placeholder="e.g. 20"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eta-down-trigger-pips"
                  />
                </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Target Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.target_pips}
                    onChange={(e) => setEtaForm({...etaForm, target_pips: e.target.value})}
                    placeholder="e.g. 180"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eta-target-pips"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Failure Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={etaForm.failure_pips}
                    onChange={(e) => setEtaForm({...etaForm, failure_pips: e.target.value})}
                    placeholder="e.g. 40"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="eta-failure-pips"
                  />
                </div>
                <div className="flex justify-center gap-3 mt-4">
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/eta-setup/USDJPY?session_id=admin_test_123`, {
                          open_price: parseFloat(etaForm.open_price),
                          up_trigger_pips: etaForm.signal_direction !== 'SELL' ? parseFloat(etaForm.up_trigger_pips) : 0,
                          down_trigger_pips: etaForm.signal_direction !== 'BUY' ? parseFloat(etaForm.down_trigger_pips) : 0,
                          target_pips: parseFloat(etaForm.target_pips),
                          failure_pips: parseFloat(etaForm.failure_pips),
                          signal_direction: etaForm.signal_direction
                        });
                        toast.success('USDJPY Scylla Forecast configured!');
                        setShowEtaSetup(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error('Failed to configure Scylla Forecast');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eta-submit-btn"
                  >
                    Set Scylla Forecast
                  </button>
                  <button
                    onClick={() => setShowEtaSetup(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="eta-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Zeta Setup Modal */}
        {showZetaSetup && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
              <h2 className="text-xl font-bold text-white mb-4 text-center">
                USDJPY Charybdis Forecast Setup
              </h2>
              <p className="text-gray-400 text-xs text-center mb-3">Enter either UP or DOWN reference price (one at a time)</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Signal Direction</label>
                  <select
                    value={zetaForm.signal_direction}
                    onChange={(e) => setZetaForm({...zetaForm, signal_direction: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="usdjpy-zeta-signal-direction"
                  >
                    <option value="BOTH">Buy & Sell Signals</option>
                    <option value="BUY">Buy Signals Only</option>
                    <option value="SELL">Sell Signals Only</option>
                  </select>
                </div>
                {(zetaForm.signal_direction === 'BOTH' || zetaForm.signal_direction === 'BUY') && (
                <div className="border border-gray-600 rounded p-3">
                  <p className="text-green-400 text-sm font-semibold mb-2">UP Forecast</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Up Forecast Reference Price</label>
                      <input
                        type="number"
                        step="0.001"
                        value={zetaForm.up_trigger_price}
                        onChange={(e) => setZetaForm({...zetaForm, up_trigger_price: e.target.value, down_trigger_price: ''})}
                        placeholder="e.g. 158.500"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="zeta-up-trigger-price"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Pips Above Reference Price</label>
                      <input
                        type="number"
                        step="1"
                        value={zetaForm.up_trigger_pips}
                        onChange={(e) => setZetaForm({...zetaForm, up_trigger_pips: e.target.value})}
                        placeholder="e.g. 15"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="zeta-up-trigger-pips"
                      />
                    </div>
                  </div>
                </div>
                )}
                {(zetaForm.signal_direction === 'BOTH' || zetaForm.signal_direction === 'SELL') && (
                <div className="border border-gray-600 rounded p-3">
                  <p className="text-red-400 text-sm font-semibold mb-2">DOWN Forecast</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Down Forecast Reference Price</label>
                      <input
                        type="number"
                        step="0.001"
                        value={zetaForm.down_trigger_price}
                        onChange={(e) => setZetaForm({...zetaForm, down_trigger_price: e.target.value, up_trigger_price: ''})}
                        placeholder="e.g. 159.500"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="zeta-down-trigger-price"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Pips Below Reference Price</label>
                      <input
                        type="number"
                        step="1"
                        value={zetaForm.down_trigger_pips}
                        onChange={(e) => setZetaForm({...zetaForm, down_trigger_pips: e.target.value})}
                        placeholder="e.g. 15"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        data-testid="zeta-down-trigger-pips"
                      />
                    </div>
                  </div>
                </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Target Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={zetaForm.target_pips}
                    onChange={(e) => setZetaForm({...zetaForm, target_pips: e.target.value})}
                    placeholder="e.g. 150"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="zeta-target-pips"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Forecast Failure Pips</label>
                  <input
                    type="number"
                    step="1"
                    value={zetaForm.failure_pips}
                    onChange={(e) => setZetaForm({...zetaForm, failure_pips: e.target.value})}
                    placeholder="e.g. 30"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    data-testid="zeta-failure-pips"
                  />
                </div>
                <div className="flex justify-center gap-3 mt-4">
                  <button
                    onClick={async () => {
                      try {
                        const payload = {
                          target_pips: parseFloat(zetaForm.target_pips),
                          failure_pips: parseFloat(zetaForm.failure_pips),
                          signal_direction: zetaForm.signal_direction
                        };
                        if (zetaForm.up_trigger_price) {
                          payload.up_trigger_price = parseFloat(zetaForm.up_trigger_price);
                          payload.up_trigger_pips = parseFloat(zetaForm.up_trigger_pips || 0);
                        }
                        if (zetaForm.down_trigger_price) {
                          payload.down_trigger_price = parseFloat(zetaForm.down_trigger_price);
                          payload.down_trigger_pips = parseFloat(zetaForm.down_trigger_pips || 0);
                        }
                        await axios.post(`${API}/admin/zeta-setup/USDJPY?session_id=admin_test_123`, payload);
                        toast.success('USDJPY Charybdis Forecast configured!');
                        setShowZetaSetup(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to configure Charybdis Forecast');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="zeta-submit-btn"
                  >
                    Set Charybdis Forecast
                  </button>
                  <button
                    onClick={() => setShowZetaSetup(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="zeta-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charybdis at Market Modal - USDJPY */}
        {showZetaMarket && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-cyan-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                USDJPY Charybdis at Market
              </h2>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.post(`${API}/admin/zeta-at-market/USDJPY?session_id=admin_test_123`, {
                    direction: zetaMarketForm.direction,
                    target_pips: parseFloat(zetaMarketForm.target_pips),
                    stop_pips: parseFloat(zetaMarketForm.stop_pips)
                  });
                  toast.success(`USDJPY Charybdis ${zetaMarketForm.direction} triggered at market!`);
                  setShowZetaMarket(false);
                  setZetaMarketForm({ direction: 'UP', target_pips: '', stop_pips: '' });
                  fetchForecast(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to trigger Charybdis at Market');
                }
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-300 text-sm">Direction</label>
                    <select
                      value={zetaMarketForm.direction}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, direction: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      data-testid="usdjpy-zeta-market-direction"
                    >
                      <option value="UP">UP</option>
                      <option value="DOWN">DOWN</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm">Target (pips)</label>
                    <input
                      type="number"
                      value={zetaMarketForm.target_pips}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, target_pips: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      placeholder="e.g. 180"
                      required
                      data-testid="usdjpy-zeta-market-target"
                    />
                  </div>
                  <div>
                    <label className="text-gray-300 text-sm">Stop (pips)</label>
                    <input
                      type="number"
                      value={zetaMarketForm.stop_pips}
                      onChange={(e) => setZetaMarketForm({ ...zetaMarketForm, stop_pips: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mt-1"
                      placeholder="e.g. 40"
                      required
                      data-testid="usdjpy-zeta-market-stop"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4 justify-center">
                  <button
                    type="submit"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-medium"
                    data-testid="usdjpy-zeta-market-submit"
                  >
                    Trigger Charybdis at Market
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowZetaMarket(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Stats Date Edit Modal */}
        {showStatsDateEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-600">
              <h2 className="text-lg font-bold text-white mb-4 text-center">
                USDJPY Statistics Start Date
              </h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={statsDateInput}
                  onChange={(e) => setStatsDateInput(e.target.value)}
                  placeholder="e.g. April 7th 2026"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  data-testid="usdjpy-stats-date-input"
                />
                <div className="flex justify-center gap-3">
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/stats-start-date/USDJPY?session_id=admin_test_123`, { stats_start_date: statsDateInput });
                        toast.success('Statistics Start Date updated!');
                        setShowStatsDateEdit(false);
                        fetchForecast(false);
                      } catch (err) {
                        toast.error('Failed to update date');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-medium text-sm"
                    data-testid="usdjpy-stats-date-save-btn"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setShowStatsDateEdit(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Pivot Point Path Dashboard Modal Component
const PocVwapDashboardModal = ({ setShowPocVwapDashboard }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`${API}/forex/poc-vwap-dashboard/EURUSD`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() }
      });
      setDashboard(response.data.dashboard);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch Pivot dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    
    // Auto-refresh every 5 minutes (aligned with 1hr pivot update frequency)
    const intervalId = setInterval(() => {
      fetchDashboard(false);
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white">📊 EURUSD Pivot Points Path</h2>
          <p className="text-gray-400 text-sm mt-1">3hr & 1hr Pivot Analysis (UTC)</p>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="mt-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors"
          >
            {refreshing ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900 bg-opacity-30 rounded-lg p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : dashboard ? (
          <div className="space-y-4">
            {/* Status Banner */}
            <div className={`rounded-lg p-3 text-center ${dashboard.status === 'running' ? 'bg-green-900 bg-opacity-30' : 'bg-yellow-900 bg-opacity-30'}`}>
              <p className={`text-sm font-medium ${dashboard.status === 'running' ? 'text-green-400' : 'text-yellow-400'}`}>
                {dashboard.status === 'running' ? '🟢 Dashboard Active' : '⏳ ' + dashboard.message}
              </p>
              <p className="text-gray-500 text-xs mt-1">3hr Pivot updates every 15min • 1hr Pivot updates every 5min</p>
            </div>

            {/* Current Price & Bias */}
            <div 
              className="rounded-lg p-6 text-center"
              style={{ backgroundColor: `${dashboard.bias_color}20`, border: `2px solid ${dashboard.bias_color}` }}
            >
              <p className="text-gray-400 text-sm mb-1">Current Price</p>
              <p className="text-2xl font-mono text-white mb-2">{dashboard.current_price?.toFixed(5) || 'N/A'}</p>
              <h3 
                className="text-3xl font-bold"
                style={{ color: dashboard.bias_color }}
              >
                {dashboard.bias_label}
              </h3>
              <p className="text-xl font-semibold text-white mt-2">
                Combined Bias: {dashboard.combined_bias > 0 ? '+' : ''}{dashboard.combined_bias}
              </p>
              <p className="text-gray-500 text-xs mt-1">Range: -24 to +24</p>
            </div>

            {/* Above/Below Ratios */}
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-xs mb-3 text-center font-semibold">ABOVE/BELOW RATIOS (Last 12 Closes Each)</p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* 3hr Pivot Data */}
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-amber-400 text-xs font-semibold mb-2 text-center">3 Hour Pivot</p>
                  <p className="text-white font-mono text-center text-sm mb-2">{dashboard.pivot_3hr_data?.level?.toFixed(5) || 'N/A'}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Above/Below:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.above}/{dashboard.pivot_3hr_data?.below}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Above %:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.above_pct}%</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Bias:</span>
                    <span className={`font-mono ${dashboard.pivot_3hr_data?.bias > 0 ? 'text-green-400' : dashboard.pivot_3hr_data?.bias < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {dashboard.pivot_3hr_data?.bias > 0 ? '+' : ''}{dashboard.pivot_3hr_data?.bias}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Δ from price:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.diff_pips > 0 ? '+' : ''}{dashboard.pivot_3hr_data?.diff_pips}p</span>
                  </div>
                </div>

                {/* 1hr Pivot Data */}
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-cyan-400 text-xs font-semibold mb-2 text-center">1 Hour Pivot</p>
                  <p className="text-white font-mono text-center text-sm mb-2">{dashboard.pivot_1hr_data?.level?.toFixed(5) || 'N/A'}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Above/Below:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.above}/{dashboard.pivot_1hr_data?.below}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Above %:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.above_pct}%</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Bias:</span>
                    <span className={`font-mono ${dashboard.pivot_1hr_data?.bias > 0 ? 'text-green-400' : dashboard.pivot_1hr_data?.bias < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {dashboard.pivot_1hr_data?.bias > 0 ? '+' : ''}{dashboard.pivot_1hr_data?.bias}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Δ from price:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.diff_pips > 0 ? '+' : ''}{dashboard.pivot_1hr_data?.diff_pips}p</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bias Scale */}
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-xs mb-2 text-center font-semibold">BIAS SCALE</p>
              <div className="flex justify-between text-xs">
                <span className="text-red-500">Strong Bear<br/>-24 to -17</span>
                <span className="text-red-400">Mod Bear<br/>-16 to -9</span>
                <span className="text-red-300">Mild Bear<br/>-8 to -5</span>
                <span className="text-yellow-400">Neutral<br/>-4 to +4</span>
                <span className="text-green-300">Mild Bull<br/>+5 to +8</span>
                <span className="text-green-400">Mod Bull<br/>+9 to +16</span>
                <span className="text-green-500">Strong Bull<br/>+17 to +24</span>
              </div>
            </div>

            {/* Pivot Calculation Note */}
            <div className="bg-gray-700 bg-opacity-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs text-center">
                Pivot = (High + Low + Close) / 3
              </p>
            </div>

            {/* Timestamp */}
            <p className="text-xs text-blue-400 text-center">
              Last updated: {new Date(dashboard.timestamp).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="bg-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400">No dashboard data available</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500 italic">Not trading advice. For educational purposes only.</p>
        </div>

        {/* Close Button */}
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setShowPocVwapDashboard(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// USDJPY Pivot Point Path Dashboard Modal Component
const UsdjpyPocVwapDashboardModal = ({ setShowUsdjpyPocVwapDashboard }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`${API}/forex/poc-vwap-dashboard/USDJPY`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() }
      });
      setDashboard(response.data.dashboard);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch USDJPY Pivot dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    
    // Auto-refresh every 5 minutes (aligned with 1hr pivot update frequency)
    const intervalId = setInterval(() => {
      fetchDashboard(false);
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white">📊 USDJPY Pivot Points Path</h2>
          <p className="text-gray-400 text-sm mt-1">3hr & 1hr Pivot Analysis (UTC)</p>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="mt-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors"
          >
            {refreshing ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900 bg-opacity-30 rounded-lg p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : dashboard ? (
          <div className="space-y-4">
            {/* Status Banner */}
            <div className={`rounded-lg p-3 text-center ${dashboard.status === 'running' ? 'bg-green-900 bg-opacity-30' : 'bg-yellow-900 bg-opacity-30'}`}>
              <p className={`text-sm font-medium ${dashboard.status === 'running' ? 'text-green-400' : 'text-yellow-400'}`}>
                {dashboard.status === 'running' ? '🟢 Dashboard Active' : '⏳ ' + dashboard.message}
              </p>
              <p className="text-gray-500 text-xs mt-1">3hr Pivot updates every 15min • 1hr Pivot updates every 5min</p>
            </div>

            {/* Current Price & Bias */}
            <div 
              className="rounded-lg p-6 text-center"
              style={{ backgroundColor: `${dashboard.bias_color}20`, border: `2px solid ${dashboard.bias_color}` }}
            >
              <p className="text-gray-400 text-sm mb-1">Current Price</p>
              <p className="text-2xl font-mono text-white mb-2">{dashboard.current_price?.toFixed(3) || 'N/A'}</p>
              <h3 
                className="text-3xl font-bold"
                style={{ color: dashboard.bias_color }}
              >
                {dashboard.bias_label}
              </h3>
              <p className="text-xl font-semibold text-white mt-2">
                Combined Bias: {dashboard.combined_bias > 0 ? '+' : ''}{dashboard.combined_bias}
              </p>
              <p className="text-gray-500 text-xs mt-1">Range: -24 to +24</p>
            </div>

            {/* Above/Below Ratios */}
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-xs mb-3 text-center font-semibold">ABOVE/BELOW RATIOS (Last 12 Closes Each)</p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* 3hr Pivot Data */}
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-amber-400 text-xs font-semibold mb-2 text-center">3 Hour Pivot</p>
                  <p className="text-white font-mono text-center text-sm mb-2">{dashboard.pivot_3hr_data?.level?.toFixed(3) || 'N/A'}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Above/Below:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.above}/{dashboard.pivot_3hr_data?.below}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Above %:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.above_pct}%</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Bias:</span>
                    <span className={`font-mono ${dashboard.pivot_3hr_data?.bias > 0 ? 'text-green-400' : dashboard.pivot_3hr_data?.bias < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {dashboard.pivot_3hr_data?.bias > 0 ? '+' : ''}{dashboard.pivot_3hr_data?.bias}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Δ from price:</span>
                    <span className="text-white font-mono">{dashboard.pivot_3hr_data?.diff_pips > 0 ? '+' : ''}{dashboard.pivot_3hr_data?.diff_pips}p</span>
                  </div>
                </div>

                {/* 1hr Pivot Data */}
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-cyan-400 text-xs font-semibold mb-2 text-center">1 Hour Pivot</p>
                  <p className="text-white font-mono text-center text-sm mb-2">{dashboard.pivot_1hr_data?.level?.toFixed(3) || 'N/A'}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Above/Below:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.above}/{dashboard.pivot_1hr_data?.below}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Above %:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.above_pct}%</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Bias:</span>
                    <span className={`font-mono ${dashboard.pivot_1hr_data?.bias > 0 ? 'text-green-400' : dashboard.pivot_1hr_data?.bias < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {dashboard.pivot_1hr_data?.bias > 0 ? '+' : ''}{dashboard.pivot_1hr_data?.bias}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Δ from price:</span>
                    <span className="text-white font-mono">{dashboard.pivot_1hr_data?.diff_pips > 0 ? '+' : ''}{dashboard.pivot_1hr_data?.diff_pips}p</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bias Scale */}
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-xs mb-2 text-center font-semibold">BIAS SCALE</p>
              <div className="flex justify-between text-xs">
                <span className="text-red-500">Strong Bear<br/>-24 to -17</span>
                <span className="text-red-400">Mod Bear<br/>-16 to -9</span>
                <span className="text-red-300">Mild Bear<br/>-8 to -5</span>
                <span className="text-yellow-400">Neutral<br/>-4 to +4</span>
                <span className="text-green-300">Mild Bull<br/>+5 to +8</span>
                <span className="text-green-400">Mod Bull<br/>+9 to +16</span>
                <span className="text-green-500">Strong Bull<br/>+17 to +24</span>
              </div>
            </div>

            {/* Pivot Calculation Note */}
            <div className="bg-gray-700 bg-opacity-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs text-center">
                Pivot = (High + Low + Close) / 3
              </p>
            </div>

            {/* Timestamp */}
            <p className="text-xs text-blue-400 text-center">
              Last updated: {new Date(dashboard.timestamp).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="bg-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400">No dashboard data available</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500 italic">Not trading advice. For educational purposes only.</p>
        </div>

        {/* Close Button */}
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setShowUsdjpyPocVwapDashboard(false)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Free Access Settings Modal Component
const FreeAccessModal = ({ 
  showFreeAccessModal, 
  setShowFreeAccessModal, 
  freeAccessEnabled, 
  setFreeAccessEnabled,
  freeAccessStartDay,
  setFreeAccessStartDay,
  freeAccessEndDay,
  setFreeAccessEndDay,
  freeAccessDate,
  setFreeAccessDate,
  freeAccessEndDate,
  setFreeAccessEndDate,
  freeAccessMode,
  setFreeAccessMode
}) => {
  const [signupCount, setSignupCount] = useState(0);
  const [signupEmails, setSignupEmails] = useState([]);
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [showEmails, setShowEmails] = useState(false);

  // Fetch signup count on modal open
  useEffect(() => {
    if (showFreeAccessModal) {
      fetchSignups();
    }
  }, [showFreeAccessModal]);

  const fetchSignups = async () => {
    setLoadingSignups(true);
    try {
      const sessionId = localStorage.getItem('subscription_session_id') || 'admin_test_123';
      const response = await axios.get(`${API}/admin/free-access-signups`, {
        params: { session_id: sessionId }
      });
      setSignupCount(response.data.total_signups || 0);
      setSignupEmails(response.data.signups || []);
    } catch (error) {
      console.error('Error fetching signups:', error);
    } finally {
      setLoadingSignups(false);
    }
  };

  if (!showFreeAccessModal) return null;

  const handleSave = async () => {
    try {
      const sessionId = localStorage.getItem('subscription_session_id') || 'admin_test_123';
      
      // Save to backend
      const response = await axios.post(`${API}/admin/free-access-settings`, null, {
        params: {
          enabled: freeAccessEnabled,
          start_day: freeAccessStartDay,
          end_day: freeAccessEndDay,
          free_access_date: freeAccessDate || null,
          free_access_end_date: freeAccessMode === 'week' ? (freeAccessEndDate || null) : null,
          session_id: sessionId
        }
      });
      
      if (response.data.status === 'success') {
        alert('Free access settings saved successfully!');
        setShowFreeAccessModal(false);
      } else {
        alert('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error saving free access settings:', error);
      if (error.response?.status === 403) {
        alert('Admin access required. Please enable Admin mode first.');
      } else {
        alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const copyEmailsToClipboard = () => {
    const emails = signupEmails.map(s => s.email).join(', ');
    navigator.clipboard.writeText(emails);
    alert('Email addresses copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">📅 Free Access Settings</h2>
          <p className="text-gray-300 text-sm">
            Configure the random free access day
          </p>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="mb-6 bg-gray-700 p-4 rounded-lg">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-white font-medium">Enable Free Access Day</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={freeAccessEnabled}
                onChange={(e) => setFreeAccessEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`block w-14 h-8 rounded-full transition ${
                freeAccessEnabled ? 'bg-green-500' : 'bg-gray-600'
              }`}></div>
              <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${
                freeAccessEnabled ? 'transform translate-x-6' : ''
              }`}></div>
            </div>
          </label>
        </div>

        {/* Access Period Mode */}
        <div className={`mb-4 ${!freeAccessEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <label className="block text-white text-sm font-medium mb-2">Access Period</label>
          <div className="flex gap-3">
            <button
              onClick={() => { setFreeAccessMode('day'); setFreeAccessEndDate(''); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${freeAccessMode === 'day' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Single Day
            </button>
            <button
              onClick={() => setFreeAccessMode('week')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${freeAccessMode === 'week' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              Full Week
            </button>
          </div>
        </div>

        {/* Date Picker for Free Access */}
        <div className={`mb-6 ${!freeAccessEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <label className="block text-white text-sm font-medium mb-2">
            {freeAccessMode === 'week' ? 'Week Start Date' : 'Select Free Access Date'}
          </label>
          <input
            type="date"
            value={freeAccessDate}
            onChange={(e) => {
              setFreeAccessDate(e.target.value);
              if (freeAccessMode === 'week' && e.target.value) {
                const start = new Date(e.target.value);
                const end = new Date(start);
                end.setDate(end.getDate() + 6);
                setFreeAccessEndDate(end.toISOString().split('T')[0]);
              }
            }}
            className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
          />
          {freeAccessMode === 'week' && freeAccessDate && (
            <div className="mt-2">
              <label className="block text-white text-sm font-medium mb-2">Week End Date</label>
              <input
                type="date"
                value={freeAccessEndDate}
                onChange={(e) => setFreeAccessEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
              />
            </div>
          )}
          {freeAccessDate && (
            <p className="text-green-400 text-sm mt-2">
              {freeAccessMode === 'week' && freeAccessEndDate
                ? `Free access from ${new Date(freeAccessDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} to ${new Date(freeAccessEndDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
                : `Free access on: ${new Date(freeAccessDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
              }
            </p>
          )}
        </div>

        {/* Notification Signups Section */}
        <div className="mb-6 bg-blue-900 bg-opacity-30 border border-blue-500 p-4 rounded-lg">
          <h3 className="text-blue-300 font-semibold mb-2">📧 Notification Signups</h3>
          {loadingSignups ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : (
            <>
              <p className="text-white text-lg font-bold">{signupCount} people signed up</p>
              <p className="text-gray-400 text-xs mt-1">These users want to be notified of the free access day</p>
              
              {signupCount > 0 && (
                <div className="mt-3 space-y-2">
                  <button
                    onClick={() => setShowEmails(!showEmails)}
                    className="text-blue-400 text-sm hover:underline"
                  >
                    {showEmails ? '▼ Hide email list' : '▶ Show email list'}
                  </button>
                  
                  {showEmails && (
                    <div className="bg-gray-800 rounded p-2 max-h-32 overflow-y-auto">
                      {signupEmails.map((signup, idx) => (
                        <p key={idx} className="text-gray-300 text-xs">{signup.email}</p>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={copyEmailsToClipboard}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-2 rounded transition-colors"
                  >
                    📋 Copy All Emails to Clipboard
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Current Status Display */}
        <div className="mb-6 bg-gray-700 p-4 rounded-lg">
          <h3 className="text-white font-semibold mb-2">Current Settings:</h3>
          <div className="text-sm text-gray-300 space-y-1">
            <p>Status: <span className={freeAccessEnabled ? 'text-green-400' : 'text-red-400'}>
              {freeAccessEnabled ? '✅ Enabled' : '❌ Disabled'}
            </span></p>
            {freeAccessEnabled && freeAccessDate && (
              <p className="text-yellow-300 mt-2">
                ℹ️ Free access day: {new Date(freeAccessDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Save Settings
          </button>
          <button
            onClick={() => setShowFreeAccessModal(false)}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Carry Hedge Ratios Modal Component
const CarryHedgeRatiosModal = ({ showCarryHedgeRatios, setShowCarryHedgeRatios, carryHedgeRatio, carryHedgeLastUpdated }) => {
  if (!showCarryHedgeRatios) return null;

  // Parse the ratio (e.g., "3:1" -> carryValue=3, hedgeValue=1)
  const [carryValue, hedgeValue] = carryHedgeRatio.split(':').map(Number);

  // Format the last updated date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not yet updated';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-3 text-white">Carry Hedge Ratios</h2>
          <p className="text-gray-300 text-sm">
            These Ratios are for educational purposes only and are derived from an analysis of 10 year USD-Foreign Interest Rate differentials, US Credit Spreads, VIX and COT Reports.
          </p>
        </div>

        {/* Ratio Display */}
        <div className="mb-6 text-center">
          <div className="text-4xl font-bold text-green-400 mb-2">
            Carry Hedge Ratio = {carryHedgeRatio}
          </div>
          <div className="text-sm text-gray-400">
            as of {formatDate(carryHedgeLastUpdated)}
          </div>
        </div>

        {/* Two Columns: Carry Pairs and Hedge Pairs */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Carry Pair Longs */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-400 mb-4 text-center">Carry Pair Longs</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">USDCHF</span>
                <span className="text-2xl font-bold text-green-300">{carryValue}</span>
              </div>
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">USDJPY</span>
                <span className="text-2xl font-bold text-green-300">{carryValue}</span>
              </div>
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">USDCAD</span>
                <span className="text-2xl font-bold text-green-300">{carryValue}</span>
              </div>
            </div>
          </div>

          {/* Hedge Pair Longs */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-400 mb-4 text-center">Hedge Pair Longs</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">EURUSD</span>
                <span className="text-2xl font-bold text-blue-300">{hedgeValue}</span>
              </div>
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">GBPUSD</span>
                <span className="text-2xl font-bold text-blue-300">{hedgeValue}</span>
              </div>
              <div className="flex justify-between items-center bg-gray-600 rounded p-3">
                <span className="text-white font-medium">AUDUSD</span>
                <span className="text-2xl font-bold text-blue-300">{hedgeValue}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={() => setShowCarryHedgeRatios(false)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Carry Hedge Ratios Admin Modal Component
const CarryHedgeAdminModal = ({ showCarryHedgeAdmin, setShowCarryHedgeAdmin, carryHedgeRatio, setCarryHedgeRatio, mapUpdateDate, setMapUpdateDate, setCarryHedgeLastUpdated }) => {
  const [tempRatio, setTempRatio] = useState(carryHedgeRatio);
  const [tempMapDate, setTempMapDate] = useState(mapUpdateDate);

  if (!showCarryHedgeAdmin) return null;

  const handleSave = async () => {
    try {
      const sessionId = localStorage.getItem('subscription_session_id') || 'admin_test_123';
      
      // Save to backend
      const response = await axios.post(`${API}/admin/carry-hedge-ratio`, null, {
        params: {
          ratio: tempRatio,
          map_update_date: tempMapDate,
          session_id: sessionId
        }
      });
      
      if (response.data.status === 'success') {
        setCarryHedgeRatio(tempRatio);
        setMapUpdateDate(tempMapDate);
        setCarryHedgeLastUpdated(new Date().toISOString());
        alert('Carry hedge ratio and map update date saved successfully! Updated at ' + new Date().toLocaleTimeString());
        setShowCarryHedgeAdmin(false);
        
        // Reload page to update the legend
        window.location.reload();
      } else {
        alert('Failed to update. Please try again.');
      }
    } catch (error) {
      console.error('Error saving carry hedge ratio:', error);
      if (error.response?.status === 403) {
        alert('Admin access required. Please enable Admin mode first.');
      } else {
        alert('Failed to save: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-white">💱 Update Carry Hedge Ratio</h2>
          <p className="text-gray-300 text-sm">
            Update once per day at 10:00 PM UTC
          </p>
        </div>

        {/* Ratio Selection */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-3">
            Select Carry Hedge Ratio:
          </label>
          <div className="grid grid-cols-2 gap-3">
            {['3:1', '4:1', '5:1', '6:1'].map(ratio => (
              <button
                key={ratio}
                onClick={() => setTempRatio(ratio)}
                className={`px-4 py-3 rounded-lg font-bold text-lg transition-all ${
                  tempRatio === ratio
                    ? 'bg-green-600 text-white border-2 border-green-400'
                    : 'bg-gray-700 text-gray-300 border-2 border-gray-600 hover:bg-gray-600'
                }`}
              >
                {ratio}
              </button>
            ))}
          </div>
        </div>

        {/* Map Update Date Input */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Prediction Map Update Date (for legend):
          </label>
          <input
            type="text"
            value={tempMapDate}
            onChange={(e) => setTempMapDate(e.target.value)}
            placeholder="e.g., 13th of December"
            className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">
            Format: "13th of December" or "20th of December"
          </p>
        </div>

        {/* Current Selection Display */}
        <div className="mb-6 bg-gray-700 p-4 rounded-lg">
          <h3 className="text-white font-semibold mb-2">Current Selections:</h3>
          <div className="text-2xl font-bold text-green-400 text-center mb-2">
            Ratio: {tempRatio}
          </div>
          <div className="text-sm text-blue-400 text-center">
            Map Update: {tempMapDate}
          </div>
          <p className="text-gray-400 text-xs text-center mt-2">
            Last updated: {new Date().toLocaleString('en-GB', { hour12: false })}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Save Changes
          </button>
          <button
            onClick={() => {
              setTempRatio(carryHedgeRatio); // Reset to current
              setTempMapDate(mapUpdateDate);
              setShowCarryHedgeAdmin(false);
            }}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;